import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Decimal from "decimal.js";
import { assignableRole } from "./auth/roleAssign";
import {
  AlertTriangle, ArrowLeft, BarChart3, Bell, Building2, Calendar, CheckCircle2,
  ChevronRight, Circle, Clock, Download, Edit2, Eye, EyeOff, FileText,
  FolderKanban, GripVertical, LifeBuoy, Lock, Mail, Menu, MessageSquarePlus, MoreHorizontal, Phone, Plus,
  Search, Send, Settings, Shield, ShieldCheck, Sparkles, Target, Trash2,
  TrendingUp, Trophy, User, Users, X, XCircle, UtensilsCrossed, Receipt, Calculator,
  Dumbbell, UserCheck, ShoppingCart, Container, Upload, Layers, Package,
  CreditCard, ChevronDown, ChefHat, History, Printer,
} from "lucide-react";
import { api, apiDownload, API_BASE, getToken, setTenantIdleBase } from "./lib/api";
import { buildEditUserBody } from "./lib/editUserBody";
import { KitchenView } from "./gastrogest/KitchenView";
import { PrintersSettings } from "./printing/PrintersSettings";
import { TicketCustomization } from "./printing/TicketCustomization";
// chore/branding-polish — aplicar branding sin recargar: refresh de /me (auth)
// + re-aplicación de las CSS vars (--brand-primary*) vía ThemeProvider.
import { useAuth } from "./auth/AuthProvider";
import { useTheme } from "./ThemeProvider";
// TEMPORAL — verificación de Sentry. QUITAR en v1.3.0.
import { SentryTestButton } from "./SentryTestButton";
import {
  Bar, BarChart, Cell, Legend, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from "recharts";
// v1.4.0 Boutique Mode foundation
import { ServicesView, ProductsView, AgendaView, HistoryView, RemindersView, InsumosView } from "./boutique/BoutiqueViews";
// v1.4.1 ContactsModule — pantalla real de contactos (persistida vía /contacts)
import { ContactsView } from "./crm/ContactsViews";
// V92 — Comunicados (correo masivo a contactos)
import { ComunicadosView } from "./comms/ComunicadosView";
// 1.4.4 — Customer Profile 360
import { CustomerProfileView } from "./crm/CustomerProfileView";
import { AttachmentsPanel } from "./crm/AttachmentsPanel";
import { GymMembersView, GymCheckInView, GymPlansView, GymVisitsView } from "./gym/GymViews";
// V69 — motor de booking (Fase 1): tab "Clases" de Wellness (kind=CLASS). La
// sub-vista de Boutique vive dentro de AgendaView (kind=SLOT).
import { BookingManagerView, BookingChannelSection } from "./booking/BookingViews";
import { BranchSelector, BranchesSection } from "./components/BranchControls";
import { ReferralCard } from "./components/ReferralCard";
import { NotificationsBell } from "./components/NotificationsBell";
import { PaymentMethodSection } from "./components/PaymentMethodForm";
// L-14 — exportar mis datos (TyC §15).
import { ExportDataSection } from "./components/ExportDataPanel";
import { FiscalDataCard } from "./components/FiscalDataCard";
// Capa 2 (V96) — el tenant como EMISOR factura a SUS clientes (auto-gateada).
import { IssuerConfigCard } from "./components/IssuerConfigCard";
import { PacProbeCard } from "./components/PacProbeCard";
// PR A (V204) — medidor/ widget de la bolsa anual de folios CFDI (auto-gateado).
import { FoliosUsageMeter } from "./components/FoliosUsageMeter";
import { StoreAdminCard } from "./store/StoreAdminCard";
import { LoyaltyAdminCard } from "./loyalty/LoyaltyAdminCard";
import { BookingNotifyContactCard } from "./components/BookingNotifyContactCard";
import { SalesCfdiListCard } from "./components/SalesCfdiListCard";
import { FacturableOrdersCard } from "./components/FacturableOrdersCard";
import { PaymentsConnectCard } from "./components/PaymentsConnectCard";
// feat/frontend-import-ui — página "Importar datos" (carga masiva self-service, envuelve /imports/*)
import { ImportDataView } from "./imports/ImportDataView";
// Tour guiado in-app (driver.js) — filtrado por módulo×rol.
import { startGuidedTour } from "./tour/guidedTour";
// Carril A PR2 — Ayuda in-app: tour progresivo (coach-marks por sección) + FAQ.
import { showSectionCoach } from "./help/coach";
import { HelpCenter } from "./help/HelpCenter";
// Legales v3.1 — plantilla de aviso de privacidad del tenant (descarga por módulos).
import { TenantPrivacyTemplateCard } from "./legal/TenantPrivacyTemplateCard";
// §17.3 — planes
import { PLAN_META, PLAN_ORDER, priceLabel, MODULE_META, MODULE_ORDER, COMBO_PRICE_MXN, fmtMxn } from "./lib/plans";
import { useContacts } from "./contacts/useContacts";
// v1.5.0 GastroGest (kernel de comercio)
import { GastroGestPOS, PendingDeliveryScreen } from "./gastrogest/GastroGestViews";
import { GastroGestInsights } from "./gastrogest/InsightsView";
// §21.3 GastroGest — analítica de margen (dueño) + ticket/volumen (operativo).
import { MarginView } from "./gastrogest/MarginView";
import { TicketVolumeView } from "./gastrogest/TicketVolumeView";
import { KitchenTimesView } from "./gastrogest/KitchenTimesView";
// V86 — "Arma tu platillo": biblioteca de modificadores + attach a platillos.
import { ModifiersView } from "./gastrogest/ModifiersView";
import { PaquetesView } from "./gastrogest/PaquetesView";
// Comentario/sugerencia global (cualquier usuario) — Topbar.
import { FeedbackButton } from "./components/FeedbackButton";
// Documentos legales in-app (Configuración → Documentos legales)
import { LegalSettings } from "./legal/LegalSettings";
import { MarketingConsentCard } from "./legal/MarketingConsentCard";
// §17.2.c — fondo/tema del espacio de trabajo
import { WorkspaceBackground, PATTERN_OPTIONS, BRAND_PALETTE, DEFAULT_THEME_COLORS } from "./theme/WorkspaceBackground";
import { GastroGestCashClose } from "./gastrogest/CashCloseView";
import { FloorAdminSection } from "./gastrogest/FloorAdminSection";
import { FloorLockProvider } from "./gastrogest/FloorLock";
// §21.1 Finanzas — P&L básico + registro de gastos (dato del dueño, financials:view).
import { PnlView } from "./finance/PnlView";
import { ExpensesView } from "./finance/ExpensesView";
// Filtro de período explícito y reutilizable (Reportes).
import { DatePeriodFilter, periodToQuery, periodLabel, defaultPeriod } from "./components/DatePeriodFilter";
// v1.4.1.1 — inputs de contraseña con toggle mostrar/ocultar + validador de match
import { PasswordInput } from "./components/PasswordInput";
import { PasswordMatchValidator } from "./components/PasswordMatchValidator";

/* =============================================================================
   Atelier CRM Dashboard — v2
   - Sidebar: CRM, Proyectos, Clientes, Configuración
   - Project Hub con BudgetBar (verde/ámbar/rojo sofisticado) + Kanban DnD
   - LogHoursModal global
   - ClientsView (tabla cartera por cliente)
   - Estilo editorial premium, sombras suaves, paleta sobria
   ============================================================================= */

// Day 4/6 cleanup: CONTACTS mock removido (era dead code post-hotfix Day 4).
// Sweep 2026-07-09: PROJECTS_INIT/TASKS_INIT mock removidos (dead code sin referencias).
const ACTIVITY_TYPES = [
  { id:"call", label:"Llamada" }, { id:"meeting", label:"Reunión" },
  { id:"email", label:"Correo" }, { id:"visit", label:"Visita" }, { id:"note", label:"Nota interna" },
];

// Tags para clasificar el tiempo registrado (≠ ACTIVITY_TYPES, que son tipos
// de interacción CRM). Estos alimentan el modal "Registrar horas" y el catálogo
// editable de Configuración.
const ACTIVITY_TAGS = [
  { id:"tag-1", name:"Consultoría",  color:"#2C5C3F", isBillableDefault:true  },
  { id:"tag-2", name:"Soporte",      color:"#7B5A12", isBillableDefault:true  },
  { id:"tag-3", name:"Desarrollo",   color:"#0F172A", isBillableDefault:true  },
  { id:"tag-4", name:"Diseño",       color:"#7B2D26", isBillableDefault:true  },
  { id:"tag-5", name:"Reuniones",    color:"#475569", isBillableDefault:false },
];

const STATUS_STYLES = {
  ACTIVE:    "bg-emerald-50 text-emerald-700 ring-emerald-700/15",
  PLANNING:  "bg-amber-50 text-amber-700 ring-amber-700/15",
  ON_HOLD:   "bg-slate-100 text-slate-600 ring-slate-500/10",
  COMPLETED: "bg-sky-50 text-sky-700 ring-sky-600/15",
  CANCELLED: "bg-rose-50 text-rose-800 ring-rose-700/15",
};
const STATUS_LABEL = { ACTIVE:"En curso", PLANNING:"Planificación", ON_HOLD:"En pausa", COMPLETED:"Completado", CANCELLED:"Cancelado" };
const KANBAN_COLS = [
  { id:"TODO",        title:"Por hacer" },
  { id:"IN_PROGRESS", title:"En curso" },
  { id:"REVIEW",      title:"Revisión" },
  { id:"DONE",        title:"Completado" },
];

/* ---------------------------- Helpers ----------------------------------- */
const fmtMoney = n => new Intl.NumberFormat("es-MX", {
  style: "currency", currency: "USD", maximumFractionDigits: 0,
}).format(n);

/**
 * Formatea horas evitando colas de coma flotante (ej. "194.98000000000002h").
 * Redondea a ≤2 decimales y descarta ceros sobrantes: 520 → "520",
 * 194.98000000000002 → "194.98", 6.5 → "6.5". Usar en TODO render de horas.
 */
const fmtHours = v => new Intl.NumberFormat("es-MX", {
  maximumFractionDigits: 2,
}).format(Number(v) || 0);

/**
 * Day 5 hotfix BUG#4 — formatea un monto USD en la moneda de visualización
 * del tenant. Si la tasa no está configurada, cae a USD con indicador "(USD)".
 *
 *   fxRates: filas de /currencies/rates (latest por currencyCode).
 *   rateToUsd = "cuántas unidades de la currency equivalen a 1 USD"
 *   ⇒ conversión USD→display = usdAmount * rateToUsd.
 *
 * Formato: siempre con prefijo ISO ("USD 1,902", "MXN 33,285") para evitar
 * ambigüedad — Intl.NumberFormat con locale es-MX devuelve solo "$" para MXN,
 * lo cual confunde a usuarios habituados a multimoneda.
 */
function fmtUsdInDisplay(usdAmount, displayCurrency = "USD", fxRates = []) {
  const usd = Number(usdAmount) || 0;
  const numberFmt = (n) =>
    new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 }).format(Math.round(n));

  if (!displayCurrency || displayCurrency === "USD") {
    return `USD ${numberFmt(usd)}`;
  }
  // Tomamos la tasa MÁS RECIENTE por currency (fxRates puede venir sin filtrar).
  const candidates = (fxRates ?? []).filter(r => r.currencyCode === displayCurrency);
  candidates.sort((a, b) => String(b.effectiveMonth).localeCompare(String(a.effectiveMonth)));
  const rate = candidates[0]?.rateToUsd != null ? Number(candidates[0].rateToUsd) : null;
  if (!rate || !Number.isFinite(rate) || rate <= 0) {
    // Fallback: muestra USD con badge para que el admin sepa configurar la tasa.
    return `USD ${numberFmt(usd)} (USD)`;
  }
  return `${displayCurrency} ${numberFmt(usd * rate)}`;
}
// V140: una fecha-solo "YYYY-MM-DD" se parsea como LOCAL (no UTC). new Date("2026-08-20")
// = UTC-medianoche -> en TZ Mexico el navegador la muestra un dia antes. Appendear
// "T00:00:00" la ancla a medianoche LOCAL. Datetimes con hora se dejan igual.
// Ver docs/DIAG_DESFASE_FECHAS.md.
const fmtDate = iso => {
  if (!iso) return "—";
  const s = String(iso);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(s + "T00:00:00") : new Date(s);
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
};

/**
 * Helper de permisos forward-compatible:
 *   - SUPER_USER bypass (igual que el PermissionsGuard del backend, V6).
 *   - Si en el futuro pobamos `user.permissions` desde /me, se respeta.
 * Mientras solo hay `user.role`, COMMON_USER no ve acciones admin — coincide
 * con el patrón del resto del dashboard (`isAdmin` flag).
 */
function hasPermission(user, perm) {
  if (!user) return false;
  if (user.role === "SUPER_USER") return true;
  return Array.isArray(user.permissions) && user.permissions.includes(perm);
}

/**
 * Day 5 Block G: gate explícito del toggle de rol (Dev-only).
 * El usuario debe activarlo manualmente desde DevTools:
 *   localStorage.setItem('stratos.debug.roleToggle', 'true')
 * Por default queda oculto incluso en development — antes confundía a usuarios.
 * Ver docs/DEV_FLAGS.md.
 */
function isRoleToggleEnabled() {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage.getItem("stratos.debug.roleToggle") === "true"
    );
  } catch {
    return false;
  }
}

/**
 * Paleta sobria de alertas — sin rojos estridentes:
 *  HEALTHY  → verde "musgo"  (#2C5C3F)
 *  WATCH    → ámbar "trigo"  (#7B5A12)
 *  CRITICAL → mismo ámbar pero con barra rust
 *  OVER     → rojo "óxido"   (#7B2D26 sobre #FBF1EE)
 */
function budgetTone(pct) {
  if (pct == null) return { name:"OK",   chipCls:"alert-ok",   barCls:"alert-ok-bar",   label:"Sin datos" };
  if (pct > 100)   return { name:"OVER", chipCls:"alert-over", barCls:"alert-over-bar", label:"Excedido" };
  if (pct >= 90)   return { name:"CRIT", chipCls:"alert-warn", barCls:"alert-over-bar", label:"Crítico" };
  if (pct >= 70)   return { name:"WARN", chipCls:"alert-warn", barCls:"alert-warn-bar", label:"Atención" };
  return              { name:"OK",   chipCls:"alert-ok",   barCls:"alert-ok-bar",   label:"Saludable" };
}

/* ===========================================================================
   <MaskedField> — datos sensibles enmascarados para no-admins
   =========================================================================== */
function MaskedField({ value, type = "text", isAdmin, allowReveal = true }) {
  const [revealed, setRevealed] = useState(false);
  const showReal = isAdmin || revealed;
  const masked = useMemo(() => {
    if (!value) return "—";
    if (type === "email") {
      const [local, domain] = String(value).split("@");
      if (!domain) return "•".repeat(8);
      return `${local[0]}${"•".repeat(Math.max(local.length - 2, 1))}${local.slice(-1)}@${domain}`;
    }
    if (type === "phone") return `${"•".repeat(8)} ${String(value).slice(-4)}`;
    return "•".repeat(Math.min(String(value).length, 12));
  }, [value, type]);

  return (
    <span className="inline-flex items-center gap-2 font-mono text-[13px] tracking-tight text-slate-700">
      <span className={`tabular-nums transition-colors ${showReal ? "text-slate-900" : "text-slate-500"}`}>
        {showReal ? value : masked}
      </span>
      {!isAdmin && (
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-500 ring-1 ring-inset ring-slate-200">
          <Shield className="h-3 w-3" /> Privado
        </span>
      )}
      {!isAdmin && allowReveal && (
        <button type="button" onClick={() => setRevealed(v => !v)}
                className="rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700">
          {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
        </button>
      )}
    </span>
  );
}

/* ===========================================================================
   Common building blocks
   =========================================================================== */
function Field({ label, children, required = false }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
        {label}{required && <span className="text-rose-700"> *</span>}
      </span>
      {children}
    </label>
  );
}

/* ============================================================================
   Modal genérico — wrapper reutilizable para los formularios de admin
   (NewProject, NewClient, EditClient, NewContact, etc.)
   ============================================================================ */
function Modal({ open, onClose, title, subtitle = null, children, maxWidth = "max-w-lg" }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full ${maxWidth} rounded-t-3xl border border-slate-200/80 bg-white shadow-xl md:rounded-3xl max-h-[90vh] overflow-auto thin-scroll`}>
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-100 bg-white px-6 pt-6 pb-4 md:px-8">
          <div>
            <h3 className="font-serif text-2xl tracking-tight text-slate-900">{title}</h3>
            {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-6 md:px-8">{children}</div>
      </div>
    </div>
  );
}

/* ============================================================================
   NewProjectModal — crea un proyecto + opcionalmente un cliente inline
   ============================================================================ */
const CURRENCIES = [
  { code: "USD", symbol: "$",  name: "US Dollar" },
  { code: "MXN", symbol: "$",  name: "Peso Mexicano" },
  { code: "BRL", symbol: "R$", name: "Real Brasileño" },
];

// Tipos de cambio iniciales (mes en curso). rate_to_usd: cuántas unidades equivalen a 1 USD.
const FX_RATES_INIT = [
  { id:"fx-1", currencyCode:"MXN", effectiveMonth: new Date().toISOString().slice(0,7), rateToUsd: 17.50, source:"MANUAL" },
  { id:"fx-2", currencyCode:"BRL", effectiveMonth: new Date().toISOString().slice(0,7), rateToUsd:  5.10, source:"MANUAL" },
];

/** Convierte un monto de la moneda nativa a la moneda de visualización. */
function convertAmount(amount, fromCode, toCode, fxRates) {
  if (!amount || fromCode === toCode) return amount ?? 0;
  // todo pasa por USD: amount → USD → toCode
  const fromRate = fromCode === "USD" ? 1 : (fxRates.find(r => r.currencyCode === fromCode)?.rateToUsd ?? null);
  const toRate   = toCode   === "USD" ? 1 : (fxRates.find(r => r.currencyCode === toCode)?.rateToUsd   ?? null);
  if (!fromRate || !toRate) return amount; // si falta FX, no convertir
  const usd = amount / fromRate;
  return usd * toRate;
}

/** Formato amistoso con la moneda de display. */
function fmtMoneyIn(amount, code) {
  try {
    return new Intl.NumberFormat("es-MX", { style:"currency", currency: code, maximumFractionDigits: 0 }).format(amount);
  } catch {
    const sym = CURRENCIES.find(c => c.code === code)?.symbol ?? "$";
    return `${sym}${Math.round(amount).toLocaleString()}`;
  }
}

function NewProjectModal({ open, onClose, clients, onCreate, isAdmin }) {
  const [form, setForm] = useState({
    name: "", code: "", clientId: "",
    budget: 0, budgetCurrency: "USD",
    budgetedHours: 100, monthlyBudgetedHours: 20,
    startDate: new Date().toISOString().slice(0,10),
    dueDate:   "",
  });
  const [creatingClient, setCreatingClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", industry: "", contactEmail: "" });

  useEffect(() => {
    if (open) {
      setForm({
        name: "", code: "", clientId: clients[0]?.id ?? "",
        budget: 0, budgetCurrency: "USD",
        budgetedHours: 100, monthlyBudgetedHours: 20,
        startDate: new Date().toISOString().slice(0,10),
        dueDate: "",
      });
      setCreatingClient(false);
      setNewClient({ name: "", industry: "", contactEmail: "" });
    }
  }, [open, clients]);

  const onChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };
  const onChangeClient = e => {
    const { name, value } = e.target;
    setNewClient(c => ({ ...c, [name]: value }));
  };

  const submit = e => {
    e.preventDefault();
    let clientId = form.clientId;
    let createdClient = null;
    if (creatingClient) {
      if (!newClient.name.trim()) return;
      createdClient = {
        id: `cl-${Date.now()}`,
        name: newClient.name.trim(),
        industry: newClient.industry,
        contactEmail: newClient.contactEmail,
        isActive: true,
      };
      clientId = createdClient.id;
    }
    if (!clientId) return;
    const clientName = createdClient?.name ?? clients.find(c => c.id === clientId)?.name ?? "—";
    const project = {
      id: `p-${Date.now()}`,
      code: form.code.trim() || `PRJ-${Date.now().toString().slice(-6)}`,
      clientId, client: clientName,
      name: form.name.trim(),
      owner: "Tú",
      status: "PLANNING",
      progress: 0,
      dueDate: form.dueDate || "2026-12-31",
      startDate: form.startDate,
      budget: Number(form.budget) || 0,
      budgetCurrency: form.budgetCurrency,
      budgetedHours: Number(form.budgetedHours) || 0,
      monthlyBudgetedHours: Number(form.monthlyBudgetedHours) || 0,
      hoursThisMonth: 0,
      hoursLogged: 0,
      // v1.1.2: workflow de aprobación
      approvalStatus: isAdmin ? "APPROVED" : "PENDING",
    };
    onCreate(project, createdClient);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}
           title="Nuevo proyecto"
           subtitle="Pacta horas, fechas y cliente final"
           maxWidth="max-w-xl">
      <form onSubmit={submit} className="space-y-5">
        <Field label="Nombre del proyecto" required>
          <input className="input" name="name" value={form.name} onChange={onChange}
                 placeholder="Ej. Rediseño de portal corporativo" required />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Código">
            <input className="input" name="code" value={form.code} onChange={onChange}
                   placeholder="ACM-2026-014" />
          </Field>
          <Field label="Moneda">
            <select className="input" name="budgetCurrency" value={form.budgetCurrency} onChange={onChange}>
              {CURRENCIES.map(c => (
                <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
              ))}
            </select>
          </Field>
        </div>
        <Field label={`Presupuesto (${form.budgetCurrency})`}>
          <input className="input tabular-nums" type="number" min="0" name="budget"
                 value={form.budget} onChange={onChange} />
        </Field>

        {!isAdmin && (
          <div className="alert-warn rounded-xl px-4 py-3 text-xs">
            <p className="font-medium">Este proyecto quedará pendiente de aprobación</p>
            <p className="mt-1 opacity-80">
              Un administrador del tenant lo revisará desde la pestaña "Aprobaciones" antes de que aparezca en la cartera oficial.
            </p>
          </div>
        )}

        {/* Cliente: selector o creación inline */}
        {!creatingClient ? (
          <Field label="Cliente final" required>
            <div className="flex gap-2">
              <select className="input" name="clientId" value={form.clientId} onChange={onChange} required>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button type="button" onClick={() => setCreatingClient(true)}
                      className="shrink-0 rounded-xl border border-dashed border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50">
                + Nuevo
              </button>
            </div>
          </Field>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                Crear cliente nuevo
              </p>
              <button type="button" onClick={() => setCreatingClient(false)}
                      className="text-xs text-slate-500 hover:text-slate-900">
                ← Usar uno existente
              </button>
            </div>
            <Field label="Nombre" required>
              <input className="input" name="name" value={newClient.name} onChange={onChangeClient}
                     placeholder="Mi Cliente S.A. de C.V." required />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Industria">
                <input className="input" name="industry" value={newClient.industry} onChange={onChangeClient}
                       placeholder="Manufactura" />
              </Field>
              <Field label="Email contacto">
                <input className="input" type="email" name="contactEmail" value={newClient.contactEmail}
                       onChange={onChangeClient} placeholder="contacto@cliente.com" />
              </Field>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Field label="Inicio">
            <input className="input" type="date" name="startDate" value={form.startDate} onChange={onChange} />
          </Field>
          <Field label="Entrega">
            <input className="input" type="date" name="dueDate" value={form.dueDate} onChange={onChange} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Horas pactadas (total)">
            <input className="input tabular-nums" type="number" min="0" name="budgetedHours"
                   value={form.budgetedHours} onChange={onChange} />
          </Field>
          <Field label="Horas/mes (techo)">
            <input className="input tabular-nums" type="number" min="0" name="monthlyBudgetedHours"
                   value={form.monthlyBudgetedHours} onChange={onChange} />
          </Field>
        </div>

        <div className="flex flex-col-reverse items-stretch gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose}
                  className="rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
            Cancelar
          </button>
          <button type="submit"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--brand-primary)] px-5 py-2.5 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
            <Plus className="h-4 w-4" /> Crear proyecto
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ============================================================================
   ReportsView — Dashboard de KPIs con filtros, charts y comparativas
   ============================================================================ */
const CHART_PALETTE = ["#0F172A", "#7B2D26", "#2C5C3F", "#7B5A12", "#475569", "#6B4F8E", "#1F4D6B", "#A86332"];

function ReportsView({ projects, tasks, users, contacts, displayCurrency = "USD", fxRates = [] }) {
  const [period, setPeriod]             = useState(defaultPeriod());   // { mode, year, month } explícito
  const [clientFilter, setClientFilter] = useState("all");
  const [userFilter, setUserFilter]     = useState("all");

  // Clientes únicos derivados de proyectos aprobados
  const clientOptions = useMemo(() => {
    const m = new Map();
    projects.forEach(p => { if (p.approvalStatus !== "PENDING") m.set(p.clientId, { id: p.clientId, name: p.client }); });
    return Array.from(m.values());
  }, [projects]);

  // Filtros aplicados
  const filteredProjects = useMemo(() =>
    projects
      .filter(p => p.approvalStatus !== "PENDING" && p.approvalStatus !== "REJECTED")
      .filter(p => clientFilter === "all" || p.clientId === clientFilter),
    [projects, clientFilter]);

  const filteredTasks = useMemo(() => {
    const projIds = new Set(filteredProjects.map(p => p.id));
    return tasks
      .filter(t => projIds.has(t.projectId))
      .filter(t => userFilter === "all" || t.assignee === users.find(u => u.id === userFilter)?.name);
  }, [tasks, filteredProjects, userFilter, users]);

  // ── KPIs ────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const revenue = filteredProjects.reduce((a, p) => a + (p.budget || 0), 0);
    // 2.5: costo real = Σ horas registradas × tarifa del proyecto. Antes usaba
    // u.hourlyRate (tarifa por usuario), campo inexistente en el backend → 0.
    const cost = filteredProjects.reduce((a, p) => a + (p.hoursLogged || 0) * (p.hourlyRate || 0), 0);
    const margin = revenue - cost;
    const marginPct = revenue > 0 ? (margin / revenue) * 100 : 0;
    const active = filteredProjects.filter(p => p.status === "ACTIVE").length;
    return { revenue, cost, margin, marginPct, active };
  }, [filteredProjects]);

  // ── Chart 1: Proyectos por estado ────────────────────────────────────────
  const projectsByStatus = useMemo(() => {
    const counts = { ACTIVE: 0, PLANNING: 0, ON_HOLD: 0, COMPLETED: 0, CANCELLED: 0 };
    filteredProjects.forEach(p => { counts[p.status] = (counts[p.status] || 0) + 1; });
    return Object.entries(counts).map(([k, v]) => ({ name: STATUS_LABEL[k] || k, count: v }));
  }, [filteredProjects]);

  // ── Chart 2: Distribución de tareas (Kanban) ─────────────────────────────
  const tasksByStatus = useMemo(() => {
    const labels = { TODO: "Por hacer", IN_PROGRESS: "En curso", REVIEW: "Revisión", DONE: "Completado" };
    const counts = { TODO: 0, IN_PROGRESS: 0, REVIEW: 0, DONE: 0 };
    filteredTasks.forEach(t => { counts[t.status] = (counts[t.status] || 0) + 1; });
    return Object.entries(counts).map(([k, v]) => ({ name: labels[k], value: v }));
  }, [filteredTasks]);

  // ── Chart 3: Carga por usuario (horas) ───────────────────────────────────
  const userWorkload = useMemo(() => {
    // Solo horas: no existe tarifa por usuario en el backend, así que el costo
    // por consultor no es derivable. El chart muestra únicamente horas.
    return users.filter(u => u.isActive !== false).map(u => {
      const hours = filteredTasks
        .filter(t => t.assignee === u.name)
        .reduce((a, t) => a + (t.hoursLogged || 0), 0);
      return { name: u.name, hours };
    }).sort((a, b) => b.hours - a.hours);
  }, [users, filteredTasks]);

  // ── Chart 4: Revenue vs Costo por proyecto ───────────────────────────────
  const projectFinancials = useMemo(() =>
    filteredProjects.slice(0, 8).map(p => {
      // 2.5: costo real = horas registradas × tarifa del proyecto (p.hourlyRate),
      // no Σ tasks × u.hourlyRate (tarifa por usuario inexistente → 0).
      const cost = (p.hoursLogged || 0) * (p.hourlyRate || 0);
      return {
        name: p.name.length > 22 ? p.name.slice(0, 20) + "…" : p.name,
        Ingreso: p.budget || 0,
        Costo: Math.round(cost),
        Margen: (p.budget || 0) - cost,
      };
    }), [filteredProjects]);

  // ── Chart 5: Progreso por cliente (avance % promedio) ────────────────────
  const clientProgress = useMemo(() => {
    const byClient = new Map();
    filteredProjects.forEach(p => {
      const c = byClient.get(p.client) ?? { name: p.client, totalH: 0, soldH: 0, projects: 0 };
      c.totalH += p.hoursLogged || 0;
      c.soldH  += p.budgetedHours || 0;
      c.projects += 1;
      byClient.set(p.client, c);
    });
    return Array.from(byClient.values()).map(c => ({
      name: c.name,
      "Vendidas (h)": c.soldH,
      "Ejecutadas (h)": c.totalH,
    }));
  }, [filteredProjects]);

  // ── Chart 6: Línea de horas registradas por período ──────────────────────
  // 2.3: agregación REAL de time_logs (GET /timelogs/summary), tenant-scoped,
  // por bucket según el período + filtros de cliente/usuario. Antes era
  // sintética (repartía un total en pesos fijos, sin leer time_logs).
  const [hoursTrend, setHoursTrend] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const qs = new URLSearchParams(periodToQuery(period));
    if (clientFilter !== "all") qs.set("endClientId", clientFilter);
    if (userFilter !== "all") qs.set("userId", userFilter);
    api<{ buckets?: Array<{ name: string; hours: number }> }>(`/timelogs/summary?${qs.toString()}`)
      .then(res => {
        if (cancelled) return;
        const buckets = Array.isArray(res?.buckets) ? res.buckets : [];
        setHoursTrend(buckets.map(b => ({ name: b.name, horas: Number(b.hours) || 0 })));
      })
      .catch(() => { if (!cancelled) setHoursTrend([]); });
    return () => { cancelled = true; };
  }, [period, clientFilter, userFilter]);

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-5">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-1.5">Período</p>
            <DatePeriodFilter value={period} onChange={setPeriod} />
          </div>
          <div className="min-w-[180px]">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-1.5">Cliente</p>
            <select value={clientFilter} onChange={e => setClientFilter(e.target.value)} className="input">
              <option value="all">Todos los clientes</option>
              {clientOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="min-w-[180px]">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-1.5">Usuario</p>
            <select value={userFilter} onChange={e => setUserFilter(e.target.value)} className="input">
              <option value="all">Todos los usuarios</option>
              {users.filter(u => u.isActive !== false).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatTile label="Ingresos" value={fmtMoneyIn(kpis.revenue, displayCurrency)} hint="Σ budget aprobado" />
        <StatTile label="Costo real" value={fmtMoneyIn(kpis.cost, displayCurrency)} hint="horas × tarifa" />
        <StatTile label="Margen" value={fmtMoneyIn(kpis.margin, displayCurrency)} hint={`${kpis.marginPct.toFixed(1)}% sobre ingresos`} />
        <StatTile label="Proyectos activos" value={kpis.active} hint={`de ${filteredProjects.length} en cartera`} />
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Proyectos por estado" subtitle="Volumen general de la cartera">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={projectsByStatus} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} />
              <YAxis tick={{ fontSize: 11, fill: "#475569" }} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#F8FAFC" }} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
              <Bar dataKey="count" fill="#0F172A" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Distribución de tareas" subtitle="Tareas filtradas por estado del Kanban">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={tasksByStatus} dataKey="value" nameKey="name"
                   innerRadius={50} outerRadius={90} paddingAngle={2}>
                {tasksByStatus.map((_, i) => <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />)}
              </Pie>
              <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Carga de trabajo por consultor" subtitle="Horas registradas en el período">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={userWorkload} layout="vertical" margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#475569" }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} width={80} />
              <Tooltip cursor={{ fill: "#F8FAFC" }} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
              <Bar dataKey="hours" name="Horas" fill="#2C5C3F" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Tendencia de horas" subtitle={`Distribución — ${periodLabel(period)}`}>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={hoursTrend} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} />
              <YAxis tick={{ fontSize: 11, fill: "#475569" }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
              <Line type="monotone" dataKey="horas" stroke="#0F172A" strokeWidth={2.5} dot={{ r: 4, fill: "#0F172A" }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts row 3 — Financials */}
      <ChartCard title="Ingreso vs Costo por proyecto" subtitle="Margen estimado (top 8 proyectos)">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={projectFinancials} margin={{ top: 10, right: 16, left: 0, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#475569" }} angle={-25} textAnchor="end" height={60} interval={0} />
            <YAxis tick={{ fontSize: 11, fill: "#475569" }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
            <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                     formatter={(v) => fmtMoneyIn(v, displayCurrency)} />
            <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="Ingreso" fill="#2C5C3F" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Costo"   fill="#7B2D26" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Margen"  fill="#7B5A12" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Charts row 4 — Cliente */}
      <ChartCard title="Avance por cliente final" subtitle="Horas vendidas vs ejecutadas">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={clientProgress} margin={{ top: 10, right: 16, left: 0, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} />
            <YAxis tick={{ fontSize: 11, fill: "#475569" }} />
            <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
            <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="Vendidas (h)"   fill="#0F172A" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Ejecutadas (h)" fill="#7B5A12" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Charts row 5 — Pipeline funnel (Día 2 / GAP 3) */}
      <PipelineFunnelCard />
    </div>
  );
}

/* ===========================================================================
   ExchangeRatesSection — admin de tasas de cambio mensuales (v1.2.0 / V8)
   ---------------------------------------------------------------------------
   Conectado al backend real (/api/v1/currencies/rates/*). Solo se monta si el
   caller es SUPER_USER (el wrapper en SettingsView ya filtra). Patrón
   monolítico: vive aquí dentro de Dashboard.tsx siguiendo el resto del file.
   =========================================================================== */
function ExchangeRatesSection({ displayCurrency, setDisplayCurrency }) {
  const [year, setYear] = useState(() => new Date().getUTCFullYear());
  const [rates, setRates] = useState([]);
  const [health, setHealth] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState({ open: false, initial: null });

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [ratesData, healthData, currList] = await Promise.all([
        api<any[]>(`/currencies/rates?year=${year}`),
        api<any>(`/currencies/rates/health`),
        api<any[]>(`/currencies`),
      ]);
      setRates(ratesData ?? []);
      setHealth(healthData);
      setCurrencies(currList ?? []);
    } catch (e: any) {
      setError(e?.body?.message || e?.message || "No se pudieron cargar las tasas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const onSaved = async () => {
    setModal({ open: false, initial: null });
    await load();
  };

  // Mes actual (primer día) en formato YYYY-MM-DD, normalizado UTC.
  const currentMonth = health?.current_month
    ?? `${new Date().getUTCFullYear()}-${String(new Date().getUTCMonth() + 1).padStart(2, "0")}-01`;
  const isCurrentMonth = (m) => m === currentMonth;

  const missing = health?.missing ?? [];
  const configuredCurrentMonth = (health?.configured ?? []);

  // Histórico: todas menos las del mes actual (esas se muestran en la card de arriba)
  const historico = rates.filter(r => !isCurrentMonth(r.effectiveMonth));

  return (
    <div className="space-y-5">
      {/* Header card */}
      <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
        <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-6 py-5 md:px-8">
          <div>
            <h3 className="font-serif text-lg tracking-tight text-slate-900">Tasas de cambio</h3>
            <p className="text-sm text-slate-500">
              Tasas mensuales fijas tipo "budget rate". Una vez que un lead se cierre con la tasa
              del mes, esa tasa queda congelada en el lead (snapshot inmutable).
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-[11px] uppercase tracking-wider text-slate-400">Ver en:</label>
            <select value={displayCurrency} onChange={e => setDisplayCurrency(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium">
              <option value="USD">USD</option>
              <option value="MXN">MXN</option>
              <option value="BRL">BRL</option>
              <option value="EUR">EUR</option>
            </select>
            <button
              onClick={() => setModal({ open: true, initial: null })}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--brand-primary)] px-4 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
              <Plus className="h-3.5 w-3.5" /> Configurar tasa
            </button>
          </div>
        </div>

        {/* Banner de tasas faltantes */}
        {missing.length > 0 && (
          <div className="border-b border-rose-200 bg-rose-50 px-6 py-4 md:px-8">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-700" />
              <div className="flex-1 text-sm text-rose-800">
                <p className="font-medium">Faltan tasas para leads activos:</p>
                <ul className="mt-1 space-y-0.5 text-[13px]">
                  {missing.map((m, i) => (
                    <li key={i}>
                      <strong>{m.currencyCode}</strong> en <strong>{m.month}</strong>
                      {" · "}
                      {m.leadsAffected} {m.leadsAffected === 1 ? "lead" : "leads"}
                      {m.projectsAffected > 0 && `, ${m.projectsAffected} proyecto${m.projectsAffected === 1 ? "" : "s"}`}
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => setModal({ open: true, initial: {
                  currencyCode: missing[0].currencyCode,
                  effectiveMonth: missing[0].month,
                } })}
                className="rounded-lg bg-rose-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-800 shrink-0">
                Configurar ahora
              </button>
            </div>
          </div>
        )}

        {/* Card "Mes actual" */}
        <div className="px-6 py-5 md:px-8">
          <div className="mb-3 flex items-baseline justify-between">
            <h4 className="font-serif text-base tracking-tight text-slate-900">
              Mes actual — {fmtMonth(currentMonth)}
            </h4>
            <p className="text-[11px] uppercase tracking-wider text-slate-400">
              {configuredCurrentMonth.length} tasa{configuredCurrentMonth.length === 1 ? "" : "s"} configurada{configuredCurrentMonth.length === 1 ? "" : "s"}
            </p>
          </div>
          {loading ? (
            <div className="h-20 animate-pulse rounded-xl bg-slate-50" />
          ) : configuredCurrentMonth.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-400">
              Sin tasas configuradas este mes
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200/80">
              {configuredCurrentMonth.map((c) => {
                const rateRow = rates.find(r =>
                  r.currencyCode === c.currencyCode
                  && r.effectiveMonth === currentMonth);
                const locked = rateRow?.locked;
                const isUsd = c.currencyCode === "USD";
                return (
                  <li key={c.currencyCode} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-[13px] font-semibold text-slate-900">{c.currencyCode}</span>
                      <span className="font-serif text-base tabular-nums text-slate-900">
                        {Number(c.rateToUsd).toFixed(4)}
                      </span>
                      {isUsd && (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">base</span>
                      )}
                      {locked && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-700/15">
                          <Lock className="h-2.5 w-2.5" /> congelada
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-slate-500">
                      {c.setBy && <span>{c.setBy}</span>}
                      {c.note && <span className="italic truncate max-w-[200px]">{c.note}</span>}
                      {!isUsd && rateRow && (
                        <button
                          onClick={() => setModal({ open: true, initial: rateRow })}
                          className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                          title={locked ? "Editar nota (rate congelado)" : "Editar"}>
                          <Edit2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Histórico */}
      <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
        <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-6 py-4 md:px-8">
          <div>
            <h4 className="font-serif text-base tracking-tight text-slate-900">Histórico</h4>
            <p className="text-xs text-slate-500">
              Tasas de meses pasados/futuros. Las filas con candado tienen leads cerrados (rate inmutable).
            </p>
          </div>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium">
            {[year - 1, year, year + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/60 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              <tr>
                <th className="px-6 py-3 text-left">Moneda</th>
                <th className="px-6 py-3 text-left">Mes</th>
                <th className="px-6 py-3 text-right">Tasa</th>
                <th className="px-6 py-3 text-left">Fuente</th>
                <th className="px-6 py-3 text-left">Quién</th>
                <th className="px-6 py-3 text-left">Cuándo</th>
                <th className="px-6 py-3 text-left">Nota</th>
                <th className="px-6 py-3 text-right">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={8} className="px-6 py-10 text-center text-slate-400">Cargando…</td></tr>
              )}
              {!loading && historico.length === 0 && (
                <tr><td colSpan={8} className="px-6 py-10 text-center text-slate-400">Sin tasas históricas en {year}</td></tr>
              )}
              {historico.map(r => (
                <tr key={r.id}>
                  <td className="px-6 py-3 font-mono text-[13px] font-medium text-slate-900">
                    <span className="inline-flex items-center gap-2">
                      {r.currencyCode}
                      {r.locked && <Lock className="h-3 w-3 text-amber-600" />}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-slate-500">{r.effectiveMonth}</td>
                  <td className="px-6 py-3 text-right font-serif text-base tabular-nums text-slate-900">
                    {Number(r.rateToUsd).toFixed(4)}
                  </td>
                  <td className="px-6 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${
                      r.source === "seed"           ? "bg-slate-100 text-slate-600 ring-slate-500/10" :
                      r.source === "bmv_suggestion" ? "bg-sky-50 text-sky-700 ring-sky-600/15" :
                                                      "bg-emerald-50 text-emerald-700 ring-emerald-700/15"
                    }`}>
                      {r.source}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-[12px] text-slate-500">{r.setBy ?? "—"}</td>
                  <td className="px-6 py-3 text-[12px] text-slate-400 tabular-nums">{r.setAt ? new Date(r.setAt).toLocaleDateString("es-MX") : "—"}</td>
                  <td className="px-6 py-3 text-[12px] text-slate-500 italic truncate max-w-[220px]">{r.note ?? "—"}</td>
                  <td className="px-6 py-3 text-right">
                    {r.currencyCode === "USD" ? (
                      <span className="text-[11px] text-slate-300">—</span>
                    ) : (
                      <button
                        onClick={() => setModal({ open: true, initial: r })}
                        className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                        title={r.locked ? "Editar nota (rate congelado)" : "Editar"}>
                        <Edit2 className="h-3 w-3" /> {r.locked ? "Nota" : "Editar"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex-1">{error}</div>
            <button onClick={() => setError(null)} className="text-rose-700 hover:text-rose-900">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <ConfigureRateModal
        open={modal.open}
        onClose={() => setModal({ open: false, initial: null })}
        initial={modal.initial}
        currencies={currencies}
        onSaved={onSaved} />
    </div>
  );
}

function fmtMonth(yyyymmdd) {
  if (!yyyymmdd) return "—";
  const [y, m] = yyyymmdd.split("-");
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, 1));
  return date.toLocaleDateString("es-MX", { month: "long", year: "numeric", timeZone: "UTC" });
}

/* ===========================================================================
   ConfigureRateModal — crear o editar una tasa
   - initial=null            → modo crear (POST /currencies/rates)
   - initial.id               → modo editar (PATCH /currencies/rates/:id)
   - initial sin id (banner)  → preset desde el banner de "missing"
   - initial.locked === true  → solo se permite editar la nota (rate disabled)
   =========================================================================== */
function ConfigureRateModal({ open, onClose, initial, currencies, onSaved }) {
  const editing = !!initial?.id;
  const locked  = !!initial?.locked;
  const presetOnly = !editing && !!(initial?.currencyCode || initial?.effectiveMonth);

  const [form, setForm] = useState({
    currencyCode: "MXN",
    effectiveMonth: "",
    rateToUsd: "",
    note: "",
  });
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    const today = new Date();
    const defaultMonth = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, "0")}-01`;
    setForm({
      currencyCode:   initial?.currencyCode   ?? "MXN",
      effectiveMonth: initial?.effectiveMonth ?? defaultMonth,
      rateToUsd:      initial?.rateToUsd != null ? String(initial.rateToUsd) : "",
      note:           initial?.note ?? "",
    });
    setErr(null);
  }, [open, initial?.id, initial?.currencyCode, initial?.effectiveMonth]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: name === "effectiveMonth" ? snapToMonthStart(value) : value }));
  };

  const onSuggest = async () => {
    if (!form.currencyCode || !form.effectiveMonth) return;
    setSuggestLoading(true); setErr(null);
    try {
      const r = await api<any>(`/currencies/rates/suggest?currency=${form.currencyCode}&month=${form.effectiveMonth}`);
      setForm(f => ({ ...f, rateToUsd: String(r.rateToUsd) }));
    } catch (e) {
      setErr(e?.body?.message || "No hay sugerencia disponible para esa moneda");
    } finally {
      setSuggestLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const rate = Number(form.rateToUsd);
    if (!locked && (!Number.isFinite(rate) || rate <= 0)) {
      setErr("La tasa debe ser un número mayor a 0");
      return;
    }
    setSubmitting(true); setErr(null);
    try {
      if (editing) {
        // PATCH: si locked solo nota, si no permitido rate + nota
        const body = locked
          ? { note: form.note }
          : { rateToUsd: rate, note: form.note };
        await api(`/currencies/rates/${initial.id}`, { method: "PATCH", body });
      } else {
        await api(`/currencies/rates`, {
          method: "POST",
          body: {
            currencyCode:   form.currencyCode,
            effectiveMonth: form.effectiveMonth,
            rateToUsd:      rate,
            note:           form.note || undefined,
          },
        });
      }
      onSaved();
    } catch (e) {
      // 409 (lead WON existente) muestra el mensaje accionable del backend
      setErr(e?.body?.message || e?.message || "No se pudo guardar la tasa");
    } finally {
      setSubmitting(false);
    }
  };

  // Moneda preview: pre-calculo la frase que va debajo del form para que el
  // usuario sepa exactamente el impacto.
  const monedaName = currencies.find(c => c.code === form.currencyCode)?.name || form.currencyCode;
  const impactMsg = !editing && form.currencyCode !== "USD"
    ? `Esta tasa se usará para convertir todos los leads/proyectos en ${form.currencyCode} (${monedaName}) con fecha en ${fmtMonth(form.effectiveMonth)}. Una vez que haya leads ganados con esta tasa, no podrá editarse el valor (solo la nota).`
    : null;

  return (
    <Modal open={open} onClose={onClose}
           title={editing ? (locked ? "Editar nota (tasa congelada)" : "Editar tasa") : "Configurar tasa"}
           subtitle={editing ? `${initial?.currencyCode} · ${initial?.effectiveMonth}` : "Define la tasa de un mes específico"}>
      <form onSubmit={submit} className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Moneda">
            <select className="input" name="currencyCode" value={form.currencyCode}
                    onChange={onChange} disabled={editing || presetOnly}>
              {currencies.filter(c => c.code !== "USD").map(c => (
                <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Mes">
            <input className="input" name="effectiveMonth" type="date"
                   value={form.effectiveMonth} onChange={onChange}
                   disabled={editing} />
          </Field>
        </div>

        <Field label="Tasa (unidades por 1 USD)">
          <div className="flex gap-2">
            <input className="input flex-1 tabular-nums" name="rateToUsd" type="number"
                   step="0.0001" min="0" value={form.rateToUsd} onChange={onChange}
                   disabled={locked}
                   placeholder={locked ? "Congelada" : "17.85"} />
            {!locked && (
              <button type="button" onClick={onSuggest} disabled={suggestLoading || !form.currencyCode}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                <Sparkles className="h-3.5 w-3.5" />
                {suggestLoading ? "…" : "Sugerir tasa"}
              </button>
            )}
          </div>
          {locked && (
            <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-700">
              <Lock className="h-3 w-3" /> Hay leads cerrados con esta tasa — el valor no puede modificarse, solo la nota.
            </p>
          )}
        </Field>

        <Field label="Nota">
          <textarea className="input min-h-[60px]" name="note" value={form.note}
                    onChange={onChange} placeholder="Ej. Cierre BMV abril 2026" />
        </Field>

        {impactMsg && (
          <p className="rounded-lg bg-slate-50 px-3 py-2.5 text-[12px] leading-relaxed text-slate-600">
            {impactMsg}
          </p>
        )}

        {err && (
          <div className="rounded-lg bg-rose-50 px-3 py-2.5 text-[12px] text-rose-800 ring-1 ring-inset ring-rose-200">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{err}</span>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 border-t border-slate-100 pt-5">
          <button type="button" onClick={onClose} disabled={submitting}
                  className="rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50">
            Cancelar
          </button>
          <button type="submit" disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-xl bg-[var(--brand-primary)] px-5 py-2.5 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
            {editing ? <Edit2 className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {submitting ? "Guardando…" : editing ? "Guardar cambios" : "Crear tasa"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function snapToMonthStart(yyyymmdd) {
  if (!yyyymmdd || yyyymmdd.length < 10) return yyyymmdd;
  return yyyymmdd.slice(0, 8) + "01";
}

/* ===========================================================================
   PipelineFunnelCard — embudo de ventas alimentado por /leads/forecast
   =========================================================================== */
function PipelineFunnelCard() {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fc = await api<any>("/leads/forecast");
        if (!cancelled) setForecast(fc);
      } catch (e) {
        if (!cancelled) setError(e?.body?.message || e?.message || "No se pudo cargar el funnel");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <ChartCard title="Embudo de ventas" subtitle="Conversión y valor por etapa del pipeline">
        <div className="h-72 animate-pulse rounded-xl bg-slate-100" />
      </ChartCard>
    );
  }
  if (error || !forecast) {
    return (
      <ChartCard title="Embudo de ventas" subtitle="Conversión y valor por etapa del pipeline">
        <div className="grid h-72 place-items-center text-sm text-slate-400">
          {error || "Sin datos de pipeline disponibles"}
        </div>
      </ChartCard>
    );
  }

  // V8/V9 shape: forecast.byStage solo contiene stages abiertos (won/lost ya
  // están sumarizados en wonTotalUsd/lostTotalUsd). Todos los valores ya
  // vienen en USD desde el backend.
  const ccy = "USD";
  const openStages = forecast.byStage ?? [];

  const topCount = Math.max(1, ...openStages.map(s => s.count));
  const topValue = Math.max(1, ...openStages.map(s => s.valueUsd));

  return (
    <ChartCard
      title="Embudo de ventas"
      subtitle={`Pipeline ponderado: ${fmtMoneyCcy(forecast.weightedForecastUsd, ccy)} · valor abierto: ${fmtMoneyCcy(forecast.totalPipelineUsd, ccy)}`}
    >
      <div className="space-y-2.5 py-2">
        {openStages.length === 0 && (
          <div className="grid h-40 place-items-center text-sm text-slate-400">
            Sin leads abiertos en el pipeline
          </div>
        )}
        {openStages.map((s, i) => {
          const widthPct = Math.max(8, (s.count / topCount) * 100);
          const valuePct = topValue > 0 ? (s.valueUsd / topValue) * 100 : 0;
          const prev = i > 0 ? openStages[i - 1] : null;
          const stepConversion = prev && prev.count > 0
            ? (s.count / prev.count) * 100
            : null;
          return (
            <div key={s.stageId} className="grid grid-cols-12 items-center gap-3">
              <div className="col-span-3 flex items-center gap-2 truncate">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-slate-400" />
                <span className="text-[12px] font-medium text-slate-700 truncate">{s.stageName}</span>
              </div>
              <div className="col-span-6">
                <div className="relative mx-auto h-7 rounded-md bg-slate-50 ring-1 ring-inset ring-slate-100"
                     style={{ width: `${widthPct}%` }}>
                  <div className="absolute inset-y-0 left-0 rounded-md transition-all duration-500"
                       style={{
                         width: `${Math.max(6, valuePct)}%`,
                         background: "#0F172A",
                         opacity: 0.85,
                       }} />
                  <div className="absolute inset-0 flex items-center justify-between px-2.5 text-[11px] font-medium tabular-nums">
                    <span className="text-white drop-shadow-sm">{s.count} leads</span>
                    {stepConversion != null && (
                      <span className="text-slate-700">{stepConversion.toFixed(0)}%</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-3 text-right text-[12px] tabular-nums">
                <p className="font-serif text-slate-900">{fmtMoneyCcy(s.valueUsd, ccy)}</p>
                <p className="text-[10px] text-slate-400">ponderado {fmtMoneyCcy(s.weightedUsd, ccy)}</p>
              </div>
            </div>
          );
        })}

        {/* Resumen ganados / perdidos (totales agregados, ya en USD) */}
        {(forecast.wonTotalUsd > 0 || forecast.lostTotalUsd > 0) && (
          <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-4">
            <div className="alert-ok rounded-xl px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-80">Ganados</p>
              <p className="mt-1 font-serif text-lg tabular-nums">{fmtMoneyCcy(forecast.wonTotalUsd, ccy)}</p>
              <p className="text-[10px] opacity-70">snapshot inmutable congelado al ganar</p>
            </div>
            <div className="alert-over rounded-xl px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-80">Perdidos</p>
              <p className="mt-1 font-serif text-lg tabular-nums">{fmtMoneyCcy(forecast.lostTotalUsd, ccy)}</p>
              <p className="text-[10px] opacity-70">descartados del pipeline</p>
            </div>
          </div>
        )}
      </div>
    </ChartCard>
  );
}

function stageColor(slug) {
  const map = {
    new:         "#94a3b8",
    contacted:   "#38bdf8",
    qualified:   "#818cf8",
    proposal:    "#fbbf24",
    negotiation: "#fb923c",
    won:         "#22c55e",
    lost:        "#ef4444",
  };
  return map[slug] || "#64748b";
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-5">
      <div className="mb-3">
        <p className="font-serif text-base tracking-tight text-slate-900">{title}</p>
        <p className="text-xs text-slate-500">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

/* ============================================================================
   ApprovalsView — v1.1.2 — admin revisa y aprueba/rechaza creaciones de COMMON_USER
   ============================================================================ */
function ApprovalsView({ projects, clients, onApproveProject, onRejectProject, onApproveClient, onRejectClient }) {
  const pendingProjects = projects.filter(p => p.approvalStatus === "PENDING");
  const pendingClients  = clients.filter(c => c.approvalStatus === "PENDING");
  const total = pendingProjects.length + pendingClients.length;
  return (
    <div className="space-y-6">
      <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">Workflow de aprobación</p>
            <h2 className="font-serif text-2xl tracking-tight text-slate-900">
              {total === 0 ? "Sin solicitudes pendientes" : `${total} solicitud${total === 1 ? "" : "es"} por revisar`}
            </h2>
          </div>
          <div className="flex gap-2">
            <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-700/15">
              {pendingProjects.length} proyectos
            </span>
            <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-700/15">
              {pendingClients.length} clientes
            </span>
          </div>
        </div>
      </div>

      {pendingProjects.length > 0 && (
        <section className="card-soft rounded-2xl border border-slate-200/80 bg-white">
          <div className="border-b border-slate-100 px-6 py-5">
            <h3 className="font-serif text-lg tracking-tight text-slate-900">Proyectos por aprobar</h3>
          </div>
          <ul className="divide-y divide-slate-100">
            {pendingProjects.map(p => (
              <li key={p.id} className="flex flex-col gap-3 px-6 py-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-[15px] font-medium text-slate-900">{p.name}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {p.client} · {p.code} · {fmtHours(p.budgetedHours)}h pactadas · {p.budgetCurrency ?? "USD"} {p.budget}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => onRejectProject(p.id)}
                    className="rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50">
                    Rechazar
                  </button>
                  <button onClick={() => onApproveProject(p.id)}
                    className="rounded-lg bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-800">
                    Aprobar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {pendingClients.length > 0 && (
        <section className="card-soft rounded-2xl border border-slate-200/80 bg-white">
          <div className="border-b border-slate-100 px-6 py-5">
            <h3 className="font-serif text-lg tracking-tight text-slate-900">Clientes por aprobar</h3>
          </div>
          <ul className="divide-y divide-slate-100">
            {pendingClients.map(c => (
              <li key={c.id} className="flex flex-col gap-3 px-6 py-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-[15px] font-medium text-slate-900">{c.name}</p>
                  <p className="mt-0.5 text-xs text-slate-500">{c.industry ?? "—"} · {c.contactEmail ?? "Sin email"}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => onRejectClient(c.id)}
                    className="rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50">
                    Rechazar
                  </button>
                  <button onClick={() => onApproveClient(c.id)}
                    className="rounded-lg bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-800">
                    Aprobar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {total === 0 && (
        <div className="card-soft rounded-2xl border border-dashed border-slate-200 bg-white p-12 text-center">
          <p className="text-sm text-slate-500">No hay solicitudes pendientes en este momento. Buen trabajo.</p>
        </div>
      )}
    </div>
  );
}

/* ============================================================================
   USERS_INIT — staff del tenant con sus tarifas por hora
   ============================================================================ */
const USERS_INIT = [
  { id:"u-1", name:"María S.",   email:"maria.solis@ejemplo.demo",   role:"COMMON_USER", hourlyRate: 55, isActive: true },
  { id:"u-2", name:"Andrea P.",  email:"andrea.paz@ejemplo.demo",    role:"COMMON_USER", hourlyRate: 60, isActive: true },
  { id:"u-3", name:"Diego R.",   email:"diego.romero@ejemplo.demo",  role:"COMMON_USER", hourlyRate: 50, isActive: true },
  { id:"u-4", name:"Karim B.",   email:"karim.bauer@ejemplo.demo",   role:"COMMON_USER", hourlyRate: 65, isActive: true },
  { id:"u-5", name:"Sara A.",    email:"admin@atelier.demo",         role:"SUPER_USER",  hourlyRate: 90, isActive: true },
];

/* ============================================================================
   NewTaskModal — crear una actividad (tarjeta) del Kanban
   ============================================================================ */
function NewTaskModal({ open, onClose, projectId, users, onCreate }) {
  const [form, setForm] = useState({
    title: "", status: "TODO", assignee: users[0]?.name ?? "", hoursEst: 8,
  });
  useEffect(() => {
    if (open) setForm({ title: "", status: "TODO", assignee: users[0]?.name ?? "", hoursEst: 8 });
  }, [open, users]);
  const onChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };
  const submit = e => {
    e.preventDefault();
    if (!form.title.trim()) return;
    onCreate({
      id: `t-${Date.now()}`,
      projectId,
      title: form.title.trim(),
      status: form.status,
      assignee: form.assignee,
      hoursEst: Number(form.hoursEst) || 0,
      hoursLogged: 0,
    });
    onClose();
  };
  return (
    <Modal open={open} onClose={onClose}
           title="Nueva actividad"
           subtitle="Se añade al tablero del proyecto">
      <form onSubmit={submit} className="space-y-5">
        <Field label="Título" required>
          <input className="input" name="title" value={form.title} onChange={onChange}
                 placeholder="Ej. Maquetar landing de pricing" required />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Estado inicial">
            <select className="input" name="status" value={form.status} onChange={onChange}>
              <option value="TODO">Por hacer</option>
              <option value="IN_PROGRESS">En curso</option>
              <option value="REVIEW">Revisión</option>
              <option value="DONE">Completado</option>
            </select>
          </Field>
          <Field label="Asignado">
            <select className="input" name="assignee" value={form.assignee} onChange={onChange}>
              {users.filter(u => u.isActive !== false).map(u =>
                <option key={u.id} value={u.name}>{u.name} (${u.hourlyRate}/h)</option>
              )}
            </select>
          </Field>
        </div>
        <Field label="Horas estimadas">
          <input className="input tabular-nums" type="number" min="0" step="0.5"
                 name="hoursEst" value={form.hoursEst} onChange={onChange} />
        </Field>
        <div className="flex justify-end gap-3 border-t border-slate-100 pt-5">
          <button type="button" onClick={onClose}
                  className="rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
            Cancelar
          </button>
          <button type="submit"
                  className="inline-flex items-center gap-2 rounded-xl bg-[var(--brand-primary)] px-5 py-2.5 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
            <Plus className="h-4 w-4" /> Crear actividad
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ============================================================================
   ContactFormModal — crea o edita un contacto del CRM
   ============================================================================ */
function ContactFormModal({ open, onClose, initial, onSave }) {
  const [form, setForm] = useState({
    name: "", role: "", company: "", email: "", phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (open) {
      setForm({
        name: initial?.name ?? "",
        // El contacto del hook trae `jobTitle`; el modal lo edita como `role`.
        role: initial?.role ?? initial?.jobTitle ?? "", company: initial?.company ?? "",
        email: initial?.email ?? "", phone: initial?.phone ?? "",
      });
      setErr(null);
    }
  }, [open, initial]);
  const onChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };
  const submit = async e => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true); setErr(null);
    try {
      // onSave persiste en /contacts (POST/PUT). Solo pasamos id si es edición.
      await onSave({ id: initial?.id, ...form, name: form.name.trim() });
      onClose();
    } catch (e2) {
      const m = e2?.body?.message;
      setErr(Array.isArray(m) ? m.join(" · ") : (m || e2?.message || "No se pudo guardar el contacto"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={open} onClose={onClose}
           title={initial ? "Editar contacto" : "Nuevo contacto"}
           subtitle={initial ? "Actualiza datos del contacto" : "Da de alta un contacto"}>
      <form onSubmit={submit} className="space-y-5">
        <Field label="Nombre" required>
          <input className="input" name="name" value={form.name} onChange={onChange} required />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Cargo">
            <input className="input" name="role" value={form.role} onChange={onChange}
                   placeholder="CFO, Director Comercial..." />
          </Field>
          <Field label="Empresa">
            <input className="input" name="company" value={form.company} onChange={onChange} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Email">
            <input className="input" type="email" name="email" value={form.email} onChange={onChange} />
          </Field>
          <Field label="Teléfono">
            <input className="input" name="phone" value={form.phone} onChange={onChange} />
          </Field>
        </div>
        {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
        <div className="flex justify-end gap-3 border-t border-slate-100 pt-5">
          <button type="button" onClick={onClose}
                  className="rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
            Cancelar
          </button>
          <button type="submit" disabled={busy}
                  className="inline-flex items-center gap-2 rounded-xl bg-[var(--brand-primary)] px-5 py-2.5 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
            <Plus className="h-4 w-4" /> {busy ? "Guardando…" : (initial ? "Guardar" : "Crear")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ============================================================================
   TagFormModal — crea o edita un tipo de actividad (tag)
   ============================================================================ */
function TagFormModal({ open, onClose, initial, onSave }) {
  const [form, setForm] = useState({
    name: "", color: "#475569", isBillableDefault: true,
  });
  useEffect(() => {
    if (open) setForm({
      name: initial?.name ?? "",
      color: initial?.color ?? "#475569",
      isBillableDefault: initial?.isBillableDefault ?? true,
    });
  }, [open, initial]);
  const onChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };
  const submit = e => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSave({ id: initial?.id ?? `tag-${Date.now()}`, ...form, name: form.name.trim() });
    onClose();
  };
  const palette = ["#0F172A","#2C5C3F","#7B2D26","#7B5A12","#475569","#6B4F8E","#1F4D6B"];
  return (
    <Modal open={open} onClose={onClose}
           title={initial ? "Editar tipo de actividad" : "Nuevo tipo de actividad"}
           maxWidth="max-w-md">
      <form onSubmit={submit} className="space-y-5">
        <Field label="Nombre" required>
          <input className="input" name="name" value={form.name} onChange={onChange}
                 placeholder="Ej. QA / Pruebas" required />
        </Field>
        <Field label="Color">
          <div className="flex flex-wrap gap-2">
            {palette.map(c => (
              <button type="button" key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                      className={`h-8 w-8 rounded-lg ring-2 transition ${form.color === c ? "ring-slate-900" : "ring-transparent"}`}
                      style={{ background: c }} aria-label={c} />
            ))}
            <input type="color" name="color" value={form.color} onChange={onChange}
                   className="h-8 w-12 rounded-lg border border-slate-200 cursor-pointer" />
          </div>
        </Field>
        <label className="inline-flex items-center gap-3 text-sm text-slate-600">
          <span className="relative inline-flex h-5 w-9 items-center">
            <input type="checkbox" name="isBillableDefault" checked={form.isBillableDefault}
                   onChange={onChange} className="peer sr-only" />
            <span className="absolute inset-0 rounded-full bg-slate-200 transition peer-checked:bg-slate-900" />
            <span className="absolute left-0.5 h-4 w-4 rounded-full bg-white shadow transition peer-checked:translate-x-4" />
          </span>
          Facturable por defecto
        </label>
        <div className="flex justify-end gap-3 border-t border-slate-100 pt-5">
          <button type="button" onClick={onClose}
                  className="rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
            Cancelar
          </button>
          <button type="submit"
                  className="inline-flex items-center gap-2 rounded-xl bg-[var(--brand-primary)] px-5 py-2.5 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
            <Plus className="h-4 w-4" /> {initial ? "Guardar" : "Crear"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ============================================================================
   ClientFormModal — crea o edita un cliente final
   ============================================================================ */
function ClientFormModal({ open, onClose, initial, onSave, isAdmin }) {
  const [form, setForm] = useState({
    name: "", industry: "", contactEmail: "", contactPhone: "", notes: "",
    isActive: true,
  });
  useEffect(() => {
    if (open) {
      setForm({
        name: initial?.name ?? "",
        industry: initial?.industry ?? "",
        contactEmail: initial?.contactEmail ?? "",
        contactPhone: initial?.contactPhone ?? "",
        notes: initial?.notes ?? "",
        isActive: initial?.isActive ?? true,
      });
    }
  }, [open, initial]);
  const onChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };
  const submit = e => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSave({
      ...initial, ...form, name: form.name.trim(),
      // v1.1.2: si no es admin, el alta queda pendiente de aprobación
      approvalStatus: initial?.approvalStatus ?? (isAdmin ? "APPROVED" : "PENDING"),
    });
    onClose();
  };
  return (
    <Modal open={open} onClose={onClose}
           title={initial ? "Editar cliente" : "Nuevo cliente"}
           subtitle={initial ? "Actualiza datos del cliente final" : "Da de alta un nuevo cliente final"}>
      <form onSubmit={submit} className="space-y-5">
        <Field label="Nombre" required>
          <input className="input" name="name" value={form.name} onChange={onChange} required />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Industria">
            <input className="input" name="industry" value={form.industry} onChange={onChange} />
          </Field>
          <Field label="Email">
            <input className="input" type="email" name="contactEmail" value={form.contactEmail} onChange={onChange} />
          </Field>
        </div>
        <Field label="Teléfono">
          <input className="input" name="contactPhone" value={form.contactPhone} onChange={onChange} />
        </Field>
        <Field label="Notas">
          <textarea className="input resize-none" rows={3} name="notes" value={form.notes} onChange={onChange} />
        </Field>
        {initial && (
          <label className="inline-flex items-center gap-3 text-sm text-slate-600">
            <span className="relative inline-flex h-5 w-9 items-center">
              <input type="checkbox" name="isActive" checked={form.isActive} onChange={onChange} className="peer sr-only" />
              <span className="absolute inset-0 rounded-full bg-slate-200 transition peer-checked:bg-slate-900" />
              <span className="absolute left-0.5 h-4 w-4 rounded-full bg-white shadow transition peer-checked:translate-x-4" />
            </span>
            Activo
          </label>
        )}
        {!initial && !isAdmin && (
          <div className="alert-warn rounded-xl px-4 py-3 text-xs">
            <p className="font-medium">Este cliente quedará pendiente de aprobación</p>
            <p className="mt-1 opacity-80">Un admin del tenant lo revisará en "Aprobaciones" antes de que aparezca en la cartera.</p>
          </div>
        )}
        <div className="flex justify-end gap-3 border-t border-slate-100 pt-5">
          <button type="button" onClick={onClose}
                  className="rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
            Cancelar
          </button>
          <button type="submit"
                  className="inline-flex items-center gap-2 rounded-xl bg-[var(--brand-primary)] px-5 py-2.5 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
            <Plus className="h-4 w-4" /> {initial ? "Guardar" : "Crear"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function StatTile({ label, value, hint = null }) {
  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-6">
      <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <p className="mt-2 font-serif text-3xl tracking-tight text-slate-900">{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

/** BudgetBar — verde si bajo el límite, rust si lo supera */
function BudgetBar({ logged, budgeted, compact = false }) {
  const pct = budgeted ? (logged / budgeted) * 100 : null;
  const tone = budgetTone(pct);
  const widthPct = pct == null ? 0 : Math.min(100, pct);
  return (
    <div className="w-full">
      {!compact && (
        <div className="flex items-center justify-between pb-2">
          <span className="text-[11px] font-medium uppercase tracking-wider text-slate-400">
            Presupuesto de horas
          </span>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ${tone.chipCls}`}>
            {(tone.name === "OVER" || tone.name === "CRIT") && <AlertTriangle className="h-3 w-3" />}
            {tone.label}
          </span>
        </div>
      )}
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`absolute left-0 top-0 h-full rounded-full ${tone.barCls} transition-all duration-700`}
             style={{ width: `${widthPct}%` }} />
        {pct != null && pct > 100 && (
          <div className="absolute top-0 h-full w-px bg-white/80" style={{ left: "100%", transform: "translateX(-1px)" }} />
        )}
      </div>
      <div className="mt-2 flex items-baseline justify-between">
        <p className="text-sm tabular-nums text-slate-700">
          <span className="font-semibold text-slate-900">{logged.toFixed(0)}h</span>
          <span className="text-slate-400"> / {budgeted}h</span>
        </p>
        {pct != null && (
          <p className={`text-xs font-medium tabular-nums ${tone.name === "OVER" || tone.name === "CRIT" ? "text-rose-700" : "text-slate-500"}`}>
            {pct.toFixed(1)}%
          </p>
        )}
      </div>
    </div>
  );
}

function Progress({ value }) {
  const v = Math.max(0, Math.min(100, value));
  const tone = v >= 85 ? "bg-emerald-600" : v >= 50 ? "bg-slate-900" : v >= 25 ? "bg-amber-500" : "bg-rose-600";
  return (
    <div className="w-full">
      <div className="flex items-center justify-between pb-1.5">
        <span className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Avance</span>
        <span className="text-xs font-semibold tabular-nums text-slate-700">{v}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${tone} transition-all duration-500`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
}

/* ===========================================================================
   Sidebar
   =========================================================================== */
/**
 * Construye URL absoluta para logo. Path relativo /api/v1/static/... se
 * prefija con API_BASE; URLs externas pasan tal cual.
 */
function resolveLogoSrc(rawUrl) {
  if (!rawUrl) return null;
  if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
  // API_BASE típico: "/api/v1" en dev (proxy) o "https://api.example.com/api/v1" en prod.
  // El backend devuelve paths ABSOLUTOS desde "/api/v1/static/..." — extraemos la base sin "/api/v1".
  const baseRoot = API_BASE.replace(/\/api\/v\d+\/?$/, "");
  return baseRoot + rawUrl;
}

/**
 * Avatar/logo del tenant. Si brandLogoUrl existe, <img> con fallback a la
 * inicial si la carga falla (404, file removed). Sin URL → render directo
 * de la inicial.
 */
function TenantBrandMark({ brandName, brandLogoUrl, size = 32 }) {
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [brandLogoUrl]);
  const initial = (brandName ?? "—").trim().slice(0, 1).toUpperCase();
  const dim = { width: size, height: size };
  if (brandLogoUrl && !imgFailed) {
    return (
      <div className="grid place-items-center rounded-lg bg-white ring-1 ring-slate-200 overflow-hidden" style={dim}>
        <img
          src={resolveLogoSrc(brandLogoUrl) ?? brandLogoUrl}
          alt={brandName ?? "Logo"}
          onError={() => setImgFailed(true)}
          className="h-full w-full object-contain p-1"
        />
      </div>
    );
  }
  return (
    <div className="grid place-items-center rounded-lg bg-[var(--brand-primary)] text-[var(--brand-primary-fg)]" style={dim}>
      <span className="font-serif text-sm italic">{initial}</span>
    </div>
  );
}

function Sidebar({ active, setActive, open, setOpen, user, me, onToggleRole, onLogout, pendingApprovalsCount = 0, isPlatformAdmin = false }) {
  const doLogout = () => {
    if (onLogout) return onLogout();
    // Fallback: limpia el JWT y recarga (vuelve a la pantalla de login)
    try { localStorage.removeItem("ag_stratos.token"); } catch (_) {}
    window.location.reload();
  };
  // §18 — módulos habilitados del tenant (mode switcher). Fallback a los 3 si /me
  // aún no los trae (compat). Los tabs se muestran si: el MODE los incluye Y el
  // ROL los permite (PR1) — los dos ejes componen.
  const enabledModules = (me?.tenant?.enabledModules?.length
    ? me.tenant.enabledModules
    : ["crm", "boutique", "gastro"]);
  const hasModule = (m) => enabledModules.includes(m);

  // CRM core: cada item con `mod:'crm'` se oculta si el tenant no tiene CRM.
  // `contacts` (clientes compartidos) es transversal → sin gate de módulo.
  // `tickets` (Soporte) y `settings` (Configuración) también son transversales,
  // pero viven en el grupo general del footer fijo (ver generalItems), no aquí.
  const items = [
    { id: "crm",      label: "Registro de actividades", Icon: Users,        mod: "crm" },
    { id: "contacts", label: "Contactos",     Icon: User },
    // V92 — Comunicados: correo masivo a contactos. Gate = cualquier módulo CON
    // contactos (crm|boutique|gym; un tenant solo-gastro no lo ve) + permiso.
    ...((hasModule("crm") || hasModule("boutique") || hasModule("gym"))
      && hasPermission(user, "communications:manage")
      ? [{ id: "comunicados", label: "Comunicados", Icon: Send }]
      : []),
    { id: "pipeline", label: "Pipeline",      Icon: Target,       mod: "crm" },
    { id: "quotes",   label: "Cotizaciones",  Icon: FileText,     mod: "crm" },
    { id: "projects", label: "Proyectos",     Icon: FolderKanban, mod: "crm" },
    { id: "clients",  label: "Clientes",      Icon: Building2,    mod: "crm" },
    { id: "reports",  label: "Reportes",      Icon: BarChart3,    mod: "crm" },
    ...(user.role === "SUPER_USER"
      ? [{ id: "approvals", label: "Aprobaciones", Icon: AlertTriangle, badge: pendingApprovalsCount, mod: "crm" }]
      : []),
  ].filter((it) => !it.mod || hasModule(it.mod));
  // v1.4.0 Boutique Mode foundation — sección separada (negocios de servicios).
  const boutiqueItems = [
    // POS en modo "Venta rápida" (mismo kernel que Mesas; carrito directo → cobrar).
    { id: "boutique-pos", label: "Venta rápida", Icon: ShoppingCart },
    // V196 — bandeja de pedidos en línea (recoger en sucursal); reusa Pendientes de entrega.
    { id: "boutique-pedidos", label: "Pedidos en línea", Icon: Package },
    { id: "boutique-services", label: "Servicios", Icon: Sparkles },
    { id: "boutique-products", label: "Productos", Icon: Building2 },
    { id: "boutique-agenda",   label: "Agenda",    Icon: Calendar },
    { id: "boutique-reminders", label: "Recordatorios", Icon: Bell },
    { id: "boutique-history",  label: "Historial", Icon: Clock },
    // feat/analytics-por-modulo — analítica de Boutique (filtra source_module='boutique').
    // Mismo gating que GastroGest: Insights/Margen = dato del dueño (financials:view);
    // Ticket = operativo o dueño; Cierre = operativo (el Cajero cierra su caja).
    ...(hasPermission(user, "financials:view")
      ? [
          { id: "boutique-sales",  label: "Insights", Icon: Receipt },
          { id: "boutique-margen", label: "Margen",   Icon: TrendingUp },
        ]
      : []),
    ...((hasPermission(user, "pos:view") || hasPermission(user, "financials:view"))
      ? [{ id: "boutique-ticket", label: "Ticket y volumen", Icon: BarChart3 }]
      : []),
    { id: "boutique-corte", label: "Cierre de día", Icon: Calculator },
  ];
  // v1.5.0 GastroGest — modo hospitalidad sobre el kernel de comercio.
  // §18 — "Insights" es dato del dueño: solo si el rol tiene financials:view
  // (Admin/Gerente). El Cajero ve Mesas y Cierre de día (operativo) pero NO Insights.
  const gastroItems = [
    { id: "gastro-pos",   label: "Mesas",  Icon: UtensilsCrossed },
    // §31.2 (V131) — Vista de cocina (semáforo FIFO). Operativa: pos:view (mesero/cajero;
    // admin bypass). El feed y las acciones están gateadas a 'gastro' + branch en el API.
    ...(hasPermission(user, "pos:view")
      ? [{ id: "gastro-cocina", label: "Cocina", Icon: ChefHat }]
      : []),
    // §32/A0 (Hallazgo 4) — Pedidos para recoger pendientes de entrega: destino propio
    // (mostrador/escuela no viven en "Mesas"). Operativa: pos:view; gate 'gastro' + branch en API.
    ...(hasPermission(user, "pos:view")
      ? [{ id: "gastro-pendientes", label: "Pendientes de entrega", Icon: Package }]
      : []),
    // feat/inventory-management — Productos (menú/catálogo + stock) también para GastroGest.
    // Reusa ProductsView (mismo puente que Wellness). API gateada por product_catalog:read/write.
    { id: "gastro-products", label: "Productos", Icon: Building2 },
    // V54 — Insumos (ingredientes): control de inventario a nivel insumo + recetas.
    { id: "gastro-insumos", label: "Insumos", Icon: Container },
    // V86 — "Arma tu platillo": biblioteca de modificadores (config de catálogo,
    // solo con product_catalog:write — el Cajero arma en el POS pero no configura).
    ...(hasPermission(user, "product_catalog:write")
      ? [
          { id: "gastro-modificadores", label: "Modificadores", Icon: Layers },
          // V126 (#310) — Paquetes prix-fixe (menú por tiempos). Mismo gate que
          // Modificadores: config de catálogo (product_catalog:write).
          { id: "gastro-paquetes", label: "Paquetes", Icon: Package },
        ]
      : []),
    // §18/§21.3 — Insights y Margen son dato del dueño (financials:view).
    ...(hasPermission(user, "financials:view")
      ? [
          { id: "gastro-sales",  label: "Insights", Icon: Receipt },
          { id: "gastro-margen", label: "Margen",   Icon: TrendingUp },
        ]
      : []),
    // §21.3 — Ticket y volumen NO expone costo: visible a operativos (pos:view) y al dueño.
    // Pink — Tiempos de cocina tampoco expone costo → mismo gate operativo.
    ...((hasPermission(user, "pos:view") || hasPermission(user, "financials:view"))
      ? [
          { id: "gastro-ticket", label: "Ticket y volumen", Icon: BarChart3 },
          { id: "gastro-cocina-tiempos", label: "Tiempos de cocina", Icon: Clock },
        ]
      : []),
    { id: "gastro-corte", label: "Cierre de día", Icon: Calculator },
  ];
  // §20 Gym/Estudio — espacio de membresías. §18: solo si el tenant tiene 'gym'.
  const gymItems = [
    // POS "Venta rápida" (modo quick, catálogo de productos). Los planes se venden
    // en "Miembros" (/gym/members) — NO se duplican aquí.
    { id: "gym-pos",      label: "Venta rápida", Icon: ShoppingCart },
    // V196 — bandeja de pedidos en línea (recoger en sucursal); reusa Pendientes de entrega.
    { id: "gym-pedidos",  label: "Pedidos en línea", Icon: Package },
    // feat/gym-catalog-bridge — gestión de productos del gym (proteínas, suplementos,
    // agua…). Reusa la vista de Boutique; el catálogo es compartido por tenant (puente).
    { id: "gym-products", label: "Productos", Icon: Building2 },
    { id: "gym-members",  label: "Miembros", Icon: Users },
    { id: "gym-checkin",  label: "Check-in", Icon: UserCheck },
    // V69 — motor de booking Fase 1: calendario de clases con cupo + reservas staff.
    { id: "gym-classes",  label: "Clases",   Icon: Calendar },
    { id: "gym-plans",    label: "Planes",   Icon: Dumbbell },
    { id: "gym-visits",   label: "Visitas",  Icon: BarChart3 },
    // feat/analytics-por-modulo — analítica de Wellness (filtra source_module='gym').
    // Mismo gating que GastroGest (Wellness == módulo 'gym' en V46/enabled_modules).
    ...(hasPermission(user, "financials:view")
      ? [
          { id: "gym-sales",  label: "Insights", Icon: Receipt },
          { id: "gym-margen", label: "Margen",   Icon: TrendingUp },
        ]
      : []),
    ...((hasPermission(user, "pos:view") || hasPermission(user, "financials:view"))
      ? [{ id: "gym-ticket", label: "Ticket y volumen", Icon: BarChart3 }]
      : []),
    { id: "gym-corte", label: "Cierre de día", Icon: Calculator },
  ];
  // §21.1 Finanzas — P&L + gastos. DATO DEL DUEÑO: cross-vertical, gateado SOLO por
  // la capacidad financials:view (no es un módulo de vertical). Mismo §18 que el margen.
  // "Mis facturas" (V98 follow-up) vive aquí con SU permiso (cfdi_sales:view):
  // cada item se gatea solo y el grupo aparece si hay al menos uno visible.
  const finanzasItems = [
    ...(hasPermission(user, "financials:view") ? [
      { id: "finance-expenses", label: "Gastos", Icon: Receipt },
      { id: "finance-pnl",      label: "Ingresos vs gastos", Icon: TrendingUp },
    ] : []),
    ...(hasPermission(user, "cfdi_sales:view") ? [
      { id: "finance-cfdi", label: "Mis facturas", Icon: FileText },
    ] : []),
  ];
  // Transversales (Soporte, Configuración): no son un "espacio" de trabajo, así
  // que van en el grupo general del footer fijo del aside (sobre la cajita de
  // usuario), fuera del <nav> scrolleable. Misma key/ruta de siempre.
  // feat/frontend-import-ui — "Importar datos" (carga masiva self-service). Transversal
  // pero SOLO comercio: visible si el tenant tiene un módulo de comercio (gastro/boutique/
  // gym) Y el rol puede escribir catálogo (product_catalog:write). El Cajero NO lo ve.
  const canImport =
    (hasModule("gastro") || hasModule("boutique") || hasModule("gym")) &&
    hasPermission(user, "product_catalog:write");
  const generalItems = [
    // Carril A PR2 — Centro de ayuda / FAQ por vertical (contextual, alcanzable
    // desde cualquier lado). "¿Cómo funciona?" del topbar re-lanza el tour.
    { id: "help",     label: "Ayuda",          Icon: LifeBuoy },
    { id: "tickets",  label: "Soporte",       Icon: LifeBuoy },
    ...(canImport ? [{ id: "import", label: "Importar datos", Icon: Upload }] : []),
    { id: "settings", label: "Configuración", Icon: Settings },
  ];
  // Day 5 Block B: sección PLATAFORMA solo para el owner. Separada visualmente.
  const platformItems = isPlatformAdmin ? [
    { id: "platform-tenants", label: "Tenants",          Icon: Building2 },
    { id: "platform-tickets", label: "Tickets globales", Icon: AlertTriangle },
    { id: "platform-feedback", label: "Comentarios",     Icon: MessageSquarePlus },
    { id: "platform-cfdi-sales", label: "CFDI Ventas",   Icon: Receipt },
    // Capa 1: facturas que FLUIDSOL emite (suscripción + adicionales) — como
    // emisor, ver al receptor es legítimo (distinto de la privacidad de Capa 2).
    { id: "platform-cfdi-subs", label: "CFDI Suscripciones", Icon: FileText },
  ] : [];
  return (
    <>
      {/* fix/mobile-drawer: backdrop con tinte real (bg-slate-900/40) para separar
          el menú del fondo; tap fuera cierra. z-30 queda por debajo del aside (z-40). */}
      {open && <div className="fixed inset-0 z-30 bg-slate-900/40 backdrop-blur-sm md:hidden" onClick={() => setOpen(false)} />}
      {/* fix/mobile-drawer: h-[100dvh] usa el viewport dinámico para que la barra de
          direcciones del móvil no tape el fondo del drawer; md:h-auto deja intacto el
          sidebar estático en desktop. */}
      <aside className={`fixed inset-y-0 left-0 z-40 flex h-[100dvh] w-72 flex-col border-r border-slate-200/80 bg-white transition-transform duration-300 md:static md:h-auto md:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center justify-between px-7 pt-7 pb-10">
          <div className="flex items-center gap-2.5">
            {/* Day 5 fix-logo: logo del tenant con fallback a inicial si falla. */}
            <TenantBrandMark
              brandName={me?.tenant?.branding?.brandName ?? me?.tenant?.name}
              brandLogoUrl={me?.tenant?.branding?.brandLogoUrl}
              size={32}
            />
            <div className="leading-tight">
              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
                {(me?.tenant?.branding?.brandName ?? me?.tenant?.name ?? "Mi negocio").slice(0, 18)}
              </p>
              <p className="text-sm font-semibold text-slate-900">FluidSol</p>
            </div>
          </div>
          <button className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 md:hidden" onClick={() => setOpen(false)}>
            <X className="h-5 w-5" />
          </button>
        </div>
        {/* fix/mobile-drawer: min-h-0 + overflow-y-auto permiten que SOLO la lista de
            espacios scrollee dentro del flex-col; el header (logo) y el footer (perfil
            + legales) quedan fijos. Sin min-h-0 un hijo flex-1 no encoge y no scrollea. */}
        <nav className="flex-1 min-h-0 overflow-y-auto px-4">
          <p className="px-3 pb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Flow</p>
          <ul className="space-y-1">
            {items.map(({ id, label, Icon, badge }) => {
              const isActive = active === id || (id === "projects" && active === "project-hub");
              return (
                <li key={id}>
                  <button data-tour={`nav-${id}`} onClick={() => { setActive(id); setOpen(false); }}
                    className={`group flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-sm transition ${isActive ? "bg-[var(--brand-primary)] text-[var(--brand-primary-fg)] shadow-sm" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                    <span className="flex items-center gap-3">
                      <Icon className={`h-4 w-4 ${isActive ? "text-[var(--brand-primary-fg)]" : "text-slate-400 group-hover:text-slate-600"}`} />
                      <span className="font-medium">{label}</span>
                    </span>
                    <span className="flex items-center gap-2">
                      {badge > 0 && (
                        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${isActive ? "bg-white/15 text-white" : "bg-rose-50 text-rose-700"}`}>
                          {badge}
                        </span>
                      )}
                      <ChevronRight className={`h-4 w-4 ${isActive ? "text-[var(--brand-primary-fg)] opacity-70" : "text-slate-300 group-hover:text-slate-400"}`} />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* v1.4.0 Boutique Mode — sección de negocios de servicios. §18: solo si
              el tenant tiene el módulo 'boutique'. */}
          {hasModule("boutique") && (<>
          <p className="mt-6 px-3 pb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Boutique</p>
          <ul className="space-y-1">
            {boutiqueItems.map(({ id, label, Icon }) => {
              const isActive = active === id;
              return (
                <li key={id}>
                  <button data-tour={`nav-${id}`} onClick={() => { setActive(id); setOpen(false); }}
                    className={`group flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-sm transition ${isActive ? "bg-[var(--brand-primary)] text-[var(--brand-primary-fg)] shadow-sm" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                    <span className="flex items-center gap-3">
                      <Icon className={`h-4 w-4 ${isActive ? "text-[var(--brand-primary-fg)]" : "text-slate-400 group-hover:text-slate-600"}`} />
                      <span className="font-medium">{label}</span>
                    </span>
                    <ChevronRight className={`h-4 w-4 ${isActive ? "text-[var(--brand-primary-fg)] opacity-70" : "text-slate-300 group-hover:text-slate-400"}`} />
                  </button>
                </li>
              );
            })}
          </ul>
          </>)}

          {/* v1.5.0 GastroGest — modo hospitalidad (kernel de comercio). §18: solo
              si el tenant tiene el módulo 'gastro'. */}
          {hasModule("gastro") && (<>
          <p className="mt-6 px-3 pb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">GastroGest</p>
          <ul className="space-y-1">
            {gastroItems.map(({ id, label, Icon }) => {
              const isActive = active === id;
              return (
                <li key={id}>
                  <button data-tour={`nav-${id}`} onClick={() => { setActive(id); setOpen(false); }}
                    className={`group flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-sm transition ${isActive ? "bg-[var(--brand-primary)] text-[var(--brand-primary-fg)] shadow-sm" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                    <span className="flex items-center gap-3">
                      <Icon className={`h-4 w-4 ${isActive ? "text-[var(--brand-primary-fg)]" : "text-slate-400 group-hover:text-slate-600"}`} />
                      <span className="font-medium">{label}</span>
                    </span>
                    <ChevronRight className={`h-4 w-4 ${isActive ? "text-[var(--brand-primary-fg)] opacity-70" : "text-slate-300 group-hover:text-slate-400"}`} />
                  </button>
                </li>
              );
            })}
          </ul>
          </>)}

          {/* §20 Gym/Estudio — espacio de membresías. §18: solo si el tenant tiene 'gym'. */}
          {hasModule("gym") && (<>
          <p className="mt-6 px-3 pb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Wellness</p>
          <ul className="space-y-1">
            {gymItems.map(({ id, label, Icon }) => {
              const isActive = active === id;
              return (
                <li key={id}>
                  <button data-tour={`nav-${id}`} onClick={() => { setActive(id); setOpen(false); }}
                    className={`group flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-sm transition ${isActive ? "bg-[var(--brand-primary)] text-[var(--brand-primary-fg)] shadow-sm" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                    <span className="flex items-center gap-3">
                      <Icon className={`h-4 w-4 ${isActive ? "text-[var(--brand-primary-fg)]" : "text-slate-400 group-hover:text-slate-600"}`} />
                      <span className="font-medium">{label}</span>
                    </span>
                    <ChevronRight className={`h-4 w-4 ${isActive ? "text-[var(--brand-primary-fg)] opacity-70" : "text-slate-300 group-hover:text-slate-400"}`} />
                  </button>
                </li>
              );
            })}
          </ul>
          </>)}

          {/* §21.1 Finanzas — P&L + gastos (financials:view) + Mis facturas
              (cfdi_sales:view). Cada item ya viene gateado en finanzasItems. */}
          {finanzasItems.length > 0 && (<>
          <p className="mt-6 px-3 pb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Finanzas</p>
          <ul className="space-y-1">
            {finanzasItems.map(({ id, label, Icon }) => {
              const isActive = active === id;
              return (
                <li key={id}>
                  <button data-tour={`nav-${id}`} onClick={() => { setActive(id); setOpen(false); }}
                    className={`group flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-sm transition ${isActive ? "bg-[var(--brand-primary)] text-[var(--brand-primary-fg)] shadow-sm" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                    <span className="flex items-center gap-3">
                      <Icon className={`h-4 w-4 ${isActive ? "text-[var(--brand-primary-fg)]" : "text-slate-400 group-hover:text-slate-600"}`} />
                      <span className="font-medium">{label}</span>
                    </span>
                    <ChevronRight className={`h-4 w-4 ${isActive ? "text-[var(--brand-primary-fg)] opacity-70" : "text-slate-300 group-hover:text-slate-400"}`} />
                  </button>
                </li>
              );
            })}
          </ul>
          </>)}

          {/* Day 5 Block B: sección PLATAFORMA — visible solo si isPlatformAdmin */}
          {platformItems.length > 0 && (
            <>
              <p className="mt-6 px-3 pb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700">
                Plataforma
              </p>
              <ul className="space-y-1">
                {platformItems.map(({ id, label, Icon }) => {
                  const isActive = active === id;
                  return (
                    <li key={id}>
                      <button data-tour={`nav-${id}`} onClick={() => { setActive(id); setOpen(false); }}
                        className={`group flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-sm transition ${isActive ? "bg-amber-600 text-white shadow-sm" : "text-amber-800 hover:bg-amber-50"}`}>
                        <span className="flex items-center gap-3">
                          <Icon className={`h-4 w-4 ${isActive ? "text-white" : "text-amber-600 group-hover:text-amber-700"}`} />
                          <span className="font-medium">{label}</span>
                        </span>
                        <ChevronRight className={`h-4 w-4 ${isActive ? "text-white/70" : "text-amber-300"}`} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </nav>
        {/* Grupo general (transversales) — zona FIJA del aside, fuera del <nav>
            scrolleable: Soporte + Configuración siempre visibles, también en el
            drawer móvil. El border-t lo separa de la lista de espacios. */}
        <div className="border-t border-slate-200/70 px-4 pt-3">
          <ul className="space-y-1">
            {generalItems.map(({ id, label, Icon }) => {
              const isActive = active === id;
              return (
                <li key={id}>
                  <button data-tour={`nav-${id}`} onClick={() => { setActive(id); setOpen(false); }}
                    className={`group flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-sm transition ${isActive ? "bg-[var(--brand-primary)] text-[var(--brand-primary-fg)] shadow-sm" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                    <span className="flex items-center gap-3">
                      <Icon className={`h-4 w-4 ${isActive ? "text-[var(--brand-primary-fg)]" : "text-slate-400 group-hover:text-slate-600"}`} />
                      <span className="font-medium">{label}</span>
                    </span>
                    <ChevronRight className={`h-4 w-4 ${isActive ? "text-[var(--brand-primary-fg)] opacity-70" : "text-slate-300 group-hover:text-slate-400"}`} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="m-4 rounded-2xl border border-slate-200/80 bg-slate-50/70 p-4">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-white ring-1 ring-slate-200">
              <span className="text-sm font-medium text-slate-700">
                {user.name.split(" ").map(p => p[0]).slice(0, 2).join("")}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-900">{user.name}</p>
              <p className="truncate text-xs text-slate-500">{user.email}</p>
            </div>
          </div>
          {/* Day 5 Block G: role chip pasa a ser informativo. Solo muestra el
              affordance "cambiar" si el flag stratos.debug.roleToggle está activo. */}
          {(() => {
            const toggle = isRoleToggleEnabled();
            const Component = toggle ? "button" : "div";
            return (
              <Component
                {...(toggle ? { onClick: onToggleRole } : {})}
                className={`mt-3 inline-flex w-full items-center justify-between rounded-lg bg-white px-3 py-2 text-xs font-medium text-slate-700 ring-1 ring-slate-200 ${toggle ? "transition hover:bg-slate-50 cursor-pointer" : ""}`}
              >
                <span className="flex items-center gap-1.5">
                  {user.role === "SUPER_USER"
                    ? <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                    : <Shield className="h-3.5 w-3.5 text-slate-400" />}
                  {user.role === "SUPER_USER" ? "Administrador" : "Usuario común"}
                </span>
                {toggle && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider">
                    <span className="rounded bg-rose-100 px-1 text-rose-700">DEV</span>
                    <span className="text-slate-400">cambiar</span>
                  </span>
                )}
              </Component>
            );
          })()}
          <button onClick={doLogout}
            className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-white px-3 py-2 text-xs font-medium text-rose-700 ring-1 ring-rose-200 transition hover:bg-rose-50">
            <X className="h-3.5 w-3.5" /> Cerrar sesión
          </button>

          {/* Enlaces legales (MVP estático, páginas públicas /legal/*). */}
          <nav className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-slate-400">
            <a href="/legal/aviso-de-privacidad" className="hover:text-slate-600 hover:underline">Privacidad</a>
            <span aria-hidden>·</span>
            <a href="/legal/terminos-y-condiciones" className="hover:text-slate-600 hover:underline">Términos</a>
            <span aria-hidden>·</span>
            <a href="/legal/tarifas" className="hover:text-slate-600 hover:underline">Tarifario</a>
            <span aria-hidden>·</span>
            <a href="/legal/aviso-simplificado" className="hover:text-slate-600 hover:underline">Aviso simplificado</a>
          </nav>
        </div>
      </aside>
    </>
  );
}

/* ===========================================================================
   Topbar
   =========================================================================== */
function Topbar({ pageTitle, breadcrumb, onMenu, user, onHelp }) {
  return (
    <header className="sticky top-0 z-20 border-b border-slate-200/70 bg-white/80 backdrop-blur">
      <div className="flex items-center justify-between gap-3 px-4 py-4 md:gap-4 md:px-10">
        {/* min-w-0 + truncate: el título puede ser largo ("Pendientes de entrega");
            sin esto no encogía y empujaba el ancho del layout más allá del viewport. */}
        <div className="flex min-w-0 items-center gap-2 md:gap-3">
          <button className="rounded-md p-2 text-slate-500 hover:bg-slate-100 md:hidden" onClick={onMenu}>
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            {/* Bloque 4 — en móvil el breadcrumb del módulo se oculta y manda el título
                de la PANTALLA (lo útil); ambos se ven desde sm:. */}
            <p className="hidden truncate text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400 sm:block">{breadcrumb}</p>
            <h1 className="truncate font-serif text-2xl tracking-tight text-slate-900 md:text-[28px]">{pageTitle}</h1>
          </div>
        </div>
        {/* min-w-0 (no shrink-0): en móvil el grupo debe poder encoger; el selector
            de sucursal es el que cede (abajo). gap más chico en móvil. Los botones de
            ícono son cuadrados p-2 en móvil (mismo tamaño que la campana) para que no
            queden "volados" ni con padding de más. */}
        <div className="flex min-w-0 items-center gap-1.5 md:gap-3">
          {/* "Buscar…" del Topbar removido: era un input decorativo (sin value/onChange,
              no cableado a ninguna búsqueda). La búsqueda real vive por vista
              (Contactos, Cotizaciones, Miembros, catálogos). */}
          {/* Tour guiado — re-lanza el recorrido cuando el usuario quiera (ignora el flag). */}
          <button onClick={onHelp} title="¿Cómo funciona?"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 bg-white p-2 text-xs font-medium text-slate-600 hover:bg-slate-50 sm:px-3 sm:py-2">
            <LifeBuoy className="h-4 w-4" />
            <span className="hidden sm:inline">¿Cómo funciona?</span>
          </button>
          {/* V76 — selector de sucursal activa (solo si el tenant tiene >1). */}
          <BranchSelector />
          {/* Comentario/sugerencia — para cualquier usuario autenticado.
              ("Registrar horas" se quitó del Topbar — sigue en Proyectos/ProjectHub.) */}
          <span data-tour="feedback"><FeedbackButton /></span>
          {/* PR4 — campana real: bandeja in-app de avisos (cancelación/reagenda +
              leyenda fiscal). Solo para quien gestiona la agenda (bookings:manage). */}
          <NotificationsBell enabled={hasPermission(user, 'bookings:manage')} />
          <div className="hidden items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 md:flex">
            {user.role === "SUPER_USER"
              ? <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              : <Shield className="h-3.5 w-3.5 text-slate-400" />}
            <span className="font-medium">{user.role === "SUPER_USER" ? "Admin" : "Común"}</span>
          </div>
        </div>
      </div>
    </header>
  );
}

/* ===========================================================================
   ProjectsView (cartera)
   =========================================================================== */
function ProjectsView({ projects, onOpenProject, onNewProject, isAdmin, displayCurrency = "USD", fxRates = [] }) {
  const stats = useMemo(() => {
    const active = projects.filter(p => p.status === "ACTIVE").length;
    const avg = projects.length
      ? Math.round(projects.reduce((a, p) => a + (p.progress ?? 0), 0) / projects.length)
      : 0;
    const total = projects.reduce((a, p) => a + (p.budget ?? 0), 0);
    return { active, avg, total, count: projects.length };
  }, [projects]);
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Proyectos activos" value={stats.active} hint={`de ${stats.count} en cartera`} />
        <StatTile label="Avance promedio" value={projects.length ? `${stats.avg}%` : "—"} hint="todos los estados" />
        <StatTile label="Presupuesto total" value={fmtUsdInDisplay(stats.total, displayCurrency, fxRates)} hint={`${displayCurrency} comprometido`} />
        <StatTile label="Próximas entregas" value="—" hint="en los próximos 30 días" />
      </div>

      <section className="card-soft rounded-2xl border border-slate-200/80 bg-white">
        <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-6 py-5">
          <div>
            <h2 className="font-serif text-lg tracking-tight text-slate-900">Cartera de proyectos</h2>
            <p className="text-sm text-slate-500">Click en un proyecto para abrir su Hub</p>
          </div>
          {/* v1.1.2: cualquier rol puede crear; el modal indica si quedará pendiente */}
          <button onClick={onNewProject}
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--brand-primary)] px-4 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
            <Plus className="h-4 w-4" /> Nuevo proyecto
          </button>
        </div>
        <ul className="divide-y divide-slate-100">
          {projects.map(p => {
            const tone = budgetTone(p.budgetedHours ? (p.hoursLogged / p.budgetedHours) * 100 : null);
            return (
              <li key={p.id}>
                <button onClick={() => onOpenProject(p.id)}
                        className="grid w-full grid-cols-1 gap-4 px-6 py-5 text-left transition hover:bg-slate-50/60 lg:grid-cols-12 lg:items-center">
                  <div className="lg:col-span-5">
                    <div className="flex items-center gap-3">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-900/95 text-white">
                        <span className="font-serif text-sm italic">
                          {p.client.split(" ").map(w => w[0]).slice(0, 2).join("")}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="truncate text-[15px] font-medium text-slate-900">{p.name}</p>
                          <span className="hidden font-mono text-[11px] uppercase tracking-wider text-slate-400 md:inline">{p.code}</span>
                        </div>
                        <p className="mt-0.5 text-sm text-slate-500">{p.client} · Owner {p.owner}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 lg:col-span-3">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset ${STATUS_STYLES[p.status]}`}>
                      {STATUS_LABEL[p.status]}
                    </span>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${tone.chipCls}`}>
                      {(tone.name === "OVER" || tone.name === "CRIT") && <AlertTriangle className="h-3 w-3" />}
                      {tone.label}
                    </span>
                  </div>
                  <div className="lg:col-span-3"><Progress value={p.progress} /></div>
                  <div className="flex items-center justify-end text-slate-400 lg:col-span-1">
                    <ChevronRight className="h-4 w-4" />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

/* ===========================================================================
   ProjectHub — vista individual con BudgetBar + Kanban DnD
   =========================================================================== */
function ProjectHub({ projectId, projects, tasks, setTasks, onBack, onLogHours, onNewTask, users, isAdmin, displayCurrency = "USD", fxRates = [], user }) {
  const project = projects.find(p => p.id === projectId);
  const [editOpen, setEditOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = async () => {
    if (!projectId) return;
    setHistoryLoading(true);
    try {
      const h = await api(`/projects/${projectId}/history`);
      setHistory(Array.isArray(h) ? h : []);
    } catch { setHistory([]); }
    finally { setHistoryLoading(false); }
  };
  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [projectId]);

  if (!project) return null;
  const canEdit = hasPermission(user, "projects:update");
  const projectTasks = tasks.filter(t => t.projectId === projectId);
  const grouped = useMemo(() => {
    const g = { TODO: [], IN_PROGRESS: [], REVIEW: [], DONE: [] };
    projectTasks.forEach(t => g[t.status].push(t));
    return g;
  }, [projectTasks]);

  const completionPct = projectTasks.length
    ? Math.round((grouped.DONE.length / projectTasks.length) * 100)
    : 0;

  const [dragId, setDragId] = useState(null);
  const [dropCol, setDropCol] = useState(null);

  const onDragStart = (taskId) => (e) => {
    setDragId(taskId);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", taskId); } catch (_) {}
  };
  const onDragOver = (colId) => (e) => { e.preventDefault(); setDropCol(colId); };
  // 2.2: persistir el move del Kanban (antes setState puro → se perdía al
  // refrescar). Update optimista + PATCH /tasks/:id con rollback si falla.
  const onDrop = (colId) => async (e) => {
    e.preventDefault();
    const id = dragId || e.dataTransfer.getData("text/plain");
    setDragId(null); setDropCol(null);
    if (!id) return;
    const prev = projectTasks.find(t => t.id === id);
    if (!prev || prev.status === colId) return;
    setTasks(ts => ts.map(t => t.id === id ? { ...t, status: colId } : t));
    // Tarea recién creada cuyo POST aún no resuelve (id temporal): no hay UUID
    // que persistir; el create ya guardará su estado inicial.
    if (String(id).startsWith("tmp-")) return;
    try {
      await api(`/tasks/${id}`, { method: "PATCH", body: { status: colId } });
    } catch {
      setTasks(ts => ts.map(t => t.id === id ? { ...t, status: prev.status } : t));
    }
  };

  return (
    <div className="space-y-8">
      {/* Hero */}
      <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-6 md:p-8">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div>
            <button onClick={onBack} className="mb-4 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900">
              <ArrowLeft className="h-3.5 w-3.5" /> Volver a proyectos
            </button>
            <p className="font-mono text-[11px] uppercase tracking-wider text-slate-400">
              {project.code} · {project.client}
            </p>
            <h2 className="mt-1 font-serif text-3xl tracking-tight text-slate-900 md:text-4xl">{project.name}</h2>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-500">
              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset ${STATUS_STYLES[project.status]}`}>
                {STATUS_LABEL[project.status]}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-slate-400" />
                {fmtDate(project.startDate)} → {fmtDate(project.dueDate)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <User className="h-3.5 w-3.5 text-slate-400" /> Owner {project.owner}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            {canEdit && (
              <button onClick={() => setEditOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                <Edit2 className="h-4 w-4" /> Editar
              </button>
            )}
            <button onClick={onLogHours} className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--brand-primary)] px-4 py-2.5 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
              <Clock className="h-4 w-4" /> Registrar horas
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="md:col-span-2">
            <BudgetBar logged={project.hoursLogged} budgeted={project.budgetedHours} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200/80 p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Avance</p>
              <p className="mt-1 font-serif text-2xl text-slate-900">{completionPct}%</p>
              <p className="text-xs text-slate-500">{grouped.DONE.length}/{projectTasks.length} tareas</p>
            </div>
            <div className="rounded-xl border border-slate-200/80 p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Presupuesto</p>
              <p className="mt-1 font-serif text-2xl text-slate-900">{fmtUsdInDisplay(project.budget, displayCurrency, fxRates)}</p>
              <p className="text-xs text-slate-500">{displayCurrency} comprometido</p>
            </div>
          </div>
        </div>

        {/* 2.5: Costo real = horas registradas del proyecto × su tarifa horaria
            (project.hourlyRate). Antes sumaba tasks × tarifa por usuario, campo
            inexistente en el backend → costo siempre 0. */}
        {(() => {
          const cost = (project.hoursLogged ?? 0) * (project.hourlyRate ?? 0);
          const margin = (project.budget ?? 0) - cost;
          const marginPct = project.budget ? (margin / project.budget) * 100 : 0;
          const marginTone = marginPct >= 30 ? "alert-ok" : marginPct >= 10 ? "alert-warn" : "alert-over";
          return (
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="rounded-xl border border-slate-200/80 p-4">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Costo real (horas × tarifa)</p>
                <p className="mt-1 font-serif text-xl text-slate-900 tabular-nums">{fmtUsdInDisplay(cost, displayCurrency, fxRates)}</p>
              </div>
              <div className="rounded-xl border border-slate-200/80 p-4">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Margen estimado</p>
                <p className="mt-1 font-serif text-xl text-slate-900 tabular-nums">{fmtUsdInDisplay(margin, displayCurrency, fxRates)}</p>
              </div>
              <div className={`rounded-xl px-4 py-3 ${marginTone}`}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-80">Margen %</p>
                <p className="mt-1 font-serif text-xl tabular-nums">{marginPct.toFixed(1)}%</p>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Kanban */}
      <section>
        <div className="mb-4 flex items-baseline justify-between">
          <div>
            <h3 className="font-serif text-xl tracking-tight text-slate-900">Tablero de actividades</h3>
            <p className="text-sm text-slate-500">Arrastra una tarjeta para cambiar su estado</p>
          </div>
          <button onClick={onNewTask}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
            <Plus className="h-3.5 w-3.5" /> Nueva actividad
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {KANBAN_COLS.map(col => {
            const items = grouped[col.id];
            return (
              <div key={col.id}
                   className="kanban-col rounded-2xl border border-slate-200/80 bg-slate-50/60 p-3 transition"
                   data-drop-active={dropCol === col.id ? "true" : "false"}
                   onDragOver={onDragOver(col.id)}
                   onDragLeave={() => setDropCol(null)}
                   onDrop={onDrop(col.id)}>
                <div className="flex items-center justify-between px-2 pb-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{col.title}</p>
                  <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 ring-1 ring-inset ring-slate-200">{items.length}</span>
                </div>
                <ul className="space-y-2.5">
                  {items.map(t => {
                    const over = t.hoursLogged > t.hoursEst;
                    const isDragging = dragId === t.id;
                    return (
                      <li key={t.id}
                          draggable
                          onDragStart={onDragStart(t.id)}
                          onDragEnd={() => { setDragId(null); setDropCol(null); }}
                          className={`kanban-card card-soft card-soft-hover cursor-grab active:cursor-grabbing rounded-xl border border-slate-200/80 bg-white p-3.5 transition ${isDragging ? "dragging" : ""}`}>
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-[13.5px] font-medium leading-snug text-slate-900">{t.title}</p>
                          <span className="text-slate-300"><GripVertical className="h-3.5 w-3.5" /></span>
                        </div>
                        <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="grid h-5 w-5 place-items-center rounded-full bg-slate-100 text-[10px] font-medium text-slate-600">
                              {t.assignee.split(" ").map(p => p[0]).join("")}
                            </span>
                            {t.assignee}
                          </span>
                          <span className={`inline-flex items-center gap-1 tabular-nums ${over ? "text-rose-700" : "text-slate-500"}`}>
                            <Clock className="h-3 w-3" />
                            {fmtHours(t.hoursLogged)}/{fmtHours(t.hoursEst)}h
                          </span>
                        </div>
                        {over && (
                          <p className="mt-2 inline-flex items-center gap-1 rounded-full alert-over px-2 py-0.5 text-[10px] font-medium">
                            <AlertTriangle className="h-3 w-3" />
                            +{fmtHours(t.hoursLogged - t.hoursEst)}h sobre estimación
                          </p>
                        )}
                      </li>
                    );
                  })}
                  {items.length === 0 && (
                    <li className="rounded-xl border border-dashed border-slate-200 px-3 py-6 text-center text-[11px] text-slate-400">
                      Soltar tarjeta aquí
                    </li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Day 6 Block B — Historial de cambios */}
      <section className="card-soft rounded-2xl border border-slate-200/80 bg-white">
        <div className="border-b border-slate-100 px-6 py-5">
          <h3 className="font-serif text-lg tracking-tight text-slate-900">Historial de cambios</h3>
          <p className="text-sm text-slate-500">Eventos de auditoría capturados por trigger de BD</p>
        </div>
        {historyLoading ? (
          <p className="px-6 py-6 text-sm text-slate-400">Cargando…</p>
        ) : history.length === 0 ? (
          <p className="px-6 py-6 text-sm text-slate-400">Sin cambios registrados.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {history.map(h => (
              <li key={h.id} className="px-6 py-3 text-sm">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-slate-900">
                    <strong>{h.changedByName ?? "—"}</strong> cambió{" "}
                    <code className="rounded bg-slate-100 px-1 font-mono text-[11px] text-slate-700">{h.fieldName}</code>
                    {" "}de{" "}
                    <span className="text-slate-500">{h.oldValue ?? "∅"}</span>
                    {" "}a{" "}
                    <span className="font-medium text-slate-900">{h.newValue ?? "∅"}</span>
                  </span>
                  <span className="text-[11px] text-slate-400 tabular-nums">{fmtDate(h.changedAt)}</span>
                </div>
                {h.reason && (
                  <p className="mt-1 text-[12px] text-slate-500">Motivo: {h.reason}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* §22 Sprint A — Adjuntos del proyecto */}
      <section className="card-soft">
        <AttachmentsPanel owner="project" ownerId={projectId} canEdit={hasPermission(user, "projects:update")} />
      </section>

      {editOpen && (
        <EditProjectModal
          project={project}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); loadHistory(); /* refresh history */ }}
        />
      )}
    </div>
  );
}

function EditProjectModal({ project, onClose, onSaved }) {
  const [name, setName] = useState(project.name ?? "");
  const [status, setStatus] = useState(project.status ?? "PLANNING");
  const [budgetHours, setBudgetHours] = useState(project.budgetedHours ?? "");
  // Day 7 Block B.3: budget se edita SIEMPRE en su moneda nativa (project.budgetCurrency).
  // Si cambias la moneda, el input se mantiene como número y la BD lo guarda en
  // la nueva moneda — admin debe re-introducir el monto si conceptualmente cambia.
  // Mostramos el código ISO al lado del input para que sea obvio en qué moneda
  // se está capturando.
  const [budgetAmount, setBudgetAmount] = useState(project.budget ?? "");
  const [budgetCurrency, setBudgetCurrency] = useState(project.budgetCurrency ?? "USD");
  const [endDate, setEndDate] = useState(project.dueDate ? String(project.dueDate).slice(0, 10) : "");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      const body = {
        name: name !== project.name ? name : undefined,
        status: status !== project.status ? status : undefined,
        budgetHours: budgetHours !== "" && Number(budgetHours) !== Number(project.budgetedHours)
          ? String(budgetHours) : undefined,
        budgetAmount: budgetAmount !== "" && Number(budgetAmount) !== Number(project.budget)
          ? String(budgetAmount) : undefined,
        budgetCurrency: budgetCurrency !== project.budgetCurrency ? budgetCurrency : undefined,
        endDate: endDate || undefined,
        reason: reason.trim() || undefined,
      };
      // Solo enviar campos modificados
      Object.keys(body).forEach(k => body[k] === undefined && delete body[k]);
      await api(`/projects/${project.id}`, { method: "PATCH", body });
      onSaved?.();
    } catch (e2) {
      const msg = Array.isArray(e2?.body?.message) ? e2.body.message.join(" · ") : (e2?.body?.message || e2?.message);
      setErr(msg || "No se pudo guardar");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <form onSubmit={submit} className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <h3 className="font-serif text-lg tracking-tight text-slate-900">Editar proyecto</h3>
        <p className="text-xs text-slate-500">{project.code ?? project.name}</p>

        <div className="mt-4 space-y-3">
          <Field label="Nombre">
            <input className="input" value={name} onChange={e => setName(e.target.value)} required />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Estado">
              <select className="input" value={status} onChange={e => setStatus(e.target.value)}>
                {Object.entries(STATUS_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </Field>
            <Field label="Moneda">
              <select className="input" value={budgetCurrency} onChange={e => setBudgetCurrency(e.target.value)}>
                <option value="USD">USD</option>
                <option value="MXN">MXN</option>
                <option value="EUR">EUR</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Horas presupuestadas">
              <input type="number" min="0" step="1" className="input" value={budgetHours} onChange={e => setBudgetHours(e.target.value)} />
            </Field>
            <Field label={`Monto del presupuesto (${budgetCurrency})`}>
              <div className="flex items-center gap-2">
                <input type="number" min="0" step="100" className="input flex-1" value={budgetAmount} onChange={e => setBudgetAmount(e.target.value)} />
                <span className="rounded-md bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600 tabular-nums">{budgetCurrency}</span>
              </div>
            </Field>
          </div>
          <Field label="Fecha de entrega">
            <input type="date" className="input" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </Field>
          <Field label="Motivo del cambio (opcional, queda en el audit log)">
            <textarea className="input min-h-[60px]" value={reason} onChange={e => setReason(e.target.value)}
              placeholder="Ej. Aumento de scope acordado en la junta de revisión" maxLength={500} />
          </Field>
        </div>

        {err && <div className="mt-3 alert-over rounded-xl px-3 py-2 text-xs">{err}</div>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200">Cancelar</button>
          <button type="submit" disabled={submitting} className="rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
            {submitting ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ===========================================================================
   ClientsView — dashboard por cliente
   =========================================================================== */
function ClientsView({ projects, clients, isAdmin, onNewClient, onEditClient, onToggleActiveClient, displayCurrency = "USD", fxRates = [] }) {
  const rows = useMemo(() => {
    const byClient = new Map();
    projects.forEach(p => {
      const c = byClient.get(p.clientId) ?? {
        id: p.clientId, name: p.client, projects: 0, budgetedHours: 0,
        hoursLogged: 0, activeProjects: 0, totalBudget: 0,
      };
      c.projects += 1;
      c.budgetedHours += p.budgetedHours || 0;
      c.hoursLogged += p.hoursLogged || 0;
      c.totalBudget += p.budget || 0;
      if (p.status === "ACTIVE") c.activeProjects += 1;
      byClient.set(p.clientId, c);
    });
    return Array.from(byClient.values()).map(c => {
      const pct = c.budgetedHours ? (c.hoursLogged / c.budgetedHours) * 100 : null;
      return { ...c, consumptionPct: pct, tone: budgetTone(pct) };
    }).sort((a, b) => (b.consumptionPct ?? 0) - (a.consumptionPct ?? 0));
  }, [projects]);

  const totals = rows.reduce((acc, r) => ({
    clients: acc.clients + 1,
    projects: acc.projects + r.projects,
    budgeted: acc.budgeted + r.budgetedHours,
    logged: acc.logged + r.hoursLogged,
    over: acc.over + (r.tone.name === "OVER" ? 1 : 0),
  }), { clients: 0, projects: 0, budgeted: 0, logged: 0, over: 0 });

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile label="Clientes activos" value={totals.clients} hint="con proyectos en curso" />
        <StatTile label="Proyectos en cartera" value={totals.projects} />
        <StatTile label="Horas presupuestadas" value={`${fmtHours(totals.budgeted)}h`} hint={`registradas: ${fmtHours(totals.logged)}h`} />
        <StatTile label="Cuentas con sobrecosto" value={totals.over}
                  hint={totals.over ? "requieren revisión" : "todo bajo control"} />
      </div>

      <section className="card-soft overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
        <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-6 py-5">
          <div>
            <h2 className="font-serif text-lg tracking-tight text-slate-900">Cartera por cliente</h2>
            <p className="text-sm text-slate-500">Estado financiero de horas, ordenado por consumo</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onNewClient}
              className="inline-flex items-center gap-2 rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
              <Plus className="h-3.5 w-3.5" /> Nuevo cliente
            </button>
            <button className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
              <TrendingUp className="h-3.5 w-3.5" /> Exportar
            </button>
          </div>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block">
          <div className="grid grid-cols-12 gap-4 border-b border-slate-100 bg-slate-50/60 px-6 py-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            <div className="col-span-3">Cliente</div>
            <div className="col-span-2">Proyectos</div>
            <div className="col-span-2 text-right">Horas presup.</div>
            <div className="col-span-2 text-right">Horas reales</div>
            <div className="col-span-2">Estado</div>
            <div className="col-span-1 text-right">{isAdmin ? "Acciones" : ""}</div>
          </div>
          <ul className="divide-y divide-slate-100">
            {rows.map(r => {
              const clientObj = clients?.find(c => c.id === r.id) ?? r;
              return (
                <li key={r.id} className="grid grid-cols-12 items-center gap-4 px-6 py-5">
                  <div className="col-span-3">
                    <div className="flex items-center gap-3">
                      <div className="grid h-9 w-9 place-items-center rounded-xl bg-slate-900/95 text-white">
                        <span className="font-serif text-sm italic">
                          {r.name.split(" ").map(w => w[0]).slice(0, 2).join("")}
                        </span>
                      </div>
                      <div>
                        <p className="text-[14px] font-medium text-slate-900">
                          {r.name}
                          {clientObj?.isActive === false && (
                            <span className="ml-2 inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">inactivo</span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500">{fmtUsdInDisplay(r.totalBudget, displayCurrency, fxRates)} comprometidos</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-slate-900 tabular-nums">{r.projects}</p>
                    <p className="text-xs text-slate-500">{r.activeProjects} activos</p>
                  </div>
                  <div className="col-span-2 text-right tabular-nums text-sm text-slate-700">{fmtHours(r.budgetedHours)}h</div>
                  <div className="col-span-2 text-right tabular-nums text-sm">
                    <span className={r.tone.name === "OVER" ? "text-rose-700 font-medium" : "text-slate-700"}>
                      {fmtHours(r.hoursLogged)}h
                    </span>
                  </div>
                  <div className="col-span-2">
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${r.tone.chipCls}`}>
                          {(r.tone.name === "OVER" || r.tone.name === "CRIT") && <AlertTriangle className="h-3 w-3" />}
                          {r.tone.label}
                        </span>
                        {r.consumptionPct != null && (
                          <span className={`text-[11px] tabular-nums ${r.tone.name === "OVER" ? "text-rose-700 font-medium" : "text-slate-500"}`}>
                            {r.consumptionPct.toFixed(0)}%
                          </span>
                        )}
                      </div>
                      <div className="h-1 w-full overflow-hidden rounded-full bg-slate-100">
                        <div className={`h-full rounded-full ${r.tone.barCls}`}
                             style={{ width: `${Math.min(100, r.consumptionPct ?? 0)}%` }} />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-1 flex items-center justify-end gap-1">
                    {isAdmin && (
                      <>
                        <button onClick={() => onEditClient(clientObj)}
                          title="Editar"
                          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                        <button onClick={() => onToggleActiveClient(clientObj)}
                          title={clientObj?.isActive === false ? "Reactivar" : "Desactivar"}
                          className={`rounded-lg p-1.5 hover:bg-slate-100 ${clientObj?.isActive === false ? "text-emerald-700" : "text-rose-700"}`}>
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Mobile cards */}
        <ul className="divide-y divide-slate-100 md:hidden">
          {rows.map(r => (
            <li key={r.id} className="space-y-3 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="grid h-9 w-9 place-items-center rounded-xl bg-slate-900/95 text-white">
                    <span className="font-serif text-sm italic">
                      {r.name.split(" ").map(w => w[0]).slice(0, 2).join("")}
                    </span>
                  </div>
                  <div>
                    <p className="text-[14px] font-medium text-slate-900">{r.name}</p>
                    <p className="text-xs text-slate-500">{r.projects} proyectos · {r.activeProjects} activos</p>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${r.tone.chipCls}`}>
                  {r.tone.label}
                </span>
              </div>
              <BudgetBar logged={r.hoursLogged} budgeted={r.budgetedHours} compact />
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

/* ===========================================================================
   LogHoursModal — registro rápido
   =========================================================================== */
function LogHoursModal({ open, onClose, projects, tasks, tags, onSubmit, defaultProjectId }) {
  const tagList = (tags ?? []).filter(t => t.isActive !== false);
  const [form, setForm] = useState({
    projectId: defaultProjectId ?? projects[0]?.id ?? "",
    taskId: "",
    activityTagId: tagList[0]?.id ?? "",
    hoursSpent: 1,
    logDate: new Date().toISOString().slice(0, 10),
    description: "",
    billable: true,
  });

  useEffect(() => {
    if (open) {
      setForm(f => ({ ...f, projectId: defaultProjectId ?? projects[0]?.id ?? "", taskId: "" }));
    }
  }, [open, defaultProjectId, projects]);

  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const projectTasks = tasks.filter(t => t.projectId === form.projectId);
  const project = projects.find(p => p.id === form.projectId);
  const projectedTotal = (project?.hoursLogged ?? 0) + Number(form.hoursSpent || 0);
  const projectedPct = project?.budgetedHours ? (projectedTotal / project.budgetedHours) * 100 : null;
  const tone = budgetTone(projectedPct);
  const willOverflow = projectedPct != null && projectedPct >= 90;

  const onChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = e => {
    e.preventDefault();
    onSubmit({ ...form, hoursSpent: Number(form.hoursSpent) });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-t-3xl border border-slate-200/80 bg-white shadow-xl md:rounded-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 pt-6 pb-4 md:px-8">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
              Hoy · {fmtDate(form.logDate)}
            </p>
            <h3 className="mt-1 font-serif text-2xl tracking-tight text-slate-900">Registrar horas</h3>
            <p className="text-sm text-slate-500">¿Qué hiciste y cuánto te tomó?</p>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-5 px-6 py-6 md:px-8">
          <Field label="Proyecto">
            <select name="projectId" value={form.projectId} onChange={onChange} className="input">
              {projects.map(p => <option key={p.id} value={p.id}>{p.name} — {p.client}</option>)}
            </select>
          </Field>
          <Field label="Tarea (opcional)">
            <select name="taskId" value={form.taskId} onChange={onChange} className="input">
              <option value="">— Sin tarea específica —</option>
              {projectTasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
          </Field>

          {tagList.length > 0 && (
            <Field label="Tipo de actividad">
              <div className="flex flex-wrap gap-2">
                {tagList.map(t => {
                  const checked = form.activityTagId === t.id;
                  return (
                    <label key={t.id}
                      className="cursor-pointer select-none rounded-full px-3 py-1.5 text-xs font-medium ring-1 ring-inset transition"
                      style={checked
                        ? { background: t.color, color: "#fff", borderColor: t.color }
                        : { background: "white", color: "#475569" }}>
                      <input type="radio" name="activityTagId" value={t.id} checked={checked} onChange={onChange} className="sr-only" />
                      <span className="inline-flex items-center gap-1.5">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: checked ? "#fff" : t.color }} />
                        {t.name}
                      </span>
                    </label>
                  );
                })}
              </div>
            </Field>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Field label="Horas">
              <input type="number" name="hoursSpent" min="0.25" max="24" step="0.25"
                     value={form.hoursSpent} onChange={onChange} className="input tabular-nums" />
            </Field>
            <Field label="Fecha">
              <input type="date" name="logDate" value={form.logDate} onChange={onChange} className="input" />
            </Field>
          </div>

          <Field label="Qué hiciste">
            <textarea name="description" value={form.description} onChange={onChange} rows={3}
                      placeholder="Ej. Maquetación de la sección de pricing y revisión con marketing"
                      className="input resize-none" />
          </Field>

          <label className="inline-flex cursor-pointer items-center gap-3 text-sm text-slate-600">
            <span className="relative inline-flex h-5 w-9 items-center">
              <input type="checkbox" name="billable" checked={form.billable} onChange={onChange} className="peer sr-only" />
              <span className="absolute inset-0 rounded-full bg-slate-200 transition peer-checked:bg-slate-900" />
              <span className="absolute left-0.5 h-4 w-4 rounded-full bg-white shadow transition peer-checked:translate-x-4" />
            </span>
            Facturable al cliente
          </label>

          {project && project.budgetedHours > 0 && (
            <div className={`rounded-xl px-4 py-3 text-xs ${tone.chipCls}`}>
              <p className="font-medium">
                {willOverflow ? "Atención: este registro deja el proyecto en zona crítica." : "Impacto en el presupuesto"}
              </p>
              <p className="mt-1 tabular-nums opacity-90">
                {fmtHours(project.hoursLogged)}h registradas + {Number(form.hoursSpent || 0).toFixed(2)}h
                {" = "}<strong>{fmtHours(projectedTotal)}h</strong> de {fmtHours(project.budgetedHours)}h
                {projectedPct != null && <> · {projectedPct.toFixed(1)}%</>}
              </p>
            </div>
          )}

          <div className="flex flex-col-reverse items-stretch gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-end">
            <button type="button" onClick={onClose}
              className="rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
              Cancelar
            </button>
            <button type="submit"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--brand-primary)] px-5 py-2.5 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
              <Plus className="h-4 w-4" /> Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ===========================================================================
   CRMView (existente)
   =========================================================================== */
function CRMView({ user, contacts, isAdmin, onNewContact, onEditContact }) {
  // Day 4 hotfix BUG#1: NO fallback a CONTACTS mock. Si el parent no pasa
  // contactos (o pasa []), la vista muestra empty states — nunca filtra
  // los nombres demo (Helena Vargas, Robert Pawlak, Sasha Moreau).
  const list = Array.isArray(contacts) ? contacts : [];
  const [form, setForm] = useState({
    contactId: list[0]?.id ?? "", type: "call", subject: "",
    occurredAt: new Date().toISOString().slice(0, 16),
    durationMin: 30, followUp: false, notes: "",
  });
  // Si la lista de contactos cambia (creación/eliminación), mantén la selección válida
  useEffect(() => {
    if (!list.find(c => c.id === form.contactId) && list.length) {
      setForm(f => ({ ...f, contactId: list[0].id }));
    }
  }, [list]);
  // Day 4 hotfix BUG#1: arranca vacío. Endpoint real /activities llega Day 7+.
  const [recent, setRecent] = useState([]);
  const onChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };
  const onSubmit = e => {
    e.preventDefault();
    const c = list.find(x => x.id === form.contactId);
    setRecent(r => [
      { id: `a-${Date.now()}`, type: form.type, subject: form.subject || "Sin asunto", contact: c?.name ?? "—", when: "Recién ahora" },
      ...r,
    ]);
    setForm(f => ({ ...f, subject: "", notes: "" }));
  };
  const selectedContact = list.find(x => x.id === form.contactId);
  const TypeIcon = ({ t }) =>
    t === "call" ? <Phone className="h-3.5 w-3.5" /> :
    t === "email" ? <Mail className="h-3.5 w-3.5" /> :
    t === "meeting" ? <Users className="h-3.5 w-3.5" /> :
    t === "visit" ? <CheckCircle2 className="h-3.5 w-3.5" /> :
    <Circle className="h-3.5 w-3.5" />;

  return (
    <div className="grid grid-cols-1 gap-8 xl:grid-cols-12">
      <section className="xl:col-span-7">
        <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
          <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5 md:px-8">
            <div>
              <h2 className="font-serif text-lg tracking-tight text-slate-900">Registrar actividad de cliente interno</h2>
              <p className="text-sm text-slate-500">Toda actividad queda en el log de auditoría del tenant</p>
            </div>
            {isAdmin && (
              <button type="button" onClick={onNewContact}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
                <Plus className="h-3.5 w-3.5" /> Nuevo contacto
              </button>
            )}
          </div>
          <form onSubmit={onSubmit} className="space-y-6 px-6 py-6 md:px-8 md:py-8">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <Field label="Contacto">
                <div className="flex gap-2">
                  <select name="contactId" value={form.contactId} onChange={onChange} className="input">
                    {list.map(c => <option key={c.id} value={c.id}>{c.name} — {c.company}</option>)}
                  </select>
                  {isAdmin && selectedContact && (
                    <button type="button" onClick={() => onEditContact(selectedContact)}
                      title="Editar contacto"
                      className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 hover:bg-slate-50">
                      Editar
                    </button>
                  )}
                </div>
              </Field>
              <Field label="Tipo">
                <div className="flex flex-wrap gap-2">
                  {ACTIVITY_TYPES.map(t => {
                    const checked = form.type === t.id;
                    return (
                      <label key={t.id} className={`cursor-pointer select-none rounded-full px-3.5 py-1.5 text-xs font-medium ring-1 ring-inset transition ${checked ? "bg-slate-900 text-white ring-slate-900" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>
                        <input type="radio" name="type" value={t.id} checked={checked} onChange={onChange} className="sr-only" />
                        {t.label}
                      </label>
                    );
                  })}
                </div>
              </Field>
            </div>
            <Field label="Asunto">
              <input type="text" name="subject" value={form.subject} onChange={onChange} placeholder="Ej. Revisión de propuesta Q3" className="input" />
            </Field>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <Field label="Fecha y hora">
                <input type="datetime-local" name="occurredAt" value={form.occurredAt} onChange={onChange} className="input" />
              </Field>
              <Field label="Duración (min)">
                <input type="number" min="1" step="5" name="durationMin" value={form.durationMin} onChange={onChange} className="input" />
              </Field>
            </div>
            <Field label="Notas">
              <textarea name="notes" value={form.notes} onChange={onChange} rows={3} placeholder="Resumen, próximos pasos…" className="input resize-none" />
            </Field>
            <div className="flex justify-end gap-3 border-t border-slate-100 pt-5">
              <button type="button" className="rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">Cancelar</button>
              <button type="submit" className="inline-flex items-center gap-2 rounded-xl bg-[var(--brand-primary)] px-5 py-2.5 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
                <Plus className="h-4 w-4" /> Registrar
              </button>
            </div>
          </form>
        </div>
      </section>

      <aside className="space-y-6 xl:col-span-5">
        <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
          <div className="border-b border-slate-100 px-6 py-5">
            <h3 className="font-serif text-lg tracking-tight text-slate-900">Actividad reciente</h3>
          </div>
          {recent.length === 0 ? (
            <p className="px-6 py-8 text-center text-sm text-slate-400">
              Aún no hay actividad reciente.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {recent.map(a => (
                <li key={a.id} className="flex items-start gap-4 px-6 py-4">
                  <div className="mt-0.5 grid h-8 w-8 place-items-center rounded-full bg-slate-100 text-slate-600">
                    <TypeIcon t={a.type} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900">{a.subject}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-500">
                      {a.contact} · <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{a.when}</span>
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-6">
          <h3 className="font-serif text-lg tracking-tight text-slate-900">Contacto seleccionado</h3>
          {(() => {
            const c = list.find(x => x.id === form.contactId);
            if (!c) {
              // Day 4 hotfix BUG#2: empty state explícito en vez de panel vacío
              // que dejaba aparecer datos del contacto anterior si list cambiaba.
              return (
                <p className="mt-4 text-sm text-slate-400">
                  {list.length === 0
                    ? "No hay contactos. Crea uno con + Nuevo contacto."
                    : "Selecciona un contacto para ver detalles."}
                </p>
              );
            }
            return (
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between"><span className="text-slate-500">Empresa</span><span className="font-medium text-slate-900">{c.company}</span></div>
                <div className="flex items-center justify-between gap-3"><span className="text-slate-500">Email</span><MaskedField value={c.email} type="email" isAdmin={user.role === "SUPER_USER"} /></div>
                <div className="flex items-center justify-between gap-3"><span className="text-slate-500">Teléfono</span><MaskedField value={c.phone} type="phone" isAdmin={user.role === "SUPER_USER"} /></div>
              </div>
            );
          })()}
        </div>
      </aside>
    </div>
  );
}

/* ===========================================================================
   MyAccountSection — Bloque B v1.2.0
   Self-service de perfil: nombre + cambio de contraseña.
   Visible para TODOS los roles (no solo SUPER_USER) — cada usuario administra
   su propia cuenta. La validación de política de password se hace tanto en
   backend (class-validator) como inline en el frontend para feedback rápido.
   =========================================================================== */
function MyAccountSection({ user, onProfileUpdated }) {
  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-8">
      <h2 className="font-serif text-lg tracking-tight text-slate-900">Mi Cuenta</h2>
      <p className="text-sm text-slate-500">Información personal y credenciales de acceso</p>

      <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
        <ProfileCard user={user} onUpdated={onProfileUpdated} />
        <ChangePasswordCard />
      </div>

      {/* TEMPORAL — verificación de Sentry (gated por VITE_SENTRY_TEST). QUITAR en v1.3.0. */}
      <SentryTestButton />
    </div>
  );
}

function ProfileCard({ user, onUpdated }) {
  const [fullName, setFullName] = useState(user?.name ?? "");
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState(null); // { type: 'ok'|'err', text }

  // Resync si el user prop cambia desde afuera (ej. otro tab actualizó el nombre).
  useEffect(() => {
    setFullName(user?.name ?? "");
  }, [user?.name]);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = fullName.trim();
    if (!trimmed) {
      setMsg({ type: "err", text: "El nombre no puede estar vacío" });
      return;
    }
    setSaving(true); setMsg(null);
    try {
      const me = await api("/me/profile", {
        method: "PATCH",
        body: { fullName: trimmed },
      });
      onUpdated?.(me);
      setMsg({ type: "ok", text: "Nombre actualizado" });
    } catch (err) {
      setMsg({ type: "err", text: err?.body?.message || "No se pudo guardar el nombre" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 p-5">
      <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
        Información personal
      </p>
      <form onSubmit={submit} className="mt-3 space-y-3">
        <Field label="Nombre completo">
          <input className="input" value={fullName}
                 onChange={e => setFullName(e.target.value)}
                 placeholder="Tu nombre como lo verán otros usuarios" />
        </Field>
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px]">
            {msg?.type === "ok"  && <span className="text-emerald-700">{msg.text}</span>}
            {msg?.type === "err" && <span className="text-rose-700">{msg.text}</span>}
          </div>
          <button type="submit" disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--brand-primary)] px-3 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
            {saving ? "Guardando…" : "Guardar nombre"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ChangePasswordCard() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState(null); // { type: 'ok'|'err', text }

  // Validación inline contra la misma política del backend.
  const policy = useMemo(() => {
    const v = form.next ?? "";
    return {
      length: v.length >= 8,
      upper:  /[A-Z]/.test(v),
      digit:  /[0-9]/.test(v),
    };
  }, [form.next]);
  const policyOk = policy.length && policy.upper && policy.digit;
  const matchOk  = form.next && form.next === form.confirm;

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    setMsg(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.current) {
      setMsg({ type: "err", text: "Falta la contraseña actual" });
      return;
    }
    if (!policyOk) {
      setMsg({ type: "err", text: "La nueva contraseña no cumple la política" });
      return;
    }
    if (!matchOk) {
      setMsg({ type: "err", text: "Las dos contraseñas nuevas no coinciden" });
      return;
    }
    setSaving(true); setMsg(null);
    try {
      await api("/me/password", {
        method: "PATCH",
        body: { currentPassword: form.current, newPassword: form.next },
      });
      setMsg({ type: "ok", text: "Contraseña actualizada correctamente" });
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      // class-validator devuelve `message: string[]`; el handler de
      // "Contraseña actual incorrecta" devuelve `message: string`. Manejamos
      // ambos para mostrar el feedback exacto del backend.
      const m = err?.body?.message;
      const text = Array.isArray(m) ? m.join(". ") : (m || "No se pudo cambiar la contraseña");
      setMsg({ type: "err", text });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 p-5">
      <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
        Cambiar contraseña
      </p>
      <form onSubmit={submit} className="mt-3 space-y-3">
        <Field label="Contraseña actual">
          <input className="input" type="password" name="current"
                 value={form.current} onChange={onChange} autoComplete="current-password" />
        </Field>
        <Field label="Nueva contraseña">
          <input className="input" type="password" name="next"
                 value={form.next} onChange={onChange} autoComplete="new-password" />
          {form.next && (
            <ul className="mt-1.5 space-y-0.5 text-[11px]">
              <PolicyLine ok={policy.length} text="Mínimo 8 caracteres" />
              <PolicyLine ok={policy.upper}  text="Al menos una mayúscula" />
              <PolicyLine ok={policy.digit}  text="Al menos un número" />
            </ul>
          )}
        </Field>
        <Field label="Confirmar nueva contraseña">
          <input className="input" type="password" name="confirm"
                 value={form.confirm} onChange={onChange} autoComplete="new-password" />
          {form.confirm && !matchOk && (
            <p className="mt-1 text-[11px] text-rose-700">Las dos contraseñas nuevas no coinciden</p>
          )}
        </Field>

        {msg?.type === "ok" && (
          <div className="rounded-lg bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800 ring-1 ring-inset ring-emerald-700/15">
            ✓ {msg.text}
          </div>
        )}
        {msg?.type === "err" && (
          <div className="rounded-lg bg-rose-50 px-3 py-2 text-[12px] text-rose-800 ring-1 ring-inset ring-rose-700/15">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{msg.text}</span>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button type="submit" disabled={saving || !form.current || !policyOk || !matchOk}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--brand-primary)] px-3 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
            {saving ? "Cambiando…" : "Cambiar contraseña"}
          </button>
        </div>
      </form>
    </div>
  );
}

function PolicyLine({ ok, text }) {
  return (
    <li className={`inline-flex items-center gap-1.5 ${ok ? "text-emerald-700" : "text-slate-400"}`}>
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
      <span>{text}</span>
    </li>
  );
}

/* ===========================================================================
   SessionRoleToggle — DEV-ONLY (Bloque C v1.2.0)
   ---------------------------------------------------------------------------
   En desarrollo permite alternar el rol del user en sesión (SUPER_USER ↔
   COMMON_USER) sin re-login, para validar UX por rol sin tener dos cuentas.
   En producción NO se renderiza (gateado con import.meta.env.DEV en
   SettingsView). NO eliminar este componente — útil para QA local futuro;
   si llega un caso staging documentado, exponer un feature flag explícito.
   =========================================================================== */
function SessionRoleToggle({ user, onToggleRole }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-amber-700">
          Sesión actual · DEV
        </p>
        <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
          dev-only
        </span>
      </div>
      <p className="mt-2 text-sm text-slate-700">
        Operando como <strong className="text-slate-900">{user.name}</strong>
        {" "}({user.role === "SUPER_USER" ? "Administrador" : "Usuario común"}).
      </p>
      <button onClick={onToggleRole}
              className="mt-4 rounded-lg bg-[var(--brand-primary)] px-3 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
        Cambiar a {user.role === "SUPER_USER" ? "Usuario común" : "Administrador"}
      </button>
    </div>
  );
}

/* ===========================================================================
   Day 6 Block A — UsersManagementSection (admin only)
   - tabla con filtro Activos/Inactivos/Todos
   - modales: Nuevo / Editar / Reset password / Desactivar (con reasignación)
   =========================================================================== */
function UsersManagementSection({ user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("active"); // active | inactive | all
  const [newOpen, setNewOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [deactivating, setDeactivating] = useState(null);
  // V217 (§1ter) — titular de la cuenta: no se resetea, desactiva ni degrada desde aquí.
  const [owner, setOwner] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const list = await api(`/users?active=${filter === "active" ? "true" : filter === "inactive" ? "false" : "all"}`);
      setUsers(Array.isArray(list) ? list : []);
      try { setOwner(await api("/tenant/owner")); } catch { setOwner(null); }
    } catch (e) {
      setError(e?.body?.message || e?.message || "No se pudieron cargar los usuarios");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const canCreate = hasPermission(user, "users:create");
  const canUpdate = hasPermission(user, "users:update");
  const canDeactivate = hasPermission(user, "users:deactivate");
  // V217 (D18): toda acción sobre otro administrador (crear/promover, editar, degradar,
  // desactivar, resetear) es solo del titular, y se bloquea si soporte la congeló.
  const canAdmin = !!owner?.isOwner && !owner?.frozen;

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 px-6 py-5 md:px-8">
        <div>
          <h3 className="font-serif text-lg tracking-tight text-slate-900">Usuarios</h3>
          <p className="text-sm text-slate-500">Equipo · alta/baja · reasignación de carga</p>
        </div>
        {owner?.owner && (
          <p className="w-full text-xs text-slate-500 md:w-auto">
            Titular de la cuenta: <strong className="text-slate-800">{owner.owner.name || owner.owner.email}</strong>
            {owner.isOwner && (
              <button onClick={() => setTransferOpen(true)} className="ml-2 rounded-lg px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50">
                Transferir titularidad
              </button>
            )}
          </p>
        )}
        {canCreate && (
          <button onClick={() => setNewOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
            <Plus className="h-3.5 w-3.5" /> Nuevo usuario
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 border-b border-slate-100 px-6 py-3 md:px-8">
        {[
          { id: "active",   label: "Activos" },
          { id: "inactive", label: "Inactivos" },
          { id: "all",      label: "Todos" },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset ${filter === f.id ? "bg-slate-900 text-white ring-slate-900" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {error && <div className="mx-6 my-4 alert-over rounded-xl px-3 py-2 text-xs">{error}</div>}
      {owner?.frozen && (
        <div className="mx-6 my-4 rounded-xl border border-rose-200 bg-rose-50/60 px-4 py-3 text-xs text-rose-800 md:mx-8">
          Soporte de FluidSol congeló la titularidad por seguridad: las acciones exclusivas del titular (cobros en línea, emisor y CSD, datos fiscales, administradores, exportar clientes y "Descargar todo") están bloqueadas hasta que el nuevo titular acepte o soporte la descongele. La operación diaria sigue normal.
        </div>
      )}
      {owner?.pendingTransfer && !owner.pendingTransfer.expired && (
        <PendingOwnerTransfer pending={owner.pendingTransfer} isOwner={owner.isOwner} onChanged={load} />
      )}

      <div className="overflow-x-auto thin-scroll">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/60 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            <tr>
              <th className="px-6 py-3 text-left">Nombre</th>
              <th className="px-6 py-3 text-left">Email</th>
              <th className="px-6 py-3 text-left">Rol</th>
              <th className="px-6 py-3 text-left">Estado</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td className="px-6 py-6 text-center text-slate-400" colSpan={5}>Cargando…</td></tr>}
            {!loading && users.length === 0 && (
              <tr><td className="px-6 py-8 text-center text-slate-400" colSpan={5}>Sin usuarios en este filtro.</td></tr>
            )}
            {!loading && users.map(u => {
              const fullName = [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.email;
              return (
                <tr key={u.id}>
                  <td className="px-6 py-3 font-medium text-slate-900">
                    {fullName}
                    {owner?.owner?.userId === u.id && (
                      <span className="ml-2 rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-medium text-white">Titular</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-slate-500">{u.email}</td>
                  <td className="px-6 py-3 text-slate-600 text-xs">
                    {u.role === "SUPER_USER" ? "Administrador" : "Usuario común"}
                  </td>
                  <td className="px-6 py-3">
                    {u.isActive
                      ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">Activo</span>
                      : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">Inactivo</span>}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <span className="inline-flex items-center gap-1">
                      {u.isAdmin && !canAdmin && u.id !== user?.userId && (
                        <span className="px-2.5 py-1 text-[11px] text-slate-400">Solo el titular</span>
                      )}
                      {canUpdate && (!u.isAdmin || canAdmin || u.id === user?.userId) && (
                        <button onClick={() => setEditing(u)}
                          className="rounded-lg px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100">
                          Editar
                        </button>
                      )}
                      {canUpdate && u.id !== user?.userId && owner?.owner?.userId !== u.id && !u.isFloor && (!u.isAdmin || canAdmin) && (
                        <button onClick={() => setResetting(u)}
                          className="rounded-lg px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100">
                          Reset
                        </button>
                      )}
                      {canDeactivate && u.isActive && u.id !== user?.userId && owner?.owner?.userId !== u.id && (!u.isAdmin || canAdmin) && (
                        <button onClick={() => setDeactivating(u)}
                          className="rounded-lg px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50">
                          Desactivar
                        </button>
                      )}
                      {canDeactivate && !u.isActive && (!u.isAdmin || canAdmin) && (
                        <button onClick={async () => {
                          if (!confirm(`Reactivar a ${fullName}?`)) return;
                          try { await api(`/users/${u.id}/activate`, { method: "PATCH" }); await load(); }
                          catch (e) { alert(e?.body?.message || "No se pudo reactivar"); }
                        }}
                          className="rounded-lg px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50">
                          Reactivar
                        </button>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {newOpen && <NewUserModal canAdmin={canAdmin} isSuper={user?.role === "SUPER_USER"} onClose={() => setNewOpen(false)} onCreated={() => { setNewOpen(false); load(); }} />}
      {editing && <EditUserModal canAdmin={canAdmin} isSuper={user?.role === "SUPER_USER"} target={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {resetting && <ResetPasswordModal target={resetting} onClose={() => setResetting(null)} />}
      {transferOpen && (
        <TransferOwnerModal
          candidates={users.filter(x => x.isActive && x.role === "SUPER_USER" && x.id !== owner?.owner?.userId && !x.isFloor)}
          onClose={() => setTransferOpen(false)}
        />
      )}
      {deactivating && (
        <DeactivateUserModal
          target={deactivating}
          activeUsers={users.filter(u => u.isActive && u.id !== deactivating.id)}
          onClose={() => setDeactivating(null)}
          onDone={() => { setDeactivating(null); load(); }} />
      )}
    </div>
  );
}

// §18 — resumen legible de qué verá/podrá hacer un usuario con el rol elegido.
// Se deriva de role.permissions[] (de GET /roles). El eje financiero (Insights/
// margen/costo) se muestra explícito porque es el punto del control de acceso.
function RoleSummary({ role }) {
  if (!role) return null;
  const isAdminRole = role.slug === "admin";
  const has = (c) => isAdminRole || (Array.isArray(role.permissions) && role.permissions.includes(c));
  const caps = [
    { ok: has("pos:view"), label: "Opera el POS (mesas, comanda, cobro + propina)" },
    { ok: has("financials:view"), label: "Ve Insights, margen y costo (datos del dueño)" },
    { ok: has("projects:read") || has("clients:read") || has("leads:read"), label: "Flow: proyectos, clientes, pipeline" },
    { ok: has("contacts:view_pii"), label: "Ve datos personales (PII) sin enmascarar" },
    { ok: isAdminRole || has("settings:roles"), label: "Configuración sensible (roles, usuarios, marca)" },
  ];
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-[12px]">
      {role.description && <p className="mb-2 text-slate-600">{role.description}</p>}
      <ul className="space-y-1">
        {caps.map((c, i) => (
          <li key={i} className={`flex items-center gap-2 ${c.ok ? "text-slate-700" : "text-slate-400 line-through decoration-slate-300"}`}>
            <span className={c.ok ? "text-emerald-600" : "text-slate-300"}>{c.ok ? "✓" : "✕"}</span>
            {c.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

function NewUserModal({ onClose, onCreated, canAdmin = true, isSuper = true }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [roleId, setRoleId] = useState("");
  // v1.3.2 — flujo de acceso: 'invitation' (Opción A, default) | 'manual' (Opción B).
  const [accessMode, setAccessMode] = useState("invitation");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  // Day 6 hotfix BUG#1: fetch real roles del tenant (4 base + custom).
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api("/roles");
        const list = Array.isArray(r) ? r : [];
        setRoles(list);
        // Default: consultant (rol más restrictivo / más común), o el primero que se pueda asignar.
        const consultant = list.find(x => x.slug === "consultant");
        setRoleId(consultant?.id ?? list.find(x => assignableRole(x, { canAdmin, isSuper }))?.id ?? "");
      } catch (e) { setErr(e?.body?.message || e?.message || "No se pudieron cargar los roles"); }
      finally { setRolesLoading(false); }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (accessMode === "manual") {
      if (password !== confirmPassword) { setErr("Las contraseñas no coinciden"); return; }
      if (password.length < 8 || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
        setErr("La contraseña debe tener mínimo 8 caracteres, una mayúscula y un número");
        return;
      }
    }
    setSubmitting(true);
    try {
      // roleId (UUID del role del tenant) — backend deriva el campo legacy `role`.
      const body = accessMode === "manual"
        ? { email, firstName, lastName, roleId, useManualPassword: true, password, confirmPassword }
        : { email, firstName, lastName, roleId };
      const res: any = await api("/users", { method: "POST", body });
      setSuccessMsg(res?.message || "Usuario creado.");
    } catch (e2) {
      const msg = Array.isArray(e2?.body?.message) ? e2.body.message.join(" · ") : (e2?.body?.message || e2?.message);
      setErr(msg || "No se pudo crear el usuario");
    } finally { setSubmitting(false); }
  };

  if (successMsg) {
    return (
      <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
          <h3 className="font-serif text-lg tracking-tight text-slate-900">Usuario creado</h3>
          <p className="mt-3 rounded-xl bg-slate-50 px-3 py-3 text-sm text-slate-600 ring-1 ring-slate-200">{successMsg}</p>
          <div className="mt-5 flex justify-end">
            <button type="button" onClick={() => onCreated?.()} className="rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">Listo</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <h3 className="font-serif text-lg tracking-tight text-slate-900">Nuevo usuario</h3>
        <div className="mt-4 space-y-3">
          <Field label="Nombre">
            <input className="input" value={firstName} onChange={e => setFirstName(e.target.value)} required maxLength={100} />
          </Field>
          <Field label="Apellido">
            <input className="input" value={lastName} onChange={e => setLastName(e.target.value)} required maxLength={100} />
          </Field>
          <Field label="Email">
            <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          </Field>
          <Field label="Rol">
            <select className="input" value={roleId} onChange={e => setRoleId(e.target.value)} required disabled={rolesLoading}>
              {rolesLoading && <option value="">Cargando roles…</option>}
              {!rolesLoading && roles.length === 0 && <option value="">Sin roles disponibles</option>}
              {roles.filter(r => assignableRole(r, { canAdmin, isSuper })).map(r => (
                <option key={r.id} value={r.id}>
                  {r.name}{r.slug === "admin" ? " (Admin)" : ""}
                </option>
              ))}
            </select>
            {!canAdmin && <p className="mt-1 text-[11px] text-slate-400">Los roles de administración solo los asigna el titular de la cuenta.</p>}
          </Field>
          <RoleSummary role={roles.find(r => r.id === roleId)} />

          {/* v1.3.2 — Configuración de acceso (Opción A invitación / Opción B password). */}
          <div className="rounded-xl border border-slate-200 p-3">
            <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
              Configuración de acceso
            </p>
            <label className="flex cursor-pointer items-start gap-2">
              <input type="radio" name="accessMode" className="mt-0.5"
                checked={accessMode === "invitation"} onChange={() => setAccessMode("invitation")} />
              <span className="text-sm text-slate-700">
                <span className="font-medium">🔗 Enviar invitación por correo</span>{" "}
                <span className="text-slate-400">(recomendado)</span>
                <span className="mt-0.5 block text-[11px] text-slate-400">
                  El usuario recibirá un email para crear su propia contraseña.
                </span>
              </span>
            </label>
            <label className="mt-2 flex cursor-pointer items-start gap-2">
              <input type="radio" name="accessMode" className="mt-0.5"
                checked={accessMode === "manual"} onChange={() => setAccessMode("manual")} />
              <span className="text-sm text-slate-700">
                <span className="font-medium">🔑 Asignar contraseña inicial</span>
                <span className="mt-0.5 block text-[11px] text-slate-400">
                  Tú creas la contraseña y la compartes aparte. El usuario deberá cambiarla
                  en su primer inicio de sesión.
                </span>
              </span>
            </label>

            {accessMode === "manual" && (
              <div className="mt-3 space-y-2">
                <Field label="Contraseña (mín 8 + mayúscula + número)">
                  {/* initialShow: el admin asigna y comparte la contraseña → visible por default, pero puede ocultarla. */}
                  <PasswordInput value={password} onChange={setPassword}
                    minLength={8} autoComplete="new-password" initialShow />
                </Field>
                <Field label="Confirmar contraseña">
                  <PasswordInput value={confirmPassword} onChange={setConfirmPassword}
                    minLength={8} autoComplete="new-password" initialShow />
                  <PasswordMatchValidator password={password} confirmPassword={confirmPassword} />
                </Field>
              </div>
            )}
          </div>
        </div>
        {err && <div className="mt-3 alert-over rounded-xl px-3 py-2 text-xs">{err}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200">Cancelar</button>
          <button type="submit" disabled={submitting || rolesLoading || !roleId} className="rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
            {submitting ? "Creando…" : "Crear usuario"}
          </button>
        </div>
      </form>
    </div>
  );
}

function EditUserModal({ target, onClose, onSaved, canAdmin = true, isSuper = true }) {
  const [firstName, setFirstName] = useState(target.firstName ?? "");
  const [lastName, setLastName] = useState(target.lastName ?? "");
  const [roleId, setRoleId] = useState(target.roleId ?? "");
  // Rol con el que abrió el modal (el suyo o el que se rellenó si no tenía): solo se manda
  // roleId si el selector cambió (buildEditUserBody).
  const [initialRoleId, setInitialRoleId] = useState(target.roleId ?? "");
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  // V76 — sucursal base del usuario ("" = todas / tenant-wide).
  const [homeBranchId, setHomeBranchId] = useState(target.homeBranchId ?? "");
  const [branches, setBranches] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api("/branches").then(r => setBranches(Array.isArray(r) ? r.filter(b => b.isActive) : []))
      .catch(() => setBranches([]));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await api("/roles");
        const list = Array.isArray(r) ? r : [];
        setRoles(list);
        // Si el user no tenía roleId, deja vacío y el admin elige.
        if (!roleId && list.length) {
          const matchByLegacy = list.find(x =>
            target.role === "SUPER_USER" ? x.slug === "admin" : x.slug === "consultant"
          );
          const filled = matchByLegacy?.id ?? list[0].id;
          setRoleId(filled);
          setInitialRoleId(filled);
        }
      } catch (e) { setErr(e?.body?.message || e?.message || "No se pudieron cargar los roles"); }
      finally { setRolesLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      await api(`/users/${target.id}`, {
        method: "PATCH",
        body: buildEditUserBody({ firstName, lastName, roleId, initialRoleId, homeBranchId }),
      });
      onSaved?.();
    } catch (e2) {
      setErr(e2?.body?.message || e2?.message || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <h3 className="font-serif text-lg tracking-tight text-slate-900">Editar usuario</h3>
        <p className="text-xs text-slate-500">{target.email}</p>
        <div className="mt-4 space-y-3">
          <Field label="Nombre">
            <input className="input" value={firstName} onChange={e => setFirstName(e.target.value)} required maxLength={100} />
          </Field>
          <Field label="Apellido">
            <input className="input" value={lastName} onChange={e => setLastName(e.target.value)} required maxLength={100} />
          </Field>
          <Field label="Rol">
            <select className="input" value={roleId} onChange={e => setRoleId(e.target.value)} disabled={rolesLoading || (!canAdmin && target.isAdmin)}>
              {rolesLoading && <option value="">Cargando roles…</option>}
              {!rolesLoading && roles.filter(r => assignableRole(r, { canAdmin, isSuper }) || r.id === roleId).map(r => (
                <option key={r.id} value={r.id}>
                  {r.name}{r.slug === "admin" ? " (Admin)" : ""}
                </option>
              ))}
            </select>
          </Field>
          <RoleSummary role={roles.find(r => r.id === roleId)} />
          {branches.length > 1 && (
            <Field label="Sucursal base — el usuario queda LIMITADO a esta sucursal (ventas, corte y reportes). Para que cubra otra, reasígnala aquí.">
              <select className="input" value={homeBranchId} onChange={e => setHomeBranchId(e.target.value)}>
                <option value="">Todas (tenant-wide: consolidado + cambio libre de sucursal)</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}{b.isDefault ? " (principal)" : ""}</option>)}
              </select>
            </Field>
          )}
        </div>
        {err && <div className="mt-3 alert-over rounded-xl px-3 py-2 text-xs">{err}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200">Cancelar</button>
          <button type="submit" disabled={saving} className="rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
            {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ResetPasswordModal({ target, onClose }) {
  // V217 (§1ter): la contraseña TEMPORAL la genera el servidor y se muestra UNA vez; la
  // persona la cambia al entrar (must_change_password) y sus sesiones abiertas se cierran.
  const [shown, setShown] = useState(null);
  const [notified, setNotified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      const r: any = await api(`/users/${target.id}/reset-password`, { method: "POST", body: {} });
      setShown(r?.temporaryPassword ?? null);
      setNotified(!!r?.notified);
    } catch (e2) {
      const msg = Array.isArray(e2?.body?.message) ? e2.body.message.join(" · ") : (e2?.body?.message || e2?.message);
      setErr(msg || "No se pudo restablecer");
    } finally { setSubmitting(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(shown); setCopied(true); } catch { /* se copia a mano */ }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <h3 className="font-serif text-lg tracking-tight text-slate-900">Restablecer contraseña</h3>
        <p className="text-xs text-slate-500">{[target.firstName, target.lastName].filter(Boolean).join(" ")} · {target.email}</p>
        {!shown ? (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <p className="text-xs text-slate-600">
              Se generará una contraseña temporal que verás una sola vez. La persona deberá crear una nueva al entrar y se cerrarán sus sesiones abiertas.
              A otros administradores solo los puede restablecer el titular de la cuenta.
            </p>
            {err && <div className="alert-over rounded-xl px-3 py-2 text-xs">{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200">Cancelar</button>
              <button type="submit" disabled={submitting} className="rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
                {submitting ? "Generando…" : "Generar contraseña temporal"}
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Contraseña temporal</p>
              <p className="mt-1 break-all font-mono text-sm text-emerald-900">{shown}</p>
              <button type="button" onClick={copy} className="mt-2 rounded-lg bg-white px-2 py-1 text-[11px] font-medium text-emerald-800 ring-1 ring-emerald-200">
                {copied ? "Copiada" : "Copiar"}
              </button>
              <p className="mt-3 text-[11px] text-slate-500">
                No se volverá a mostrar. Compártela por un medio seguro; al entrar tendrá que crear la suya.
                {notified ? " Le avisamos por correo (sin la contraseña)." : " Este usuario no tiene un correo real: no se le envió aviso."}
              </p>
            </div>
            <button onClick={onClose} className="w-full rounded-xl bg-[var(--brand-primary)] px-3 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
              Listo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* V217 (§1ter) — transferencia de titularidad PENDIENTE. El destinatario la acepta desde SU
   sesión (acepta a su nombre los Términos y Condiciones y, si el negocio factura, el contrato
   de emisor); el titular puede cancelarla. Caduca a los 7 días. */
function PendingOwnerTransfer({ pending, isOwner, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const until = new Date(pending.expiresAt).toLocaleDateString("es-MX");

  const act = async (what) => {
    if (what === "accept" && !confirm("Al aceptar serás el titular de la cuenta y aceptas a tu nombre los Términos y Condiciones vigentes (y el contrato de emisor de facturas, si el negocio factura). ¿Aceptar?")) return;
    setBusy(true); setErr(null);
    try {
      await api(`/tenant/owner/transfer/${pending.id}/${what}`, { method: "POST" });
      if (what === "accept") window.location.reload(); else await onChanged?.();
    } catch (e) {
      setErr(e?.body?.message || e?.message || "No se pudo completar");
    } finally { setBusy(false); }
  };

  if (!pending.forMe && !isOwner) return null;
  return (
    <div className="mx-6 my-4 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3 text-xs text-amber-900 md:mx-8">
      {pending.forMe ? (
        <>
          <p>
            {pending.byPlatform ? "El equipo de soporte de FluidSol" : (pending.fromName || "El titular")} inició la transferencia de la titularidad a tu nombre. Acéptala antes del {until}.
          </p>
          <button onClick={() => act("accept")} disabled={busy}
            className="mt-2 rounded-lg bg-slate-900 px-3 py-1 font-medium text-white disabled:opacity-50">
            {busy ? "Aceptando…" : "Aceptar la titularidad"}
          </button>
        </>
      ) : (
        <>
          <p>Transferencia de titularidad pendiente a <strong>{pending.toName}</strong> hasta el {until}. Sigues siendo el titular hasta que la acepte.</p>
          <button onClick={() => act("cancel")} disabled={busy}
            className="mt-2 rounded-lg bg-white px-3 py-1 font-medium text-slate-700 ring-1 ring-slate-200 disabled:opacity-50">
            {busy ? "Cancelando…" : "Cancelar transferencia"}
          </button>
        </>
      )}
      {err && <p className="mt-2 text-rose-700">{err}</p>}
    </div>
  );
}

/* V217 (§1ter) — el titular cede la titularidad a otro administrador activo. Confirma con su
   contraseña; después su sesión se cierra (sigue como administrador). */
function TransferOwnerModal({ candidates, onClose }) {
  // (V217) queda PENDIENTE hasta que el destinatario acepta desde su sesión.
  const [to, setTo] = useState(candidates[0]?.id ?? "");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      await api("/tenant/owner/transfer", { method: "POST", body: { newOwnerUserId: to, password } });
      setDone(true);
    } catch (e2) {
      const msg = Array.isArray(e2?.body?.message) ? e2.body.message.join(" · ") : (e2?.body?.message || e2?.message);
      setErr(msg || "No se pudo transferir");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <h3 className="font-serif text-lg tracking-tight text-slate-900">Transferir titularidad</h3>
        {done ? (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-slate-700">Listo: la transferencia quedó pendiente. Se hará efectiva cuando la otra persona la acepte desde su sesión (tiene 7 días). Mientras tanto sigues siendo el titular. Avisamos por correo a ambos.</p>
            <button onClick={onClose} className="w-full rounded-xl bg-[var(--brand-primary)] px-3 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
              Entendido
            </button>
          </div>
        ) : candidates.length === 0 ? (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-slate-600">Primero da de alta a otro administrador activo; la titularidad solo se transfiere a un administrador.</p>
            <button onClick={onClose} className="w-full rounded-xl bg-white px-3 py-2 text-xs font-medium text-slate-700 ring-1 ring-slate-200">Cerrar</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <p className="text-xs text-slate-600">
              El titular conecta la cuenta de cobros, captura los datos fiscales, recibe los avisos de la cuenta y es el único que puede restablecer la contraseña de otros administradores.
            </p>
            <Field label="Nuevo titular">
              <select className="input" value={to} onChange={e => setTo(e.target.value)} required>
                {candidates.map(c => (
                  <option key={c.id} value={c.id}>{[c.firstName, c.lastName].filter(Boolean).join(" ") || c.email} · {c.email}</option>
                ))}
              </select>
            </Field>
            <Field label="Tu contraseña (para confirmar)">
              <input className="input" type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} required />
            </Field>
            {err && <div className="alert-over rounded-xl px-3 py-2 text-xs">{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200">Cancelar</button>
              <button type="submit" disabled={submitting || !to} className="rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
                {submitting ? "Transfiriendo…" : "Transferir"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function DeactivateUserModal({ target, activeUsers, onClose, onDone }) {
  const [workload, setWorkload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reassignTo, setReassignTo] = useState(activeUsers[0]?.id ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const w = await api(`/users/${target.id}/workload`);
        setWorkload(w);
      } catch (e) {
        setErr(e?.body?.message || e?.message || "No se pudo cargar la carga");
      } finally { setLoading(false); }
    })();
  }, [target.id]);

  const totalLoad = (workload?.projects ?? 0) + (workload?.leads ?? 0) + (workload?.quotes ?? 0) + (workload?.tasks ?? 0);
  const needsReassign = totalLoad > 0;

  const submit = async () => {
    setSubmitting(true); setErr(null);
    try {
      const url = needsReassign && reassignTo
        ? `/users/${target.id}/deactivate?reassignTo=${encodeURIComponent(reassignTo)}`
        : `/users/${target.id}/deactivate`;
      await api(url, { method: "PATCH" });
      onDone?.();
    } catch (e2) {
      setErr(e2?.body?.message || e2?.message || "No se pudo desactivar");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <h3 className="font-serif text-lg tracking-tight text-slate-900">Desactivar usuario</h3>
        <p className="text-xs text-slate-500">
          {[target.firstName, target.lastName].filter(Boolean).join(" ")} · {target.email}
        </p>

        {loading && <p className="mt-4 text-sm text-slate-400">Calculando carga…</p>}

        {!loading && workload && (
          <>
            {needsReassign ? (
              <>
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-xs text-amber-900">
                  Este usuario tiene <strong>{workload.projects}</strong> proyecto(s),{" "}
                  <strong>{workload.leads}</strong> lead(s),{" "}
                  <strong>{workload.quotes}</strong> cotización(es) y{" "}
                  <strong>{workload.tasks}</strong> tarea(s) asignados. Para desactivar, reasigna su carga.
                </div>
                <Field label="Reasignar a">
                  <select className="input mt-3" value={reassignTo} onChange={e => setReassignTo(e.target.value)}>
                    {activeUsers.length === 0 && <option value="">No hay otros usuarios activos</option>}
                    {activeUsers.map(u => (
                      <option key={u.id} value={u.id}>
                        {[u.firstName, u.lastName].filter(Boolean).join(" ") || u.email}
                      </option>
                    ))}
                  </select>
                </Field>
              </>
            ) : (
              <p className="mt-4 text-sm text-slate-600">Sin carga pendiente. Se desactivará directamente.</p>
            )}
          </>
        )}

        {err && <div className="mt-3 alert-over rounded-xl px-3 py-2 text-xs">{err}</div>}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200">Cancelar</button>
          <button onClick={submit}
            disabled={submitting || (needsReassign && !reassignTo)}
            className="rounded-xl bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50">
            {submitting ? "Desactivando…" : needsReassign ? "Confirmar y reasignar" : "Confirmar desactivación"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===========================================================================
   Day 5 hotfix BUG#2: BrandingSection — admin edita name/color/logo del tenant
   =========================================================================== */
const DISPLAY_CURRENCIES = [
  { code: "USD", name: "US Dollar" },
  { code: "MXN", name: "Peso Mexicano" },
  { code: "EUR", name: "Euro" },
  { code: "BRL", name: "Real Brasileño" },
  { code: "GBP", name: "Libra Esterlina" },
  { code: "CAD", name: "Dólar Canadiense" },
  { code: "JPY", name: "Yen" },
  { code: "ARS", name: "Peso Argentino" },
  { code: "COP", name: "Peso Colombiano" },
  { code: "CLP", name: "Peso Chileno" },
];

function BrandingSection({ me }) {
  const [displayName, setDisplayName] = useState(me?.tenant?.branding?.brandName ?? me?.tenant?.name ?? "");
  const [primaryColor, setPrimaryColor] = useState(me?.tenant?.branding?.primaryColor ?? "#0F172A");
  const [logoUrl, setLogoUrl] = useState(me?.tenant?.branding?.brandLogoUrl ?? "");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  // chore/branding-polish — antes pedía recargar la página para ver el theme.
  // setTheme re-aplica las CSS vars (--brand-primary*) al instante; el refresh
  // de auth re-lee /me y propaga el nuevo branding a sidebar/topbar (BrandMark).
  const { setTheme } = useTheme();
  const { refresh: refreshMe } = useAuth();

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      await api("/me/tenant/branding", {
        method: "PATCH",
        body: { displayName, primaryColor, logoUrl: logoUrl ?? "" },
      });
      setTheme({ brandName: displayName, brandLogoUrl: logoUrl || null, primaryColor });
      await refreshMe();
      setMsg({ type: "ok", text: "Branding actualizado." });
    } catch (e) {
      setMsg({ type: "err", text: e?.body?.message || e?.message || "No se pudo guardar" });
    } finally { setSaving(false); }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("logo", file);
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API_BASE}/me/tenant/logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: form,
      });
      const data = await res.json();
      if (!res.ok) {
        setMsg({ type: "err", text: data?.message ?? "No se pudo subir el logo" });
        return;
      }
      setLogoUrl(data.logoUrl);
      setMsg({ type: "ok", text: "Logo subido." });
    } catch (err) {
      setMsg({ type: "err", text: err?.message ?? "No se pudo subir el logo" });
    } finally { setSaving(false); }
  };

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-8">
      <h2 className="font-serif text-lg tracking-tight text-slate-900">Marca del negocio</h2>
      <p className="text-sm text-slate-500">Nombre comercial, color primario y logo. Aparece en sidebar, headers y cotizaciones.</p>

      <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
        <Field label="Nombre comercial">
          <input className="input" value={displayName} onChange={e => setDisplayName(e.target.value)} minLength={3} maxLength={80} />
        </Field>
        <Field label="Color primario">
          <div className="flex items-center gap-3">
            <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)}
              className="h-10 w-14 cursor-pointer rounded-lg border border-slate-200" />
            <input className="input flex-1" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} />
          </div>
        </Field>
        <Field label="Logo">
          {logoUrl ? (
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
              <img src={logoUrl} alt="Logo" className="h-12 w-12 rounded object-contain ring-1 ring-slate-200" />
              <button type="button" onClick={() => setLogoUrl("")}
                className="text-xs font-medium text-rose-700 hover:underline">
                Quitar logo
              </button>
            </div>
          ) : (
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={onPickFile}
              className="w-full text-xs file:mr-3 file:rounded-lg file:border file:border-slate-200 file:bg-white file:px-3 file:py-2 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-50"
            />
          )}
          <span className="mt-1 block text-[11px] text-slate-400">PNG/JPG, máx 1MB.</span>
        </Field>
      </div>

      <div className="mt-6 flex items-center justify-between gap-3 border-t border-slate-100 pt-5">
        <div className="text-xs">
          {msg?.type === "ok"  && <span className="text-emerald-700">{msg.text}</span>}
          {msg?.type === "err" && <span className="text-rose-700">{msg.text}</span>}
        </div>
        <button onClick={save} disabled={saving}
          className="rounded-xl bg-[var(--brand-primary)] px-4 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
          {saving ? "Guardando…" : "Guardar branding"}
        </button>
      </div>
    </div>
  );
}

/**
 * §17.2.c — Fondo del espacio de trabajo. Selector de patrón (librería curada) +
 * 2-3 colores (paleta de marca por default, custom con color picker) + live
 * preview. Persiste en tenants.theme (aplica a TODOS los usuarios del tenant).
 */
function WorkspaceThemeSection({ me }) {
  const initial = me?.tenant?.theme ?? { pattern: "none", colors: [] };
  const [pattern, setPattern] = useState(initial.pattern ?? "none");
  const [colors, setColors] = useState(
    (initial.colors?.length ? initial.colors : DEFAULT_THEME_COLORS).slice(0, 3),
  );
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const setColorAt = (i, v) => setColors((cs) => cs.map((c, idx) => (idx === i ? v : c)));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      // 'none' guarda colores vacíos (fondo neutro); el resto manda los 3 colores.
      const body = pattern === "none" ? { pattern, colors: [] } : { pattern, colors };
      await api("/me/tenant/theme", { method: "PATCH", body });
      // Recarga para aplicar el fondo a todo el shell (mismo patrón que branding/módulos).
      window.location.reload();
    } catch (e) {
      setMsg({ type: "err", text: e?.body?.message || e?.message || "No se pudo guardar" });
      setSaving(false);
    }
  };

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-8">
      <h2 className="font-serif text-lg tracking-tight text-slate-900">Fondo del espacio de trabajo</h2>
      <p className="text-sm text-slate-500">Un fondo sutil detrás del contenido. Curado para no estorbar la lectura; aplica a todo el equipo.</p>

      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Controles */}
        <div className="space-y-5">
          <Field label="Patrón">
            <select className="input" value={pattern} onChange={(e) => setPattern(e.target.value)}>
              {PATTERN_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </Field>

          {pattern !== "none" && (
            <div>
              <p className="mb-1.5 text-xs font-medium text-slate-600">Colores del degradado (hasta 3)</p>
              <div className="space-y-2">
                {colors.map((c, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <input type="color" value={c} onChange={(e) => setColorAt(i, e.target.value)}
                      className="h-9 w-12 cursor-pointer rounded-lg border border-slate-200" />
                    <input className="input flex-1 font-mono text-xs" value={c}
                      onChange={(e) => setColorAt(i, e.target.value)} />
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <p className="mb-1 text-[11px] text-slate-400">Paleta de marca</p>
                <div className="flex flex-wrap gap-1.5">
                  {BRAND_PALETTE.map((c) => (
                    <button key={c} type="button" title={c}
                      onClick={() => setColors((cs) => { const n = [...cs]; n[0] = c; return n; })}
                      className="h-6 w-6 rounded-md ring-1 ring-inset ring-black/10 transition hover:scale-110"
                      style={{ background: c }} />
                  ))}
                </div>
                <button type="button"
                  onClick={() => setColors(DEFAULT_THEME_COLORS.slice())}
                  className="mt-2 text-[11px] text-slate-500 underline hover:text-slate-800">
                  Restablecer al degradado de marca
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Live preview: el fondo real + una card de muestra para validar legibilidad. */}
        <div>
          <p className="mb-1.5 text-xs font-medium text-slate-600">Vista previa</p>
          <div className="relative h-48 overflow-hidden rounded-xl border border-slate-200 bg-[#FAFAF7]">
            <WorkspaceBackground theme={{ pattern, colors }} preview />
            <div className="relative z-10 flex h-full flex-col gap-2 p-4">
              <div className="rounded-lg bg-white p-3 shadow-sm ring-1 ring-slate-200">
                <p className="text-sm font-semibold text-slate-900">Texto de muestra</p>
                <p className="text-xs text-slate-500">El contenido va en cards opacas — legible sobre cualquier patrón.</p>
              </div>
              <div className="mt-auto h-2 w-1/2 rounded bg-white/70" />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between gap-3 border-t border-slate-100 pt-5">
        <div className="text-xs">
          {msg?.type === "err" && <span className="text-rose-700">{msg.text}</span>}
        </div>
        <button onClick={save} disabled={saving}
          className="rounded-xl bg-[var(--brand-primary)] px-4 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
          {saving ? "Guardando…" : "Guardar fondo"}
        </button>
      </div>
    </div>
  );
}

/* ===========================================================================
   §17.3 — YourPlanSection: "Tu plan" READ-ONLY. Reemplaza el toggle libre de
   módulos (ModulesSection, retirado): el admin NO puede prender módulos gratis.
   El cambio de plan es controlado (platform-admin hoy; pago de diferencia vía
   Stripe en un PR posterior). El módulo se DERIVA del plan (backend, V34).
   =========================================================================== */
// Display-only: el módulo interno sigue siendo `gym`; en UI se muestra "Wellness".
const MODULE_LABEL = { crm: "Flow / Proyectos", boutique: "Boutique", gastro: "GastroGest", gym: "Wellness" };
function YourPlanSection({ me }) {
  const planId = me?.tenant?.plan && PLAN_META[me.tenant.plan] ? me.tenant.plan : "mixto";
  const meta = PLAN_META[planId];
  const modules = me?.tenant?.enabledModules?.length ? me.tenant.enabledModules : meta.modules;
  const [askOpen, setAskOpen] = useState(false);
  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-8">
      <h2 className="font-serif text-lg tracking-tight text-slate-900">Tu plan</h2>
      <p className="mt-1 text-sm text-slate-500">El plan define qué módulos ves. Para cambiarlo, mejora tu plan o contáctanos.</p>

      <div className="mt-5 rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">{meta.label}</p>
            <p className="text-[11px] uppercase tracking-wide text-slate-400">{meta.tagline}</p>
          </div>
          <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-medium text-white">{priceLabel(planId)}</span>
        </div>
        <p className="mt-3 text-sm text-slate-600">{meta.includes}</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {modules.map((m) => (
            <span key={m} className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
              {MODULE_LABEL[m] ?? m}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button type="button" onClick={() => setAskOpen(true)}
          className="rounded-xl bg-[var(--brand-primary)] px-4 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
          Cambiar plan / mejorar
        </button>
      </div>

      {askOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="font-serif text-lg tracking-tight text-slate-900">Mejorar tu plan</h3>
            <p className="mt-2 text-sm text-slate-600">
              Al cambiar a plan anual se cobra el <strong>año completo hoy</strong> y tu plan anual arranca de inmediato (no es un prorrateo de la diferencia). Escríbenos con el plan que quieres y lo activamos.
            </p>
            {/* SLOT STRIPE: aquí irá el botón de "pagar diferencia" (PR Stripe posterior),
                que tras el cobro disparará el upgrade automático del plan. */}
            <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-500">
              Escríbenos a <a className="underline" href="mailto:soporte@fluidsol.mx">soporte@fluidsol.mx</a> con el plan que quieres.
            </div>
            <div className="mt-5 flex justify-end">
              <button onClick={() => setAskOpen(false)}
                className="rounded-xl bg-[var(--brand-primary)] px-4 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">Entendido</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ===========================================================================
   Day 5 hotfix BUG#3: DisplayCurrencyPreferenceSection
   Dropdown de 10 monedas. PATCH /me/tenant/display-currency + reload para que
   se aplique en todos los renderers.
   =========================================================================== */
function DisplayCurrencyPreferenceSection({ me }) {
  const initial = me?.tenant?.displayCurrency ?? "USD";
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const save = async () => {
    if (value === initial) {
      setMsg({ type: "err", text: "No hay cambios que guardar." });
      return;
    }
    setSaving(true); setMsg(null);
    try {
      await api("/me/tenant/display-currency", {
        method: "PATCH",
        body: { displayCurrency: value },
      });
      // Reload para que los renderers leen la nueva preferencia desde /me.
      window.location.reload();
    } catch (e) {
      setMsg({ type: "err", text: e?.body?.message || e?.message || "No se pudo guardar" });
      setSaving(false);
    }
  };

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-8">
      <h2 className="font-serif text-lg tracking-tight text-slate-900">Preferencias de visualización</h2>
      <p className="text-sm text-slate-500">
        Todas las stats y reportes se mostrarán en esta moneda. La conversión usa las Budget FX rates configuradas para tu tenant.
      </p>
      <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto] md:items-end">
        <Field label="Moneda de visualización">
          <select className="input" value={value} onChange={e => setValue(e.target.value)}>
            {DISPLAY_CURRENCIES.map(c => (
              <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
            ))}
          </select>
        </Field>
        <button onClick={save} disabled={saving || value === initial}
          className="rounded-xl bg-[var(--brand-primary)] px-4 py-2.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
          {saving ? "Guardando…" : "Guardar preferencia"}
        </button>
      </div>
      {msg?.type === "err" && <p className="mt-3 text-xs text-rose-700">{msg.text}</p>}
    </div>
  );
}

/* ===========================================================================
   V128 (Carril K) — Ventana de inactividad configurable por el tenant.
   El admin elige cuánto dura la sesión sin actividad (1h/4h/8h/12h). Aplica a
   TODOS los usuarios del negocio. Toma efecto sin re-login (setTenantIdleBase +
   se re-evalúa en cada /me). Advertencia visible en 8h/12h (equipos compartidos).
   =========================================================================== */
const SESSION_IDLE_OPTIONS = [
  { min: 60, label: "1 hora" },
  { min: 240, label: "4 horas" },
  { min: 480, label: "8 horas" },
  { min: 720, label: "12 horas" },
];
function SessionIdleSection({ me }) {
  const initial = me?.tenant?.sessionIdleMinutes ?? 60;
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const warn = value >= 480; // 8h / 12h

  const save = async () => {
    if (value === initial) { setMsg({ type: "err", text: "No hay cambios que guardar." }); return; }
    setSaving(true); setMsg(null);
    try {
      await api("/me/tenant/session-idle", { method: "PATCH", body: { sessionIdleMinutes: value } });
      // Aplica sin re-login: actualiza la ventana local (el resto de sesiones del
      // tenant la toman en su próximo /me).
      setTenantIdleBase(value);
      setMsg({ type: "ok", text: "Guardado. Aplica a las sesiones de este negocio." });
    } catch (e) {
      setMsg({ type: "err", text: e?.body?.message || e?.message || "No se pudo guardar" });
    } finally { setSaving(false); }
  };

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-8">
      <h2 className="font-serif text-lg tracking-tight text-slate-900">Sesión e inactividad</h2>
      <p className="text-sm text-slate-500">
        Cierra la sesión automáticamente tras un periodo de inactividad. Aplica a todos los usuarios de este negocio.
      </p>
      <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto] md:items-end">
        <Field label="Cerrar sesión por inactividad">
          <select className="input" value={value} onChange={e => setValue(Number(e.target.value))}>
            {SESSION_IDLE_OPTIONS.map(o => (
              <option key={o.min} value={o.min}>Tras {o.label} de inactividad</option>
            ))}
          </select>
        </Field>
        <button onClick={save} disabled={saving || value === initial}
          className="rounded-xl bg-[var(--brand-primary)] px-4 py-2.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
          {saving ? "Guardando…" : "Guardar"}
        </button>
      </div>
      {warn && (
        <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          No recomendado en equipos compartidos entre varios cajeros.
        </p>
      )}
      {msg?.type === "err" && <p className="mt-3 text-xs text-rose-700">{msg.text}</p>}
      {msg?.type === "ok" && <p className="mt-3 text-xs text-emerald-700">{msg.text}</p>}
    </div>
  );
}

/* ===========================================================================
   §31.2 (V131) — Umbral del semáforo de comandas (GastroGest). 10/15/20 min.
   El semáforo pasa a rojo cuando el pendiente más viejo supera el umbral; ámbar
   al acercarse (2/3). Aplica igual a bebidas y platillos. Sin re-login (refresh
   de /me → el POS/cocina leen el umbral nuevo).
   =========================================================================== */
const KITCHEN_ALERT_OPTIONS = [10, 15, 20];
function KitchenAlertSection({ me }) {
  const { refresh } = useAuth();
  const initial = me?.tenant?.kitchenAlertMinutes ?? 15;
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  // V169 (🅑) — separación Cocina vs Barra.
  const [twoStations, setTwoStations] = useState(!!me?.tenant?.kitchenTwoStations);

  const save = async (next) => {
    setSaving(true); setMsg(null);
    try {
      await api("/me/tenant/kitchen-alert", { method: "PATCH", body: { kitchenAlertMinutes: next } });
      setValue(next);
      await refresh(); // el semáforo lee el umbral de /me → responde sin re-login
      setMsg({ type: "ok", text: `Umbral guardado: ${next} minutos.` });
    } catch (e) {
      setMsg({ type: "err", text: e?.body?.message || e?.message || "No se pudo guardar" });
    } finally { setSaving(false); }
  };

  // V169 (🅑) — enciende/apaga dos estaciones. refresh() → KitchenView refleja el
  // cambio SIN recargar la app (lee user.tenant.kitchenTwoStations de /me).
  const saveStations = async (next) => {
    setSaving(true); setMsg(null);
    try {
      await api("/me/tenant/kitchen-stations", { method: "PATCH", body: { enabled: next } });
      setTwoStations(next);
      await refresh();
      setMsg({ type: "ok", text: next ? "Barra separada de Cocina." : "Una sola línea de cocina." });
    } catch (e) {
      setMsg({ type: "err", text: e?.body?.message || e?.message || "No se pudo guardar" });
    } finally { setSaving(false); }
  };

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-8">
      <h2 className="font-serif text-lg tracking-tight text-slate-900">Semáforo de cocina</h2>
      <p className="text-sm text-slate-500">
        Tiempo tope de preparación de una comanda. El semáforo pasa a <strong>rojo</strong> al superarlo y a <strong>ámbar</strong> al acercarse. Aplica igual a bebidas y platillos.
      </p>
      <div className="mt-6 flex flex-wrap gap-2">
        {KITCHEN_ALERT_OPTIONS.map(o => (
          <button key={o} onClick={() => save(o)} disabled={saving}
            className={`rounded-xl px-4 py-2.5 text-sm font-medium transition ${value === o ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"} disabled:opacity-50`}>
            {o} min
          </button>
        ))}
      </div>
      <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-500">
        ¿Tienes una pantalla fija en cocina? Sube la <strong>ventana de sesión</strong> (hasta 12h) para que no se cierre sola durante el servicio.
      </p>

      {/* V169 (🅑) — Barra vs Cocina. Apagado = una sola línea (no se ven dos vistas). */}
      <div className="mt-6 flex items-center justify-between gap-4 border-t border-slate-100 pt-6">
        <span>
          <span className="block text-sm font-medium text-slate-800">Separar Barra de Cocina</span>
          <span className="block text-[12px] text-slate-500">
            Enciéndelo si tienes barra: las bebidas van a la vista de <strong>Barra</strong> y los platillos a <strong>Cocina</strong>, según la estación de cada categoría. Apagado, todo va a una sola línea.
          </span>
        </span>
        <button disabled={saving} onClick={() => saveStations(!twoStations)}
          className={`relative h-6 w-11 shrink-0 rounded-full transition ${twoStations ? "bg-emerald-500" : "bg-slate-300"} disabled:opacity-50`}
          aria-label="Separar Barra de Cocina">
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${twoStations ? "left-[22px]" : "left-0.5"}`} />
        </button>
      </div>

      {msg?.type === "err" && <p className="mt-3 text-xs text-rose-700">{msg.text}</p>}
      {msg?.type === "ok" && <p className="mt-3 text-xs text-emerald-700">{msg.text}</p>}
    </div>
  );
}

/* ===========================================================================
   §30 (V129) — Cambio en efectivo: toggle de redondeo por tenant.
   Redondea el CAMBIO hacia arriba a $0.50 a favor del cliente. Default off. No
   toca el total del ticket; el diferencial lo absorbe el negocio y aparece en el
   Cierre de día. Aplica sin re-login (refresh de /me → el POS lee el flag).
   =========================================================================== */
function CashRoundingSection({ me }) {
  const { refresh } = useAuth();
  const [enabled, setEnabled] = useState(me?.tenant?.cashRoundingEnabled ?? false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const toggle = async (next) => {
    setSaving(true); setMsg(null);
    try {
      await api("/me/tenant/cash-rounding", { method: "PATCH", body: { enabled: next } });
      setEnabled(next);
      await refresh(); // el POS lee cashRoundingEnabled de /me
      setMsg({ type: "ok", text: next ? "Redondeo activado." : "Redondeo desactivado." });
    } catch (e) {
      setMsg({ type: "err", text: e?.body?.message || e?.message || "No se pudo guardar" });
    } finally { setSaving(false); }
  };

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-8">
      <h2 className="font-serif text-lg tracking-tight text-slate-900">Cambio en efectivo</h2>
      <p className="text-sm text-slate-500">
        Al cobrar en efectivo, redondear el <strong>cambio</strong> hacia arriba a $0.50 a favor del cliente. No cambia el total del ticket; el diferencial lo absorbe el negocio y se reporta en el <strong>Cierre de día</strong>.
      </p>
      <div className="mt-6 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
        <span className="text-sm font-medium text-slate-700">Redondear el cambio a $0.50 (a favor del cliente)</span>
        <button type="button" onClick={() => toggle(!enabled)} disabled={saving}
          className={`h-8 w-14 shrink-0 rounded-full p-1 transition ${enabled ? "bg-slate-900" : "bg-slate-300"} disabled:opacity-50`}>
          <span className={`block h-6 w-6 rounded-full bg-white transition ${enabled ? "translate-x-6" : ""}`} />
        </button>
      </div>
      {msg?.type === "err" && <p className="mt-3 text-xs text-rose-700">{msg.text}</p>}
      {msg?.type === "ok" && <p className="mt-3 text-xs text-emerald-700">{msg.text}</p>}
    </div>
  );
}

/* ===========================================================================
   Carril [A] — Reorganización de Configuración en grupos colapsables (acordeón).
   REORGANIZACIÓN PURA: cada sección conserva su gate (módulo×rol) y su
   comportamiento EXACTO; esto solo agrupa y colapsa. La estructura es
   data-driven (ver SETTINGS_GROUPS en SettingsView): cada item declara `show`
   = su gate original y `keywords` para el buscador. Vertical-aware: un grupo
   sin items visibles NO se renderiza (un tenant Flow no ve POS/sucursales).
   =========================================================================== */

/** Normaliza para búsqueda insensible a acentos/mayúsculas. */
const normalizeText = (s) => (s ?? "").toString().toLowerCase()
  .normalize("NFD").replace(/\p{Diacritic}/gu, "");

/** Estado de expansión del acordeón, recordado en localStorage POR USUARIO
 *  (preferencia local — §1.4: no requiere columna en base). Default: solo el
 *  primer grupo abierto. */
function useSettingsAccordion(userId, firstGroupId) {
  const storageKey = `stratos.settings.accordion.${userId ?? "anon"}`;
  const [openMap, setOpenMap] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore corrupt/absent */ }
    return firstGroupId ? { [firstGroupId]: true } : {};
  });
  const toggle = useCallback((id) => {
    setOpenMap((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* quota */ }
      return next;
    });
  }, [storageKey]);
  return [openMap, toggle];
}

/** Grupo colapsable (acordeón) — un card con header clickable + chevron. Cuando
 *  hay búsqueda activa (forceOpen) se expande y el chevron se bloquea.
 *
 *  Cuenta los items que REALMENTE se renderizan midiendo el DOM: varias tarjetas
 *  se auto-ocultan en runtime (kill-switch/estatus async → devuelven null), así
 *  que un conteo por gate estático mentiría. Si nada se renderiza, el grupo
 *  entero se oculta (nada de headers vacíos con un badge fantasma). Los hijos se
 *  MONTAN siempre (mismo fetch que antes de la reorg); el colapso es visual. */
function SettingsGroup({ id, title, icon: Icon, open, forceOpen, onToggle, children }) {
  const expanded = forceOpen || open;
  const contentRef = useRef(null);
  const [count, setCount] = useState(0);
  // #578 (fix UI) — antes se medía el count una sola vez por render; un grupo con
  // tarjetas que resuelven ASYNC (devuelven null y luego montan) se quedaba en
  // count=0 (oculto) hasta un re-render ajeno (p.ej. abrir "Negocio y marca").
  // Un MutationObserver recuenta cuando los hijos aparecen/desaparecen.
  React.useLayoutEffect(() => {
    const el = contentRef.current as HTMLElement | null;
    if (!el) return;
    const recount = () => setCount(el.childElementCount);
    recount();
    const mo = new MutationObserver(recount);
    mo.observe(el, { childList: true });
    return () => mo.disconnect();
  }, []);
  return (
    <section className={`card-soft overflow-hidden rounded-2xl border border-slate-200/80 bg-white ${count === 0 ? "hidden" : ""}`}>
      <button type="button"
        onClick={() => !forceOpen && onToggle(id)}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left transition hover:bg-slate-50/70 md:px-8">
        <span className="flex items-center gap-3">
          {Icon && <Icon className="h-5 w-5 text-slate-400" />}
          <span className="font-serif text-lg tracking-tight text-slate-900">{title}</span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">{count}</span>
        </span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""} ${forceOpen ? "opacity-40" : ""}`} />
      </button>
      <div ref={contentRef}
        className={`space-y-6 border-t border-slate-100 p-5 md:p-6 ${expanded ? "" : "hidden"}`}>
        {children}
      </div>
    </section>
  );
}

function SettingsView({ user, me, onToggleRole, isAdmin, tags, onNewTag, onEditTag, onToggleTag,
                       users, onChangeUserRate,
                       displayCurrency, setDisplayCurrency, fxRates, onUpsertFx, onFetchBmv,
                       onProfileUpdated }) {
  // §18 — Configuración por espacio: las secciones específicas de un módulo solo se
  // muestran si el tenant lo tiene activo (enabled_modules). Las transversales
  // (Mi cuenta, marca, moneda, legal, usuarios, política de datos) van siempre.
  // Ej.: un tenant GastroGest no ve ajustes de Flow (tipos de actividad, etapas del
  // pipeline). Fuente de verdad: backend; el front solo oculta (no es control de acceso).
  const enabledModules = me?.tenant?.enabledModules?.length
    ? me.tenant.enabledModules
    : ["crm", "boutique", "gastro"];
  const hasModule = (m) => enabledModules.includes(m);
  // Vertical-aware: reusa el gating de módulos existente (enabled_modules). Cada
  // item declara su `show` = el gate ORIGINAL (mismo módulo×rol); un grupo sin
  // items visibles no se renderiza. Comportamiento y defaults INTACTOS (reorg pura).
  const isCrm = hasModule("crm");
  const isBooking = hasModule("gym") || hasModule("boutique");
  const isPos = hasModule("gastro") || hasModule("boutique") || hasModule("gym");
  const isGastro = hasModule("gastro"); // §31.4 — recordatorio de sugeridos (gastro-only)
  // Las tarjetas de facturación/pago a FluidSol se auto-ocultan si el billing
  // está apagado; ese candado (me.billing.checkoutEnabled) SÍ lo conoce el padre
  // → lo metemos en `show` para no contar tarjetas que no se renderizan. Los
  // otros candados (CFDI_ISSUER, PAYMENTS_ENABLED) son async → los resuelve el
  // conteo por DOM de SettingsGroup.
  const checkoutEnabled = !!me?.billing?.checkoutEnabled;

  const [query, setQuery] = useState("");
  const q = normalizeText(query.trim());

  // Catálogo declarativo de grupos → items. El `node` es la MISMA sección de antes.
  // "Mi cuenta" NO va aquí: es del USUARIO (no configuración del negocio) → se
  // renderiza como bloque fijo arriba del acordeón.
  const groups = [
    { id: "negocio", title: "Negocio y marca", icon: Building2, items: [
      { key: "branding", title: "Marca", kw: "marca logo color nombre negocio branding", show: isAdmin && !!me,
        node: <BrandingSection me={me} /> },
      { key: "theme", title: "Tema del espacio", kw: "tema apariencia colores workspace", show: isAdmin && !!me,
        node: <WorkspaceThemeSection me={me} /> },
      { key: "currency", title: "Moneda de visualizacion", kw: "moneda visualizacion divisa currency", show: isAdmin && !!me,
        node: <DisplayCurrencyPreferenceSection me={me} /> },
      { key: "plan", title: "Tu plan", kw: "plan suscripcion modulos precio billing", show: isAdmin && !!me,
        node: <YourPlanSection me={me} /> },
      // Sucursales vive aquí (era un grupo de 1 item = puro overhead): es parte
      // del alta del negocio. Solo verticales con POS físico (gate intacto).
      { key: "branches", title: "Sucursales", kw: "sucursales branches multisucursal sedes ubicaciones", show: isAdmin && isPos,
        node: <BranchesSection /> },
    ] },
    { id: "operacion", title: "Operacion", icon: Layers, items: [
      { key: "activityTags", title: "Tipos de actividad", kw: "tipos actividad horas registro facturable flow crm etiquetas", show: isAdmin && isCrm && !!tags,
        node: (
          <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
            <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-6 py-5 md:px-8">
              <div>
                <h3 className="font-serif text-lg tracking-tight text-slate-900">Tipos de actividad</h3>
                <p className="text-sm text-slate-500">Catalogo que aparece al registrar horas</p>
              </div>
              <button onClick={onNewTag}
                className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
                <Plus className="h-3.5 w-3.5" /> Nuevo tipo
              </button>
            </div>
            <ul className="divide-y divide-slate-100">
              {tags.map(t => (
                <li key={t.id} className="flex items-center justify-between gap-4 px-6 py-3">
                  <span className="inline-flex items-center gap-3">
                    <span className="h-3 w-3 rounded-full" style={{ background: t.color }} />
                    <span className={`text-sm font-medium ${t.isActive === false ? "text-slate-400 line-through" : "text-slate-900"}`}>{t.name}</span>
                    {t.isBillableDefault && <span className="text-[10px] uppercase tracking-wider text-slate-400">facturable</span>}
                  </span>
                  <span className="flex items-center gap-1">
                    <button onClick={() => onEditTag(t)}
                      className="rounded-lg px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100">
                      Editar
                    </button>
                    <button onClick={() => onToggleTag(t)}
                      className={`rounded-lg px-2.5 py-1 text-xs font-medium hover:bg-slate-100 ${t.isActive === false ? "text-emerald-700" : "text-rose-700"}`}>
                      {t.isActive === false ? "Reactivar" : "Desactivar"}
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) },
      { key: "salesStages", title: "Etapas del pipeline", kw: "etapas pipeline ventas flow crm", show: isAdmin && isCrm,
        node: <SalesStagesSection /> },
      { key: "exchangeRates", title: "Tasas de cambio", kw: "tasas cambio divisa tipo de cambio flow crm", show: isAdmin && isCrm,
        node: <ExchangeRatesSection displayCurrency={displayCurrency} setDisplayCurrency={setDisplayCurrency} /> },
      { key: "bookingChannel", title: "Reservas en linea", kw: "reservas en linea canal publico link qr booking", show: isAdmin && isBooking,
        node: <BookingChannelSection /> },
      { key: "bookingNotify", title: "Contacto de avisos", kw: "avisos contacto reserva cancelacion reagenda notificaciones", show: isAdmin && isBooking,
        node: <BookingNotifyContactCard /> },
      // B1 (PR2b) — Tienda en línea: encender pedidos (gate aviso) + liga/QR pública.
      // Solo verticales con POS; el backend además gatea por módulo. La card se
      // auto-oculta si el backend no responde (feature dormida).
      { key: "storeChannel", title: "Tienda en linea", kw: "tienda en linea catalogo pedidos online link qr liga publica store recoger sucursal", show: isAdmin && isPos,
        node: <StoreAdminCard /> },
      // V205 — Fidelidad §29: sellos por visita + cortesía $0 + "lo de siempre" (gate
      // aviso con finalidad fidelidad). Solo verticales con POS; SUPER_USER en backend.
      { key: "loyalty", title: "Programa de lealtad", kw: "fidelidad lealtad sellos visitas cartilla cortesia recompensa qr pdf lo de siempre miembros", show: isAdmin && isPos,
        node: <LoyaltyAdminCard /> },
      // §31.4 (V130) — recordatorio de la mecánica de "Productos sugeridos". Se pone
      // aquí (Configuración) y en el Cierre de día — NO como aviso permanente en la
      // pantalla de cobro (un aviso fijo se vuelve invisible en dos días).
      { key: "suggestedProducts", title: "Productos sugeridos (POS)", kw: "sugeridos upsell postre bebida extra pos punto de venta gastro", show: isAdmin && isGastro,
        node: (
          <div className="rounded-xl border border-slate-200 p-5">
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">Productos sugeridos</p>
            <p className="mt-2 text-sm text-slate-700">
              Marca un producto como <strong>Sugerido</strong> en <strong>Productos</strong> (editor del producto) y aparecerá en la franja <strong>Sugeridos</strong> del punto de venta para hacer upsell (postre, bebida, extra) durante la comanda.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              La franja muestra <strong>máximo 4</strong> productos y <strong>oculta</strong> los que no tengan stock o insumos suficientes en la sucursal que vende. Sin productos marcados, el POS se ve igual que hoy.
            </p>
          </div>
        ) },
      // §31.2 (V131) — umbral del semáforo de comandas (gastro-only).
      { key: "kitchenAlert", title: "Semaforo de cocina", kw: "cocina comanda semaforo tiempo umbral platillo urgencia gastro", show: isAdmin && isGastro,
        node: <KitchenAlertSection me={me} /> },
    ] },
    { id: "facturacion", title: "Facturacion (CFDI)", icon: Receipt, items: [
      // PR A (V204) — widget glanceable de la bolsa de folios (primero del grupo).
      // Se auto-oculta si el medidor no trae dato (no-SUPER_USER / endpoint dormido).
      { key: "folios", title: "Folios de este periodo", kw: "folios bolsa cuota cfdi timbres limite renovacion consumo", show: isAdmin && checkoutEnabled,
        node: <FoliosUsageMeter compact /> },
      // Datos fiscales se oculta si el billing esta apagado (checkoutEnabled).
      { key: "fiscalData", title: "Datos fiscales", kw: "datos fiscales rfc regimen csd receptor cfdi factura", show: isAdmin && checkoutEnabled,
        node: <FiscalDataCard /> },
      // Emisor CFDI (Capa 2) depende de CFDI_ISSUER_ENABLED (async) → el conteo
      // por DOM lo oculta si el backend lo tiene dormido.
      { key: "issuer", title: "Emisor CFDI", kw: "emisor cfdi capa 2 facturar clientes csd sellos series folios", show: isAdmin,
        node: <IssuerConfigCard /> },
    ] },
    { id: "pagos", title: "Cobros y pagos", icon: CreditCard, items: [
      { key: "paymentMethod", title: "Metodo de pago", kw: "metodo de pago tarjeta stripe recuperacion cobro suscripcion", show: isAdmin && checkoutEnabled,
        node: <PaymentMethodSection /> },
      // Cobros en línea (Pagos): solo verticales con POS (gastro/gym/boutique),
      // NO Flow. El componente además se auto-gatea por vertical (defensa en profundidad).
      { key: "paymentsConnect", title: "Cobros en linea", kw: "cobros en linea stripe connect pagos", show: isAdmin && isPos,
        node: <PaymentsConnectCard /> },
      { key: "cashRounding", title: "Redondeo de efectivo", kw: "redondeo efectivo cambio caja pos", show: isAdmin && isPos,
        node: <CashRoundingSection me={me} /> },
      // L-14 — los mismos conjuntos que la pantalla de suspensión (30 días), con la cuenta activa.
      { key: "exportData", title: "Exportar mis datos", kw: "exportar descargar datos csv excel respaldo clientes ventas productos reservas cfdi zip", show: isAdmin,
        node: <ExportDataSection /> },
      { key: "referral", title: "Refiere y gana", kw: "refiere gana referido descuento codigo", show: isAdmin,
        node: <ReferralCard /> },
    ] },
    { id: "usuarios", title: "Usuarios, acceso y legal", icon: Users, items: [
      // V221 — Recursos Humanos (users:create sin SUPER_USER) también ve Usuarios; las acciones sobre
      // administradores siguen ocultas (canAdmin = titular) y el backend las rechaza.
      { key: "users", title: "Usuarios", kw: "usuarios equipo roles permisos cajero admin", show: isAdmin || hasPermission(user, "users:create"),
        node: <UsersManagementSection user={user} /> },
      // PINK #1 (P1) - PIN de piso + acceso de la tablet compartida (solo POS).
      { key: "floorPins", title: "Piso y PINs (tablet)", kw: "pin piso mesero tablet compartida sesion atribucion", show: isAdmin && isPos,
        node: <FloorAdminSection /> },
      { key: "sessionIdle", title: "Ventana de sesion", kw: "sesion inactividad tiempo cierre automatico seguridad", show: isAdmin && !!me,
        node: <SessionIdleSection me={me} /> },
      { key: "roleToggle", title: "Rol de sesion (debug)", kw: "rol sesion debug", show: isRoleToggleEnabled(),
        node: <SessionRoleToggle user={user} onToggleRole={onToggleRole} /> },
      { key: "dataPolicy", title: "Politica de datos", kw: "politica datos privacidad enmascarado audit logs", show: true,
        node: (
          <div className="rounded-xl border border-slate-200 p-5">
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">Politica de datos</p>
            <p className="mt-2 text-sm text-slate-700">Datos sensibles enmascarados para roles no administrativos. Toda revelacion queda registrada en <code className="font-mono text-xs">audit_logs</code>.</p>
          </div>
        ) },
      { key: "legal", title: "Documentos legales", kw: "legal terminos condiciones aviso privacidad documentos", show: true,
        node: <LegalSettings /> },
      // §1bis.5 — las novedades ya no se ofrecen en el alta: se deciden aquí (cada usuario).
      { key: "marketingConsent", title: "Novedades de FluidSol", kw: "novedades correo comunicaciones marketing boletin preferencias", show: true,
        node: <MarketingConsentCard /> },
      // Legales v3.1 — plantilla de aviso de privacidad del tenant (se auto-oculta
      // si no aplica ninguna: sin CFDI ni lealtad). Gate real dentro del componente.
      { key: "tenantPrivacyTemplate", title: "Aviso de privacidad para tus clientes", kw: "plantilla aviso privacidad clientes cfdi lealtad fidelidad descarga", show: isAdmin,
        node: <TenantPrivacyTemplateCard /> },
    ] },
    // V146/V147 — Impresión: impresoras (servidor, roles, prueba) + personalización del
    // ticket (logo mono, textos, QR). Solo verticales con POS físico (gastro/boutique/gym).
    { id: "impresion", title: "Impresión y tickets", icon: Printer, items: [
      { key: "printers", title: "Impresoras", kw: "impresora impresoras ticket comanda qz epos red imprimir termica caja cocina", show: isAdmin && isPos,
        node: <PrintersSettings branchId={me?.branch?.activeBranchId ?? null} /> },
      { key: "ticketCustomization", title: "Personalización del ticket", kw: "ticket logo encabezado pie qr fidelidad personalizar recibo monocromo", show: isAdmin && isPos,
        node: <TicketCustomization branchId={me?.branch?.activeBranchId ?? null} brandName={me?.tenant?.branding?.brandName || me?.tenant?.name || "Mi negocio"} /> },
    ] },
  ];

  // Abrir por default el PRIMER grupo con contenido: "Negocio y marca" para un
  // admin (lo primero que toca un tenant recién dado de alta); para un usuario
  // común, el primero que le aplique. Estable (no depende del buscador).
  const defaultOpenId = groups.find((g) => g.items.some((it) => it.show))?.id;
  const [openMap, toggleGroup] = useSettingsAccordion(me?.userId, defaultOpenId);

  // Aplica gates + buscador. Un grupo sin items visibles se omite (vertical-aware).
  const visibleGroups = groups
    .map((g) => ({
      ...g,
      visibleItems: g.items.filter((it) => it.show
        && (!q || normalizeText(`${it.title} ${it.kw} ${g.title}`).includes(q))),
    }))
    .filter((g) => g.visibleItems.length > 0);

  return (
    <div className="space-y-4">
      {/* Mi cuenta — es del USUARIO, no configuracion del negocio: acceso FIJO
          arriba del acordeon (siempre visible, todos los roles). Sacarlo del
          acordeon elimina un singleton y deja "Negocio y marca" como el primer
          grupo abierto (lo que un tenant recien dado de alta toca primero). */}
      <MyAccountSection user={user} onProfileUpdated={onProfileUpdated} />

      {/* Buscador dentro de Configuracion (§1.5): con muchos toggles, encontrar
          vale mas que la agrupacion. Presentacion pura: filtra que se muestra y
          expande los grupos con coincidencias; no cambia ningun comportamiento. */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar en Configuracion..."
          className="input w-full !pl-9"
          aria-label="Buscar en Configuracion" />
        {query && (
          <button onClick={() => setQuery("")} aria-label="Limpiar busqueda"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {visibleGroups.length === 0 ? (
        <p className="rounded-2xl border border-slate-200 bg-white px-6 py-8 text-center text-sm text-slate-400">
          Sin resultados para "{query}".
        </p>
      ) : (
        visibleGroups.map((g) => (
          <SettingsGroup key={g.id} id={g.id} title={g.title} icon={g.icon}
            open={!!openMap[g.id]} forceOpen={q.length > 0} onToggle={toggleGroup}>
            {g.visibleItems.map((it) => (
              <React.Fragment key={it.key}>{it.node}</React.Fragment>
            ))}
          </SettingsGroup>
        ))
      )}
    </div>
  );
}

/* ===========================================================================
   PipelineView — Kanban de leads con drag-and-drop + forecasting (Día 2)
   Conectado al backend real (/api/v1/leads/*). RLS aplica server-side.
   =========================================================================== */
function fmtMoneyCcy(n, currency = "USD") {
  return new Intl.NumberFormat("es-MX", {
    style: "currency", currency, maximumFractionDigits: 0,
  }).format(Number(n) || 0);
}

function StagePill({ stage }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-full" style={{ background: stage.color }} />
      <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-600">
        {stage.name}
      </span>
    </span>
  );
}

function ForecastHero({ forecast, displayCurrency = "USD", fxRates = [] }) {
  if (!forecast) {
    return (
      <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-6 md:p-7">
        <div className="h-24 animate-pulse rounded-xl bg-slate-100" />
      </div>
    );
  }
  // V8/V9: backend devuelve USD; Day 5 hotfix BUG#4 convierte a la moneda
  // de display del tenant usando fxRates locales. Si no hay rate, fmtUsdInDisplay
  // cae a USD con sufijo "(USD)" para que el admin sepa configurar la tasa.
  const fmt = (v) => fmtUsdInDisplay(v, displayCurrency, fxRates);
  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-6 md:p-7">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Forecast ponderado</p>
          <p className="mt-1 font-serif text-3xl tracking-tight text-slate-900 md:text-4xl tabular-nums">
            {fmt(forecast.weightedForecastUsd)}
          </p>
          <p className="text-xs text-slate-500">Σ (valor × probabilidad / 100) de stages abiertos · {displayCurrency}</p>
        </div>
        <div className="grid grid-cols-3 gap-3 md:gap-4">
          <div className="rounded-xl border border-slate-200/80 px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Pipeline abierto</p>
            <p className="mt-1 font-serif text-lg text-slate-900 tabular-nums">{fmt(forecast.totalPipelineUsd)}</p>
          </div>
          <div className="alert-ok rounded-xl px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-80">Ganados</p>
            <p className="mt-1 font-serif text-lg tabular-nums">{fmt(forecast.wonTotalUsd)}</p>
          </div>
          <div className="alert-over rounded-xl px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-80">Perdidos</p>
            <p className="mt-1 font-serif text-lg tabular-nums">{fmt(forecast.lostTotalUsd)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Sub-header debajo del título del Kanban con las tasas vigentes en uso
 * (data de forecast.ratesUsed). Una línea compacta: "Tasas: USD 1.00 · MXN 17.85 · EUR 0.92".
 * Solo muestra el mes actual para no inflar el header.
 */
function RatesUsedSubheader({ forecast }) {
  if (!forecast?.ratesUsed?.length) return null;
  // Mostramos todas las tasas referenciadas por el pipeline abierto, agrupadas
  // por mes (un lead con expected_close=julio usa la tasa de julio, no la del
  // mes actual). USD se asume implícito y no se lista.
  const byMonth = new Map();
  for (const r of forecast.ratesUsed) {
    if (!byMonth.has(r.month)) byMonth.set(r.month, []);
    byMonth.get(r.month).push(r);
  }
  const months = Array.from(byMonth.keys()).sort();
  return (
    <p className="mt-1 text-[11px] text-slate-400 tabular-nums">
      Tasas en uso:{" "}
      {months.map((m, i) => (
        <span key={m}>
          {i > 0 && " · "}
          <span className="text-slate-500">{fmtMonthShort(m)}</span>{" "}
          {byMonth.get(m).map((r, j) => (
            <span key={j}>
              {j > 0 && " · "}
              <span className="text-slate-600">{r.currency} {Number(r.rate).toFixed(4)}</span>
            </span>
          ))}
        </span>
      ))}
    </p>
  );
}

/**
 * Línea con el equivalente USD + chip de fx_status. Renderiza un comportamiento
 * distinto según el status del lead (FROZEN/CONVERTED/MISSING_RATE/NATIVE_USD).
 */
function FxLine({ fxStatus, valueUsd, fxRate, currency, rateMonth, displayCurrency = "USD", fxRates = [] }) {
  // Para leads nativos en la moneda de display, NO mostramos la línea "≈" porque
  // sería redundante con el monto principal (ej. lead en MXN, display MXN).
  if (fxStatus === "NATIVE_USD" && displayCurrency === "USD") return null;
  if (currency === displayCurrency) return null;

  const month = rateMonth ? new Date(rateMonth).toISOString().slice(0, 7) : null;
  const monthLabel = month ? fmtMonthShort(month + "-01") : "—";

  if (fxStatus === "MISSING_RATE") {
    return (
      <div className="mt-1.5">
        <span className="inline-flex items-center gap-1 rounded-md bg-rose-50 px-2 py-0.5 text-[10px] font-medium text-rose-700 ring-1 ring-inset ring-rose-700/15"
              title={`No hay tasa configurada para ${currency} en ${monthLabel}. Configura en Settings → Tasas de cambio.`}>
          <AlertTriangle className="h-2.5 w-2.5" />
          Falta tasa {currency}/{monthLabel}
        </span>
      </div>
    );
  }

  // Day 5 hotfix BUG#4: línea "≈" en moneda de display (no USD hardcoded).
  const displayLabel = valueUsd != null
    ? fmtUsdInDisplay(valueUsd, displayCurrency, fxRates)
    : "—";
  const rateLabel = fxRate != null ? Number(fxRate).toFixed(4) : "—";

  if (fxStatus === "FROZEN") {
    return (
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <span className="text-[11px] tabular-nums text-slate-500">≈ {displayLabel}</span>
        <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-700/15"
              title={`Tasa congelada al ganar: 1 USD = ${rateLabel} ${currency}`}>
          <Lock className="h-2.5 w-2.5" /> Tasa ganada
        </span>
      </div>
    );
  }

  // CONVERTED (default)
  return (
    <div className="mt-1.5 flex items-center justify-between gap-2">
      <span className="text-[11px] tabular-nums text-slate-500">≈ {displayLabel}</span>
      <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600"
            title={`Tasa ${currency}/${monthLabel}: 1 USD = ${rateLabel} ${currency}`}>
        Tasa {monthLabel}
      </span>
    </div>
  );
}

function fmtMonthShort(yyyymmdd) {
  if (!yyyymmdd) return "—";
  const [y, m] = yyyymmdd.split("-");
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, 1));
  return date.toLocaleDateString("es-MX", { month: "short", year: "numeric", timeZone: "UTC" })
             .replace(".", "");
}

function PipelineView({ isAdmin, contacts = [], displayCurrency = "USD", fxRates = [] }) {
  const [stages, setStages] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dropStage, setDropStage] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const refreshForecast = async () => {
    try {
      const fc = await api("/leads/forecast");
      setForecast(fc);
    } catch (_) { /* swallow */ }
  };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [pipe, fc] = await Promise.all([
        api<{ stages: any[] }>("/leads/pipeline"),
        api<any>("/leads/forecast"),
      ]);
      setStages(pipe.stages ?? []);
      setForecast(fc);
    } catch (e) {
      setError(e?.body?.message || e?.message || "No se pudo cargar el pipeline");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onDragStart = (leadId) => (e) => {
    setDragId(leadId);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", leadId); } catch (_) {}
  };
  const onDragOver = (stageId) => (e) => { e.preventDefault(); setDropStage(stageId); };

  const onDrop = (stageId) => async (e) => {
    e.preventDefault();
    const id = dragId || e.dataTransfer.getData("text/plain");
    setDragId(null); setDropStage(null);
    if (!id) return;

    const target = stages.find(s => s.id === stageId);
    if (!target) return;

    // Optimistic update: muevo lead a la columna destino y aplico probability default.
    const prev = stages;
    let moved = null;
    const next = stages.map(s => ({
      ...s,
      leads: s.leads.filter(l => {
        if (l.id === id) { moved = l; return false; }
        return true;
      }),
    }));
    if (!moved) return;
    const updated = { ...moved, stageId, probability: target.defaultProbability };
    next.find(s => s.id === stageId).leads = [updated, ...next.find(s => s.id === stageId).leads];
    setStages(next);

    try {
      await api(`/leads/${id}/stage`, { method: "PATCH", body: { stageId } });
      refreshForecast(); // totales cambiaron porque probability cambió
    } catch (err) {
      setStages(prev); // rollback
      setError(err?.body?.message || "No se pudo mover el lead — se restauró la posición anterior");
    }
  };

  const onCreated = async () => {
    setNewOpen(false);
    await load();
  };

  const onWin = async (id) => {
    const prev = stages;
    try {
      await api(`/leads/${id}/win`, { method: "POST" });
      await load();
    } catch (err) {
      setStages(prev);
      setError(err?.body?.message || "No se pudo cerrar como ganado");
    }
  };
  const onLose = async (id) => {
    const prev = stages;
    try {
      await api(`/leads/${id}/lose`, { method: "POST" });
      await load();
    } catch (err) {
      setStages(prev);
      setError(err?.body?.message || "No se pudo cerrar como perdido");
    }
  };

  return (
    <div className="space-y-7">
      <ForecastHero forecast={forecast} displayCurrency={displayCurrency} fxRates={fxRates} />

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex-1">{error}</div>
            <button onClick={() => setError(null)} className="text-rose-700 hover:text-rose-900">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <section>
        <div className="mb-4 flex items-baseline justify-between">
          <div>
            <h3 className="font-serif text-xl tracking-tight text-slate-900">Pipeline Kanban</h3>
            <p className="text-sm text-slate-500">Arrastra un lead entre columnas para cambiar su etapa — la probabilidad se ajusta al default del stage destino</p>
            <RatesUsedSubheader forecast={forecast} />
          </div>
          <button onClick={() => setNewOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--brand-primary)] px-4 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
            <Plus className="h-3.5 w-3.5" /> Nuevo lead
          </button>
        </div>

        {/* Banner de tasas faltantes — V8/V9 */}
        {forecast?.warnings?.length > 0 && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
              <div className="flex-1">
                <p className="font-medium">
                  {forecast.warnings.reduce((a, w) => a + w.leadsAffected, 0)} {" "}
                  {forecast.warnings.reduce((a, w) => a + w.leadsAffected, 0) === 1 ? "lead" : "leads"} sin tasa de cambio configurada:
                </p>
                <ul className="mt-1 space-y-0.5 text-[12px]">
                  {forecast.warnings.map((w, i) => (
                    <li key={i}>
                      <strong>{w.currency}/{w.month}</strong> — {w.leadsAffected} {w.leadsAffected === 1 ? "lead" : "leads"}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-[12px] opacity-80">
                  Configura las tasas faltantes en <strong>Configuración → Tasas de cambio</strong>.
                </p>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="h-64 animate-pulse rounded-2xl bg-slate-100" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7">
            {stages.map(stage => {
              // V8/V9: cada lead trae valueUsd / fxStatus desde el backend.
              // Los totales de columna se calculan sumando valueUsd (ya en USD).
              const totalUsd = stage.leads.reduce(
                (a, l) => a + (Number(l.valueUsd) || 0), 0,
              );
              const weightedUsd = stage.leads.reduce(
                (a, l) => a + (Number(l.valueUsd) || 0) * (Number(l.probability) || 0) / 100,
                0,
              );
              return (
                <div key={stage.id}
                     className="kanban-col rounded-2xl border border-slate-200/80 bg-slate-50/60 p-3 transition"
                     data-drop-active={dropStage === stage.id ? "true" : "false"}
                     onDragOver={onDragOver(stage.id)}
                     onDragLeave={() => setDropStage(null)}
                     onDrop={onDrop(stage.id)}>
                  <div className="flex items-center justify-between px-2 pb-3">
                    <StagePill stage={stage} />
                    <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 ring-1 ring-inset ring-slate-200 tabular-nums">
                      {stage.leads.length}
                    </span>
                  </div>
                  <p className="px-2 pb-1 text-[10px] uppercase tracking-[0.18em] text-slate-400 tabular-nums">
                    {fmtUsdInDisplay(totalUsd, displayCurrency, fxRates)}
                  </p>
                  <p className="px-2 pb-2 text-[10px] tabular-nums text-slate-400">
                    Ponderado {fmtUsdInDisplay(weightedUsd, displayCurrency, fxRates)} · prob. {stage.defaultProbability}%
                  </p>
                  <ul className="space-y-2.5">
                    {stage.leads.map(l => {
                      const isDragging = dragId === l.id;
                      const lccy = l.currency || "USD";
                      const contact = contacts.find(c => c.id === l.contactId);
                      const fxStatus = l.fxStatus || "NATIVE_USD";
                      const valueUsd = l.valueUsd != null ? Number(l.valueUsd) : null;
                      const rateMonth = l.expectedCloseDate || l.createdAt;
                      return (
                        <li key={l.id}
                            draggable
                            onDragStart={onDragStart(l.id)}
                            onDragEnd={() => { setDragId(null); setDropStage(null); }}
                            className={`kanban-card card-soft card-soft-hover cursor-grab active:cursor-grabbing rounded-xl border border-slate-200/80 bg-white p-3.5 transition ${isDragging ? "dragging" : ""}`}>
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-[13.5px] font-medium leading-snug text-slate-900">{l.title}</p>
                            <span className="text-slate-300"><GripVertical className="h-3.5 w-3.5" /></span>
                          </div>
                          {contact && (
                            <p className="mt-1 text-[11px] text-slate-500 truncate">
                              {contact.name}{contact.company ? ` · ${contact.company}` : ""}
                            </p>
                          )}
                          {/* Valor nativo (grande) */}
                          <div className="mt-3 flex items-baseline justify-between gap-2">
                            <span className="font-serif text-base tabular-nums text-slate-900">
                              {fmtMoneyCcy(l.estimatedValue, lccy)}
                            </span>
                            <span className="inline-flex items-center gap-1 text-[11px] tabular-nums text-slate-500">
                              <TrendingUp className="h-3 w-3" /> {l.probability ?? 0}%
                            </span>
                          </div>
                          {/* Display ccy convertido + fx_status chip — Day 5 hotfix BUG#4 */}
                          <FxLine
                            fxStatus={fxStatus}
                            valueUsd={valueUsd}
                            fxRate={l.fxRateUsed}
                            currency={lccy}
                            rateMonth={rateMonth}
                            displayCurrency={displayCurrency}
                            fxRates={fxRates}
                          />
                          {l.expectedCloseDate && (
                            <p className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-slate-400 tabular-nums">
                              <Calendar className="h-3 w-3" /> {fmtDate(l.expectedCloseDate)}
                            </p>
                          )}
                          {!stage.isWon && !stage.isLost && (
                            // flex-wrap + min-w en cada botón: en columnas angostas
                            // (2xl:grid-cols-7) los botones se apilan en vez de
                            // desbordar la tarjeta. min-w-0 deja que encojan.
                            <div className="mt-3 flex flex-wrap gap-1.5 border-t border-slate-100 pt-2">
                              <button onClick={() => onWin(l.id)}
                                title="Marcar como ganado"
                                className="inline-flex min-w-[4.5rem] flex-1 items-center justify-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-[10px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-700/15 hover:bg-emerald-100">
                                <Trophy className="h-3 w-3 shrink-0" /> Ganar
                              </button>
                              <button onClick={() => onLose(l.id)}
                                title="Marcar como perdido"
                                className="inline-flex min-w-[4.5rem] flex-1 items-center justify-center gap-1 rounded-md bg-rose-50 px-2 py-1 text-[10px] font-medium text-rose-700 ring-1 ring-inset ring-rose-700/15 hover:bg-rose-100">
                                <XCircle className="h-3 w-3 shrink-0" /> Perder
                              </button>
                            </div>
                          )}
                        </li>
                      );
                    })}
                    {stage.leads.length === 0 && (
                      <li className="rounded-xl border border-dashed border-slate-200 px-3 py-6 text-center text-[11px] text-slate-400">
                        Sin leads
                      </li>
                    )}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <NewLeadModal open={newOpen} onClose={() => setNewOpen(false)}
                    stages={stages} contacts={contacts} onCreated={onCreated} />
    </div>
  );
}

/* ===========================================================================
   NewLeadModal — formulario mínimo para crear un lead en el pipeline
   =========================================================================== */
function NewLeadModal({ open, onClose, stages, contacts, onCreated }) {
  const firstOpenStage = stages.find(s => !s.isWon && !s.isLost) || stages[0];
  const [form, setForm] = useState({
    title: "", contactId: "", stageId: "", estimatedValue: "",
    currency: "USD", probability: "", expectedCloseDate: "", source: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({
        title: "", contactId: "",
        stageId: firstOpenStage?.id ?? "",
        estimatedValue: "",
        currency: "USD",
        probability: firstOpenStage ? String(firstOpenStage.defaultProbability) : "",
        expectedCloseDate: "",
        source: "",
      });
      setErr(null);
    }
  }, [open, firstOpenStage?.id]);

  const onChange = e => {
    const { name, value } = e.target;
    setForm(f => {
      const next = { ...f, [name]: value };
      // Si cambia el stage, sugerimos la probability del default.
      if (name === "stageId") {
        const s = stages.find(x => x.id === value);
        if (s) next.probability = String(s.defaultProbability);
      }
      return next;
    });
  };

  const submit = async e => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSubmitting(true); setErr(null);
    try {
      const body = {
        title: form.title.trim(),
        stageId: form.stageId || undefined,
        contactId: form.contactId || undefined,
        estimatedValue: form.estimatedValue || undefined,
        currency: form.currency || undefined,
        probability: form.probability !== "" ? Number(form.probability) : undefined,
        expectedCloseDate: form.expectedCloseDate || undefined,
        source: form.source || undefined,
      };
      await api("/leads", { method: "POST", body });
      onCreated();
    } catch (e2) {
      setErr(e2?.body?.message || e2?.message || "No se pudo crear el lead");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}
           title="Nuevo lead" subtitle="Da de alta una oportunidad en el pipeline">
      <form onSubmit={submit} className="space-y-5">
        <Field label="Título" required>
          <input className="input" name="title" value={form.title} onChange={onChange}
                 placeholder="Migración ERP — Industrias del Norte" required />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Etapa">
            <select className="input" name="stageId" value={form.stageId} onChange={onChange}>
              {stages.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Contacto">
            <select className="input" name="contactId" value={form.contactId} onChange={onChange}>
              <option value="">— sin contacto —</option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Valor estimado">
            <input className="input tabular-nums" name="estimatedValue" type="number" step="0.01" min="0"
                   value={form.estimatedValue} onChange={onChange} placeholder="125000" />
          </Field>
          <Field label="Moneda">
            <select className="input" name="currency" value={form.currency} onChange={onChange}>
              <option value="USD">USD</option>
              <option value="MXN">MXN</option>
              <option value="BRL">BRL</option>
              <option value="EUR">EUR</option>
            </select>
          </Field>
          <Field label="Probabilidad %">
            <input className="input tabular-nums" name="probability" type="number" min="0" max="100"
                   value={form.probability} onChange={onChange} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Cierre esperado">
            <input className="input" name="expectedCloseDate" type="date"
                   value={form.expectedCloseDate} onChange={onChange} />
          </Field>
          <Field label="Origen">
            <input className="input" name="source" value={form.source} onChange={onChange}
                   placeholder="inbound-web, referido, evento..." />
          </Field>
        </div>
        {err && (
          <div className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</div>
        )}
        <div className="flex justify-end gap-3 border-t border-slate-100 pt-5">
          <button type="button" onClick={onClose} disabled={submitting}
                  className="rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50">
            Cancelar
          </button>
          <button type="submit" disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-xl bg-[var(--brand-primary)] px-5 py-2.5 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
            <Plus className="h-4 w-4" /> {submitting ? "Creando…" : "Crear lead"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ===========================================================================
   QuotesView (V10 / GAP 4) — listado + filtros + modales de creación/detalle
   =========================================================================== */
const QUOTE_STATUS_LABEL = {
  DRAFT:    "Borrador",
  SENT:     "Enviada",
  ACCEPTED: "Aceptada",
  REJECTED: "Rechazada",
  EXPIRED:  "Expirada",
};
const QUOTE_STATUS_STYLES = {
  DRAFT:    "bg-slate-100 text-slate-600 ring-slate-500/10",
  SENT:     "bg-sky-50 text-sky-700 ring-sky-700/15",
  ACCEPTED: "bg-emerald-50 text-emerald-700 ring-emerald-700/15",
  REJECTED: "bg-rose-50 text-rose-800 ring-rose-700/15",
  EXPIRED:  "bg-amber-50 text-amber-800 ring-amber-700/15",
};
function QuoteStatusChip({ status }) {
  const label = QUOTE_STATUS_LABEL[status] ?? status;
  const cls = QUOTE_STATUS_STYLES[status] ?? QUOTE_STATUS_STYLES.DRAFT;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wide ring-1 ring-inset ${cls}`}>
      {label}
    </span>
  );
}

/**
 * Descarga el PDF de una cotización vía fetch autenticado y lo abre en una
 * pestaña nueva. NO usamos <a href> porque el endpoint exige Bearer token.
 */
async function downloadQuotePdf(quoteId, code) {
  const res = await fetch(`${API_BASE}/quotes/${quoteId}/pdf`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j?.message || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  // Liberamos el blob después de un rato — el navegador ya tiene el PDF cargado.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  if (!win) {
    // Fallback si el popup fue bloqueado: descarga directa con el nombre del folio.
    const a = document.createElement("a");
    a.href = url; a.download = `${code || "quote"}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
  }
}

function QuotesView({ user, isAdmin, displayCurrency = "USD", fxRates = [], onOpenProject }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  // Datos secundarios para el formulario de "Nueva cotización":
  // necesitamos leads + end_clients para el dropdown de destinatario.
  const [leads, setLeads] = useState([]);
  const [endClients, setEndClients] = useState([]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "ALL") params.set("status", statusFilter);
      const path = `/quotes${params.toString() ? `?${params.toString()}` : ""}`;
      const list = await api(path);
      setQuotes(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.body?.message || e?.message || "No se pudieron cargar las cotizaciones");
    } finally {
      setLoading(false);
    }
  };

  const loadTargets = async () => {
    try {
      const [ls, ecs] = await Promise.all([
        api("/leads").catch(() => []),
        api("/end-clients").catch(() => []),
      ]);
      setLeads(Array.isArray(ls) ? ls : []);
      setEndClients(Array.isArray(ecs) ? ecs : []);
    } catch (_) { /* silent — no bloquea la lista */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { loadTargets(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return quotes;
    const q = search.toLowerCase();
    return quotes.filter(qt =>
      (qt.code || "").toLowerCase().includes(q) ||
      (qt.title || "").toLowerCase().includes(q) ||
      (qt.target_name || "").toLowerCase().includes(q)
    );
  }, [quotes, search]);

  const canCreate = hasPermission(user, "quotes:create");

  return (
    <div className="space-y-6">
      {/* Header + acciones */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h3 className="font-serif text-xl tracking-tight text-slate-900">Cotizaciones comerciales</h3>
          <p className="text-sm text-slate-500">
            Genera, envía y firma cotizaciones (NO facturación CFDI).
            Folio automático por tenant — IVA 16% MX configurable.
          </p>
        </div>
        {canCreate && (
          <button onClick={() => setNewOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--brand-primary)] px-4 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
            <Plus className="h-3.5 w-3.5" /> Nueva cotización
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Bloque 5 — overflow-x-auto: la barra de estados (6 pestañas) se cortaba en
            móvil y no se llegaba a las últimas; ahora scrollea horizontal. */}
        <div className="inline-flex max-w-full gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1">
          {[
            { id: "ALL",      label: "Todas" },
            { id: "DRAFT",    label: "Borrador" },
            { id: "SENT",     label: "Enviadas" },
            { id: "ACCEPTED", label: "Aceptadas" },
            { id: "REJECTED", label: "Rechazadas" },
            { id: "EXPIRED",  label: "Expiradas" },
          ].map(f => (
            <button key={f.id}
              onClick={() => setStatusFilter(f.id)}
              className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-[11px] font-medium transition ${
                statusFilter === f.id
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:bg-slate-50"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar folio, título o cliente…"
            className="w-72 rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/5" />
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex-1">{error}</div>
            <button onClick={() => setError(null)} className="text-rose-700 hover:text-rose-900">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Tabla */}
      {loading ? (
        <div className="space-y-2">
          {[0,1,2,3].map(i => <div key={i} className="h-12 animate-pulse rounded-xl bg-slate-100" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 px-6 py-16 text-center">
          <FileText className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">
            {quotes.length === 0
              ? "Aún no hay cotizaciones. Crea la primera para empezar."
              : "Ninguna cotización coincide con los filtros."}
          </p>
        </div>
      ) : (
        <div className="card-soft overflow-x-auto rounded-2xl border border-slate-200/80 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50/60 text-[11px] uppercase tracking-[0.12em] text-slate-500">
              <tr>
                <th className="px-5 py-3 text-left font-medium">Folio</th>
                <th className="px-5 py-3 text-left font-medium">Título</th>
                <th className="px-5 py-3 text-left font-medium">Cliente</th>
                <th className="px-5 py-3 text-right font-medium">Total</th>
                <th className="px-5 py-3 text-left font-medium">Estado</th>
                <th className="px-5 py-3 text-left font-medium">Vigencia</th>
                <th className="px-5 py-3 text-right font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(qt => {
                const display = qt.display_status || qt.status;
                const isExpiredVirtual = display === "EXPIRED" && qt.status === "SENT";
                return (
                  <tr key={qt.id}
                      onClick={() => setDetailId(qt.id)}
                      className="cursor-pointer transition hover:bg-slate-50/60">
                    <td className="px-5 py-3.5 font-mono text-[12px] text-slate-900">{qt.code}</td>
                    <td className="px-5 py-3.5">
                      <p className="line-clamp-1 text-slate-800">{qt.title}</p>
                    </td>
                    <td className="px-5 py-3.5 text-slate-600">{qt.target_name || "—"}</td>
                    <td className="px-5 py-3.5 text-right tabular-nums text-slate-900">
                      {fmtMoneyCcy(Number(qt.total_amount), (qt.currency || "USD").trim())}
                      {/* Day 5 hotfix BUG#4: línea ≈ en displayCurrency. Solo mostrar si
                          la moneda nativa de la quote difiere del displayCurrency. */}
                      {qt.total_usd_snapshot != null && (qt.currency || "").trim() !== displayCurrency && (
                        <p className="text-[10px] text-slate-400">
                          ≈ {fmtUsdInDisplay(Number(qt.total_usd_snapshot), displayCurrency, fxRates)}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <QuoteStatusChip status={display} />
                      {isExpiredVirtual && (
                        <span className="ml-1 text-[10px] text-amber-700/70">(virtual)</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-slate-600 tabular-nums">
                      {qt.valid_until ? fmtDate(qt.valid_until) : "—"}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <button onClick={(e) => { e.stopPropagation(); setDetailId(qt.id); }}
                        className="rounded-lg px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-100">
                        Ver detalle <ChevronRight className="ml-0.5 inline h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <NewQuoteModal open={newOpen} onClose={() => setNewOpen(false)}
                     /* #2: al crear, abre el detalle → editar/enviar desde el primer momento */
                     leads={leads} endClients={endClients}
                     onCreated={(newId) => { setNewOpen(false); load(); if (newId) setDetailId(newId); }} />

      <QuoteDetailModal id={detailId} onClose={() => setDetailId(null)}
                        onChanged={() => load()}
                        user={user} isAdmin={isAdmin} onOpenProject={onOpenProject} />
    </div>
  );
}

/* ===========================================================================
   NewQuoteModal — wizard 3 pasos: target → detalles → líneas
   =========================================================================== */
// --- Matemática de dinero EXACTA (decimal.js), MISMAS reglas que el trigger SQL
// (V139): line_total = qty*unit - descLinea; global sobre subtotal; escala 4,
// half-up. Así el preview == el definitivo del backend (por eso NO hay nota de
// "puede diferir"). El % es solo forma de CAPTURA: se resuelve a monto aquí y se
// manda el monto; el prorrateo mayor-resto al timbrar sigue siendo del backend (F1/F3).
const D = (x) => { try { return new Decimal(x === "" || x == null ? 0 : x); } catch { return new Decimal(0); } };
const round2 = (d) => d.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
function discAmount(mode, val, base) {
  return mode === "pct" ? base.mul(D(val)).div(100) : D(val);
}
function lineGross(l) { return D(l.quantity).mul(D(l.unitPrice)); }
function lineDiscAmt(l) { return Decimal.min(discAmount(l.discMode || "amount", l.disc ?? "0", lineGross(l)), lineGross(l)); }
function lineNetD(l) { return Decimal.max(0, lineGross(l).minus(lineDiscAmt(l))); }
// Para el POST/PATCH: descuento de línea resuelto a monto (string, 4 dec).
function resolvedLineDiscount(l) { return lineDiscAmt(l).toDecimalPlaces(4, Decimal.ROUND_HALF_UP).toFixed(4); }
// Descuento global resuelto a monto (string, 4 dec) desde el subtotal de las líneas.
function resolvedGlobalDiscount(lines, mode, val) {
  const subtotal = lines.reduce((a, l) => a.plus(lineNetD(l)), new Decimal(0));
  return Decimal.min(discAmount(mode || "amount", val, subtotal), subtotal).toDecimalPlaces(4, Decimal.ROUND_HALF_UP).toFixed(4);
}

// Input numérico SIN spinners (type=text + inputmode=decimal), alineado a la derecha.
function DecimalInput({ value, onChange, placeholder = "", suffix = "" }) {
  return (
    <div className="relative">
      <input type="text" inputMode="decimal"
        className={`input tabular-nums text-right ${suffix ? "pr-6" : ""}`}
        value={value ?? ""} placeholder={placeholder}
        onChange={e => onChange(e.target.value.replace(/[^0-9.]/g, ""))} />
      {suffix && <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-slate-400">{suffix}</span>}
    </div>
  );
}
// Money: como DecimalInput + FORMATO de moneda al perder el foco (1500 -> 1,500.00).
function MoneyInput({ value, onChange, placeholder }) {
  const [focused, setFocused] = useState(false);
  const fmt = (v) => { const n = Number(v); return v !== "" && v != null && Number.isFinite(n) ? n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ""; };
  return (
    <input type="text" inputMode="decimal"
      className="input tabular-nums text-right" placeholder={placeholder}
      value={focused ? (value ?? "") : fmt(value)}
      onFocus={() => setFocused(true)}
      onChange={e => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
      onBlur={() => setFocused(false)} />
  );
}
// Celda de descuento: toggle $/% + input + la OTRA cara siempre visible.
function DiscountCell({ mode, val, base, onMode, onVal, currency }) {
  const amt = discAmount(mode || "amount", val, base);
  const pct = base.gt(0) ? amt.div(base).mul(100) : new Decimal(0);
  return (
    <div>
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => onMode(mode === "pct" ? "amount" : "pct")}
          className="w-7 shrink-0 rounded-lg border border-slate-200 bg-white py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50" title="Cambiar $/%">
          {mode === "pct" ? "%" : "$"}
        </button>
        <div className="flex-1">
          {mode === "pct"
            ? <DecimalInput value={val} onChange={onVal} suffix="%" placeholder="0" />
            : <MoneyInput value={val} onChange={onVal} placeholder="0.00" />}
        </div>
      </div>
      <div className="mt-0.5 text-right text-[10px] text-slate-400">
        {mode === "pct" ? `= ${fmtMoneyCcy(amt.toNumber(), currency)}` : `= ${round2(pct).toString()}%`}
      </div>
    </div>
  );
}

// Editor de líneas + descuentos + totales COMPARTIDO por crear y editar (un solo
// componente). Tabla en tablet/escritorio, TARJETAS apiladas en móvil. Unidad B3 +
// descuentos $/% B5 (línea y global). Preview EXACTO (decimal.js) = definitivo backend.
function QuoteLinesEditor({ lines, setLine, addLine, rmLine, currency, taxRate, globalDiscMode, globalDisc, setGlobalDiscMode, setGlobalDisc }) {
  const subtotal = lines.reduce((a, l) => a.plus(lineNetD(l)), new Decimal(0));
  const gd = Decimal.min(discAmount(globalDiscMode || "amount", globalDisc, subtotal), subtotal);
  const gpct = subtotal.gt(0) ? gd.div(subtotal).mul(100) : new Decimal(0);
  const prorated = (l) => subtotal.gt(0) ? gd.mul(lineNetD(l).div(subtotal)) : new Decimal(0);
  const taxable = subtotal.minus(gd);
  const taxAmount = round2(taxable.mul(D(taxRate)));
  const total = round2(taxable).plus(taxAmount);
  const unitLabel = (v) => (QUOTE_SALE_UNITS.find(u => u.v === v)?.l) || v;

  return (
    <div className="space-y-4">
      {/* Escritorio / tablet: tabla (anchos proporcionales, descripción cede espacio) */}
      <div className="hidden overflow-hidden rounded-xl border border-slate-200 md:block">
        <table className="w-full table-fixed text-sm">
          <colgroup>
            <col /><col className="w-[104px]" /><col className="w-[92px]" />
            <col className="w-[128px]" /><col className="w-[136px]" /><col className="w-[116px]" /><col className="w-[36px]" />
          </colgroup>
          <thead className="bg-slate-50/60 text-[10px] uppercase tracking-[0.12em] text-slate-500">
            <tr>
              <th className="px-2 py-2 text-left font-medium">Descripción</th>
              <th className="px-2 py-2 text-left font-medium">Unidad</th>
              <th className="px-2 py-2 text-right font-medium">Cant.</th>
              <th className="px-2 py-2 text-right font-medium">P. unit.</th>
              <th className="px-2 py-2 text-right font-medium">Desc. línea</th>
              <th className="px-2 py-2 text-right font-medium">Neto</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.id || `new-${i}`} className="border-t border-slate-100 align-top">
                <td className="p-1.5"><input className="input" value={l.description}
                  onChange={e => setLine(i, { description: e.target.value })} placeholder="Servicio / producto" /></td>
                <td className="p-1.5">
                  <select className="input" value={l.saleUnit || "pieza"} onChange={e => setLine(i, { saleUnit: e.target.value })}>
                    {QUOTE_SALE_UNITS.map(u => <option key={u.v} value={u.v}>{u.l}</option>)}
                  </select>
                </td>
                <td className="p-1.5"><DecimalInput value={l.quantity} onChange={v => setLine(i, { quantity: v })} /></td>
                <td className="p-1.5"><MoneyInput value={l.unitPrice} onChange={v => setLine(i, { unitPrice: v })} placeholder="0.00" /></td>
                <td className="p-1.5">
                  <DiscountCell mode={l.discMode} val={l.disc} base={lineGross(l)} currency={currency}
                    onMode={m => setLine(i, { discMode: m })} onVal={v => setLine(i, { disc: v })} />
                </td>
                <td className="p-1.5 text-right tabular-nums text-slate-700">
                  {fmtMoneyCcy(lineNetD(l).toNumber(), currency)}
                  {gd.gt(0) && <div className="text-[10px] text-amber-700">−{fmtMoneyCcy(prorated(l).toNumber(), currency)} global</div>}
                </td>
                <td className="p-1.5 text-right">
                  {lines.length > 1 && (
                    <button type="button" onClick={() => rmLine(i)} className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600" title="Eliminar línea">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="border-t border-slate-100 bg-slate-50/40 px-3 py-2">
          <button type="button" onClick={addLine} className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 hover:text-slate-900">
            <Plus className="h-3.5 w-3.5" /> Agregar línea
          </button>
        </div>
      </div>

      {/* Móvil: tarjetas apiladas y etiquetadas */}
      <div className="space-y-3 md:hidden">
        {lines.map((l, i) => (
          <div key={l.id || `m-${i}`} className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-start gap-2">
              <input className="input flex-1" value={l.description} onChange={e => setLine(i, { description: e.target.value })} placeholder="Servicio / producto" />
              {lines.length > 1 && (
                <button type="button" onClick={() => rmLine(i)} className="mt-1.5 shrink-0 rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <label className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Unidad
                <select className="input mt-0.5" value={l.saleUnit || "pieza"} onChange={e => setLine(i, { saleUnit: e.target.value })}>
                  {QUOTE_SALE_UNITS.map(u => <option key={u.v} value={u.v}>{u.l}</option>)}
                </select>
              </label>
              <label className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Cantidad
                <div className="mt-0.5"><DecimalInput value={l.quantity} onChange={v => setLine(i, { quantity: v })} /></div>
              </label>
              <label className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Precio unit.
                <div className="mt-0.5"><MoneyInput value={l.unitPrice} onChange={v => setLine(i, { unitPrice: v })} placeholder="0.00" /></div>
              </label>
              <label className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Descuento
                <div className="mt-0.5"><DiscountCell mode={l.discMode} val={l.disc} base={lineGross(l)} currency={currency}
                  onMode={m => setLine(i, { discMode: m })} onVal={v => setLine(i, { disc: v })} /></div>
              </label>
            </div>
            <div className="mt-2 flex justify-between border-t border-slate-100 pt-2 text-sm">
              <span className="text-slate-500">Neto ({unitLabel(l.saleUnit)})</span>
              <span className="tabular-nums font-medium text-slate-800">{fmtMoneyCcy(lineNetD(l).toNumber(), currency)}</span>
            </div>
          </div>
        ))}
        <button type="button" onClick={addLine} className="inline-flex items-center gap-1 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50">
          <Plus className="h-3.5 w-3.5" /> Agregar línea
        </button>
      </div>

      {/* Descuento global + totales */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Descuento global">
          <DiscountCell mode={globalDiscMode} val={globalDisc} base={subtotal} currency={currency}
            onMode={setGlobalDiscMode} onVal={setGlobalDisc} />
          <p className="mt-1 text-[10px] text-slate-400">Se prorratea por línea al timbrar (efecto mostrado arriba).</p>
        </Field>
        <div className="rounded-xl bg-slate-50/70 p-4">
          <div className="flex justify-between text-sm"><span className="text-slate-600">Subtotal</span><span className="tabular-nums">{fmtMoneyCcy(subtotal.toNumber(), currency)}</span></div>
          {gd.gt(0) && <div className="flex justify-between text-sm"><span className="text-slate-600">Descuento global{globalDiscMode === "pct" ? ` (${round2(gpct)}%)` : ""}</span><span className="tabular-nums text-amber-700">−{fmtMoneyCcy(gd.toNumber(), currency)}</span></div>}
          <div className="flex justify-between text-sm"><span className="text-slate-600">IVA ({round2(D(taxRate).mul(100)).toString()}%)</span><span className="tabular-nums">{fmtMoneyCcy(taxAmount.toNumber(), currency)}</span></div>
          <div className="mt-2 flex justify-between border-t border-slate-200 pt-2 text-base font-semibold"><span>Total</span><span className="tabular-nums">{fmtMoneyCcy(total.toNumber(), currency)}</span></div>
        </div>
      </div>
    </div>
  );
}

function NewQuoteModal({ open, onClose, leads, endClients, onCreated }) {
  const [step, setStep] = useState(1);
  const [targetKind, setTargetKind] = useState("lead"); // "lead" | "end_client"
  const [targetId, setTargetId] = useState("");
  const [form, setForm] = useState({
    title: "", currency: "MXN", taxRate: "0.16", globalDiscMode: "amount", globalDisc: "0", validUntil: "",
    notes: "", terms: "",
  });
  const [lines, setLines] = useState([{ description: "", quantity: "1", unitPrice: "", discMode: "amount", disc: "0", saleUnit: "pieza" }]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setStep(1); setTargetKind("lead"); setTargetId("");
      setForm({ title: "", currency: "MXN", taxRate: "0.16", globalDiscMode: "amount", globalDisc: "0", validUntil: "", notes: "", terms: "" });
      setLines([{ description: "", quantity: "1", unitPrice: "", discMode: "amount", disc: "0", saleUnit: "pieza" }]);
      setErr(null);
    }
  }, [open]);

  const canNext1 = !!targetId;
  const canNext2 = !!form.title.trim() && !!form.currency;
  // El descuento se capea a bruto en la UI (DiscountCell), así que solo validamos lo básico.
  const canSubmit = lines.length > 0 &&
    lines.every(l => l.description.trim() && Number(l.quantity) > 0 && Number(l.unitPrice) >= 0);

  const addLine  = () => setLines(ls => [...ls, { description: "", quantity: "1", unitPrice: "", discMode: "amount", disc: "0", saleUnit: "pieza" }]);
  const rmLine   = (i) => setLines(ls => ls.filter((_, idx) => idx !== i));
  const setLine  = (i, patch) => setLines(ls => ls.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  const submit = async (sendAfter) => {
    if (!canSubmit) return;
    setSubmitting(true); setErr(null);
    try {
      const body = {
        title: form.title.trim(),
        [targetKind === "lead" ? "leadId" : "endClientId"]: targetId,
        currency: form.currency,
        taxRate: form.taxRate,
        globalDiscount: resolvedGlobalDiscount(lines, form.globalDiscMode, form.globalDisc),
        ...(form.validUntil ? { validUntil: form.validUntil } : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
        ...(form.terms.trim() ? { terms: form.terms.trim() } : {}),
        lineItems: lines.map((l, idx) => ({
          description:  l.description.trim(),
          quantity:     String(l.quantity),
          unitPrice:    String(l.unitPrice),
          lineDiscount: resolvedLineDiscount(l),   // % resuelto a monto
          saleUnit:     l.saleUnit || "pieza",
          sortOrder:    idx,
        })),
      };
      const created: any = await api("/quotes", { method: "POST", body });
      if (sendAfter) {
        // Si falla el /send, mantenemos abierto el modal con el error pero el
        // quote YA se creó como DRAFT — el caller puede recargar y verlo.
        try { await api(`/quotes/${created.id}/send`, { method: "POST" }); }
        catch (e) {
          setErr(`Cotización creada como DRAFT (folio ${created.code}), pero el envío falló: ${e?.body?.message || e?.message}`);
          onCreated(created.id);
          return;
        }
      }
      onCreated(created.id);
    } catch (e) {
      setErr(e?.body?.message || e?.message || "No se pudo crear la cotización");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}
           title="Nueva cotización"
           subtitle={`Paso ${step}/3 — ${["Destinatario","Detalles","Líneas y totales"][step-1]}`}
           maxWidth="max-w-3xl">
      {/* Stepper */}
      <div className="mb-5 flex items-center gap-2">
        {[1,2,3].map(n => (
          <div key={n} className="flex flex-1 items-center gap-2">
            <span className={`grid h-6 w-6 place-items-center rounded-full text-[11px] font-semibold ${
              n === step ? "bg-slate-900 text-white"
              : n < step ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-700/15"
              : "bg-slate-100 text-slate-400"
            }`}>{n}</span>
            {n < 3 && <span className={`h-px flex-1 ${n < step ? "bg-emerald-200" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      {/* Paso 1 — target */}
      {step === 1 && (
        <div className="space-y-4">
          <Field label="Tipo de destinatario" required>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "lead",       label: "Lead del pipeline" },
                { v: "end_client", label: "Cliente final"     },
              ].map(opt => (
                <button key={opt.v}
                  onClick={() => { setTargetKind(opt.v); setTargetId(""); }}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-medium transition ${
                    targetKind === opt.v
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </Field>
          <Field label={targetKind === "lead" ? "Lead" : "Cliente final"} required>
            <select className="input" value={targetId} onChange={e => setTargetId(e.target.value)}>
              <option value="">— selecciona —</option>
              {(targetKind === "lead" ? leads : endClients).map(t => (
                <option key={t.id} value={t.id}>
                  {targetKind === "lead" ? t.title : t.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
      )}

      {/* Paso 2 — detalles */}
      {step === 2 && (
        <div className="space-y-4">
          <Field label="Título" required>
            <input className="input" value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Cotización piloto módulo financiero — Q2 2026" />
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Moneda" required>
              <select className="input" value={form.currency}
                onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
                <option value="USD">USD</option>
                <option value="MXN">MXN</option>
                <option value="BRL">BRL</option>
                <option value="EUR">EUR</option>
              </select>
            </Field>
            <Field label="IVA (decimal)" required={false}>
              <input className="input tabular-nums" type="number" step="0.0001" min="0" max="0.9999"
                value={form.taxRate}
                onChange={e => setForm(f => ({ ...f, taxRate: e.target.value }))} />
              <p className="mt-1 text-[10px] text-slate-400">0.16 = 16% MX · 0 = sin impuesto</p>
            </Field>
            <Field label="Vigencia" required={false}>
              <input className="input" type="date" value={form.validUntil}
                onChange={e => setForm(f => ({ ...f, validUntil: e.target.value }))} />
              <p className="mt-1 text-[10px] text-slate-400">Default: emisión + 30 días</p>
            </Field>
          </div>
          <Field label="Notas (opcional)" required={false}>
            <textarea className="input" rows={2} value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </Field>
          <Field label="Términos (opcional)" required={false}>
            <textarea className="input" rows={2} value={form.terms}
              onChange={e => setForm(f => ({ ...f, terms: e.target.value }))} />
          </Field>
        </div>
      )}

      {/* Paso 3 — líneas (editor COMPARTIDO con la edición: unidad + descuentos) */}
      {step === 3 && (
        <QuoteLinesEditor lines={lines} setLine={setLine} addLine={addLine} rmLine={rmLine}
          currency={form.currency} taxRate={form.taxRate}
          globalDiscMode={form.globalDiscMode} globalDisc={form.globalDisc}
          setGlobalDiscMode={(m) => setForm(f => ({ ...f, globalDiscMode: m }))}
          setGlobalDisc={(v) => setForm(f => ({ ...f, globalDisc: v }))} />
      )}

      {err && (
        <div className="mt-4 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</div>
      )}

      {/* Footer del wizard */}
      <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-5">
        <button type="button" onClick={step > 1 ? () => setStep(step-1) : onClose}
                disabled={submitting}
                className="rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50">
          {step > 1 ? "← Atrás" : "Cancelar"}
        </button>
        <div className="flex gap-2">
          {step < 3 ? (
            <button onClick={() => setStep(step+1)}
              disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)}
              className="rounded-xl bg-[var(--brand-primary)] px-5 py-2.5 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-40">
              Siguiente →
            </button>
          ) : (
            <>
              <button onClick={() => submit(false)} disabled={!canSubmit || submitting}
                className="rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-40">
                Guardar borrador
              </button>
              <button onClick={() => submit(true)} disabled={!canSubmit || submitting}
                className="inline-flex items-center gap-2 rounded-xl bg-[var(--brand-primary)] px-5 py-2.5 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-40">
                <Send className="h-4 w-4" />
                {submitting ? "Procesando…" : "Guardar y enviar"}
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

/* ===========================================================================
   QuoteDetailModal — vista full-screen + acciones según estado
   =========================================================================== */
function QuoteDetailModal({ id, onClose, onChanged, user, isAdmin, onOpenProject }) {
  const [quote, setQuote] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoading(true); setErr(null);
    try {
      const q = await api(`/quotes/${id}`);
      setQuote(q);
      // line_items NO viene en el detalle (la vista usa COUNT). Si quisiéramos
      // ver el desglose por línea aquí, habría que añadir un endpoint. Por
      // ahora el PDF es la fuente canónica del breakdown.
      setLines([]);
    } catch (e) {
      setErr(e?.body?.message || e?.message || "No se pudo cargar la cotización");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (id) load(); /* eslint-disable-next-line */ }, [id]);

  if (!id) return null;

  const display = quote?.display_status || quote?.status;
  const isDraft     = display === "DRAFT";
  const isSent      = display === "SENT";

  const isAccepted  = display === "ACCEPTED";
  const canAccept = isSent && hasPermission(user, "quotes:accept");
  const canReject = isSent && hasPermission(user, "quotes:reject");
  const canDelete = isDraft && hasPermission(user, "quotes:delete");
  const canEdit   = (isDraft || isSent) && hasPermission(user, "quotes:update");
  const canDownload = !!quote?.pdf_path;

  const openEdit = () => {
    // B7: avisar ANTES de editar una cotización enviada (creará una versión nueva).
    if (isSent && !window.confirm(
      `Esta cotización ya se envió. Al editarla se archivará la versión actual (v${quote.version}) ` +
      `y volverá a borrador para re-enviarla. ¿Continuar?`)) return;
    setEditOpen(true);
  };

  const doRegenerate = async () => {
    if (!window.confirm("Se invalidará el link actual y se generará uno nuevo. ¿Continuar?")) return;
    setErr(null); setLinkBusy(true);
    try { await api(`/quotes/${id}/link/regenerate`, { method: "POST" }); await load(); onChanged?.(); }
    catch (e) { setErr(e?.body?.message || e?.message); } finally { setLinkBusy(false); }
  };
  const doRevoke = async () => {
    if (!window.confirm("Se cortará el acceso público a esta cotización (el link dejará de funcionar). ¿Continuar?")) return;
    setErr(null); setLinkBusy(true);
    try { await api(`/quotes/${id}/link/revoke`, { method: "POST" }); await load(); onChanged?.(); }
    catch (e) { setErr(e?.body?.message || e?.message); } finally { setLinkBusy(false); }
  };
  const copyLink = () => {
    if (quote?.acceptance_token) navigator.clipboard?.writeText(quotePublicUrl(quote.acceptance_token));
  };
  const doReject = async () => {
    setErr(null);
    try {
      await api(`/quotes/${id}/reject`, { method: "POST", body: { reason: rejectReason || undefined } });
      setShowRejectInput(false); setRejectReason("");
      await load(); onChanged?.();
    } catch (e) { setErr(e?.body?.message || e?.message); }
  };
  const doDelete = async () => {
    if (!window.confirm(`¿Eliminar cotización ${quote?.code}? Esto NO se puede deshacer.`)) return;
    setErr(null);
    try { await api(`/quotes/${id}`, { method: "DELETE" }); onClose(); onChanged?.(); }
    catch (e) { setErr(e?.body?.message || e?.message); }
  };
  const doDownload = async () => {
    setErr(null); setPdfBusy(true);
    try { await downloadQuotePdf(id, quote?.code); }
    catch (e) { setErr(e?.message || "No se pudo descargar el PDF"); }
    finally { setPdfBusy(false); }
  };

  return (
    <>
      <Modal open={!!id} onClose={onClose}
             title={quote ? `Cotización ${quote.code}` : "Cargando…"}
             subtitle={quote?.title}
             maxWidth="max-w-4xl">
        {loading || !quote ? (
          <div className="space-y-3">
            {[0,1,2,3].map(i => <div key={i} className="h-12 animate-pulse rounded-xl bg-slate-100" />)}
          </div>
        ) : (
          <div className="space-y-5">
            {/* Banner virtual expired */}
            {isExpiredVirtual(quote) && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                  <div className="flex-1">
                    <p className="font-medium">Cotización vencida</p>
                    <p className="text-[12px] opacity-80">
                      La vigencia ({fmtDate(quote.valid_until)}) ya pasó. La BD aún la lista
                      como SENT — el cron de expiración llega en Día 7.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Resumen */}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Stat label="Estado">
                <QuoteStatusChip status={display} />
              </Stat>
              <Stat label="Cliente">
                {quote.target_name || "—"}
              </Stat>
              <Stat label="Total nativo">
                <span className="font-mono tabular-nums">
                  {fmtMoneyCcy(Number(quote.total_amount), (quote.currency || "USD").trim())}
                </span>
              </Stat>
              <Stat label="Total USD (snapshot)">
                <span className="font-mono tabular-nums">
                  {quote.total_usd_snapshot != null
                    ? fmtMoneyCcy(Number(quote.total_usd_snapshot), "USD")
                    : "— (se congela al enviar)"}
                </span>
              </Stat>
              <Stat label="Folio"><span className="font-mono">{quote.code}</span></Stat>
              <Stat label="Emisión">{quote.issued_at ? fmtDate(quote.issued_at) : "—"}</Stat>
              <Stat label="Vigencia">{quote.valid_until ? fmtDate(quote.valid_until) : "—"}</Stat>
              <Stat label="Creado por">{quote.created_by_name || "—"}</Stat>
            </div>

            {/* Totales breakdown */}
            <div className="rounded-xl bg-slate-50/70 p-4">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal</span>
                <span className="tabular-nums">{fmtMoneyCcy(Number(quote.subtotal), quote.currency)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">
                  IVA ({(Number(quote.tax_rate) * 100).toFixed(2).replace(/\.00$/, "")}%)
                </span>
                <span className="tabular-nums">{fmtMoneyCcy(Number(quote.tax_amount), quote.currency)}</span>
              </div>
              <div className="mt-2 flex justify-between border-t border-slate-200 pt-2 text-base font-semibold">
                <span>Total</span>
                <span className="tabular-nums">{fmtMoneyCcy(Number(quote.total_amount), quote.currency)}</span>
              </div>
            </div>

            {/* V142: link público (SENT con token) — compartir / whatsapp / regenerar / revocar */}
            {isSent && quote.acceptance_token && (
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Link para el cliente</p>
                <div className="mt-2 flex items-center gap-2">
                  <input readOnly className="input flex-1 text-xs text-slate-600" value={quotePublicUrl(quote.acceptance_token)} />
                  <button onClick={copyLink} className="rounded-lg bg-white px-3 py-2 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50" title="Copiar">Copiar</button>
                  <a href={`https://wa.me/?text=${encodeURIComponent(`Hola, aquí tienes la cotización ${quote.code}: ${quotePublicUrl(quote.acceptance_token)}`)}`}
                     target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700">
                    WhatsApp
                  </a>
                </div>
                <div className="mt-2 flex gap-3 text-[11px]">
                  <button onClick={doRegenerate} disabled={linkBusy} className="text-slate-500 hover:text-slate-800 disabled:opacity-50">Regenerar link</button>
                  <button onClick={doRevoke} disabled={linkBusy} className="text-rose-600 hover:text-rose-800 disabled:opacity-50">Revocar acceso</button>
                </div>
              </div>
            )}

            {/* V142: B9 — link al proyecto creado al aceptar */}
            {isAccepted && quote.project_id && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 text-sm text-emerald-900">
                <button onClick={() => { onClose(); onOpenProject?.(quote.project_id); }} className="inline-flex items-center gap-1.5 font-medium hover:underline">
                  <FolderKanban className="h-4 w-4" /> Ver el proyecto creado
                </button>
              </div>
            )}

            {/* V144: facturación por avance (solo cotización ACEPTADA) */}
            {isAccepted && (
              <QuoteBillingsPanel quoteId={id} user={user} onChanged={onChanged} />
            )}

            {/* Líneas count + nota */}
            <p className="text-xs text-slate-500">
              {quote.line_count} {Number(quote.line_count) === 1 ? "línea" : "líneas"} —
              {" "}el desglose completo está en el PDF (descárgalo abajo).
            </p>

            {/* Notas / términos */}
            {quote.notes && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Notas</p>
                <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{quote.notes}</p>
              </div>
            )}
            {quote.terms && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Términos</p>
                <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{quote.terms}</p>
              </div>
            )}

            {/* Firmas (si ACCEPTED) */}
            {Array.isArray(quote.signatures) && quote.signatures.length > 0 && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-800">
                  Aceptación de la cotización
                </p>
                <ul className="mt-2 space-y-1.5 text-sm text-emerald-900">
                  {quote.signatures.map((s, i) => (
                    <li key={i} className="leading-relaxed">
                      Aceptado por <strong>{s.signer_name}</strong>
                      {s.signer_email && <span className="text-emerald-700/80"> ({s.signer_email})</span>}
                      {" "}el <span className="tabular-nums">{fmtDate(s.signed_at)}</span>
                      {" "}desde IP <span className="font-mono">{s.signer_ip ?? "desconocida"}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {err && (
              <div className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</div>
            )}

            {/* Reject sub-form (inline para no abrir 4o nivel de modal) */}
            {showRejectInput && (
              <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4">
                <p className="mb-2 text-xs font-medium text-rose-800">Motivo del rechazo (opcional)</p>
                <textarea className="input" rows={2} value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="Ej: Presupuesto fuera de rango — esperamos Q4." />
                <div className="mt-3 flex justify-end gap-2">
                  <button onClick={() => { setShowRejectInput(false); setRejectReason(""); }}
                    className="rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200">
                    Cancelar
                  </button>
                  <button onClick={doReject}
                    className="rounded-lg bg-rose-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-800">
                    Confirmar rechazo
                  </button>
                </div>
              </div>
            )}

            {/* Acciones */}
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 pt-5">
              <div className="mr-auto flex flex-wrap items-center gap-2">
                <button onClick={() => setEventsOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-white px-4 py-2 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50">
                  <Clock className="h-3.5 w-3.5" /> Bitácora
                </button>
                {Number(quote.version) > 1 && (
                  <button onClick={() => setVersionsOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-white px-4 py-2 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50">
                    <History className="h-3.5 w-3.5" /> Versiones (v{quote.version})
                  </button>
                )}
                {isAccepted && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-400" title="Una cotización aceptada está congelada; genera una nueva para cambios.">
                    <Lock className="h-3.5 w-3.5" /> Aceptada — congelada
                  </span>
                )}
              </div>
              {canEdit && (
                <button onClick={openEdit}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-white px-4 py-2 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
                  <Edit2 className="h-3.5 w-3.5" /> Editar
                </button>
              )}
              {canDownload && (
                <button onClick={doDownload} disabled={pdfBusy}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-white px-4 py-2 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50">
                  <Download className="h-3.5 w-3.5" /> {pdfBusy ? "Descargando…" : "Descargar PDF"}
                </button>
              )}
              {canDelete && (
                <button onClick={doDelete}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-white px-4 py-2 text-xs font-medium text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50">
                  <Trash2 className="h-3.5 w-3.5" /> Eliminar
                </button>
              )}
              {isSent && hasPermission(user, "quotes:send") && (
                <button onClick={() => setSendOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-white px-4 py-2 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
                  <Send className="h-3.5 w-3.5" /> Reenviar
                </button>
              )}
              {isDraft && hasPermission(user, "quotes:send") && (
                <button onClick={() => setSendOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--brand-primary)] px-4 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
                  <Send className="h-3.5 w-3.5" /> Enviar
                </button>
              )}
              {canReject && !showRejectInput && (
                <button onClick={() => setShowRejectInput(true)}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-white px-4 py-2 text-xs font-medium text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50">
                  <XCircle className="h-3.5 w-3.5" /> Marcar como rechazada
                </button>
              )}
              {canAccept && (
                <button onClick={() => setAcceptOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-medium text-white hover:bg-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Marcar como aceptada
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      <AcceptQuoteModal open={acceptOpen} onClose={() => setAcceptOpen(false)}
                        quoteId={id} quoteCode={quote?.code}
                        onAccepted={() => { setAcceptOpen(false); load(); onChanged?.(); }} />

      <EditQuoteModal open={editOpen} quote={quote} onClose={() => setEditOpen(false)}
                      onSaved={() => { setEditOpen(false); load(); onChanged?.(); }} />
      <QuoteVersionsModal open={versionsOpen} quote={quote} onClose={() => setVersionsOpen(false)} />
      <SendQuoteModal open={sendOpen} quote={quote} onClose={() => setSendOpen(false)}
                      onSent={() => { setSendOpen(false); load(); onChanged?.(); }} />
      <QuoteEventsModal open={eventsOpen} quote={quote} onClose={() => setEventsOpen(false)} />
    </>
  );
}

/* ───────────────────────────────────────────────────────────────────────────
   V144 — Facturación por AVANCE. Registrar avances (monto/% o por líneas) y ver
   facturado / cobrado / saldo (todo DERIVADO; el backend es la verdad). NO timbra
   — el CFDI del avance llega en V145. El cobrado se lee del ledger de pagos.
   ─────────────────────────────────────────────────────────────────────────── */
function BillKpi({ label, value, tone = "" }) {
  const color = tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-slate-800";
  return (
    <div className="rounded-lg bg-slate-50/70 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</p>
      <p className={`mt-0.5 font-mono text-sm tabular-nums ${color}`}>{value}</p>
    </div>
  );
}

function ModeTab({ active, onClick, label }) {
  return (
    <button type="button" onClick={onClick}
      className={`rounded-lg px-3 py-1.5 text-xs font-medium ${active ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>
      {label}
    </button>
  );
}

function QuoteBillingsPanel({ quoteId, user, onChanged }) {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const canBill = hasPermission(user, "quotes:bill");

  const load = async () => {
    setLoading(true); setErr(null);
    try { setView(await api(`/quotes/${quoteId}/billings`)); }
    catch (e) { setErr(e?.body?.message || e?.message || "No se pudo cargar la facturación"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (quoteId) load(); /* eslint-disable-next-line */ }, [quoteId]);

  const doVoid = async (bId) => {
    if (!window.confirm("¿Anular este avance? El facturado se recalcula.")) return;
    setBusyId(bId); setErr(null);
    try { setView(await api(`/quotes/${quoteId}/billings/${bId}/void`, { method: "POST" })); onChanged?.(); }
    catch (e) { setErr(e?.body?.message || e?.message); }
    finally { setBusyId(null); }
  };

  const ccy = view?.currency || "USD";

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Facturación por avance</p>
        {canBill && view && Number(view.saldo_por_facturar) > 0 && (
          <button onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
            <Plus className="h-3.5 w-3.5" /> Registrar avance
          </button>
        )}
      </div>

      {loading && !view ? (
        <div className="mt-3 h-16 animate-pulse rounded-lg bg-slate-100" />
      ) : view ? (
        <>
          <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
            <BillKpi label="Facturado" value={fmtMoneyCcy(Number(view.facturado_total), ccy)} />
            <BillKpi label="Cobrado" value={fmtMoneyCcy(Number(view.cobrado), ccy)} />
            <BillKpi label="Saldo por facturar" value={fmtMoneyCcy(Number(view.saldo_por_facturar), ccy)}
              tone={Number(view.saldo_por_facturar) <= 0 ? "ok" : "warn"} />
            <BillKpi label="Saldo por cobrar" value={fmtMoneyCcy(Number(view.saldo_por_cobrar), ccy)} />
          </div>
          <p className="mt-2 text-[11px] text-slate-400">
            Base contratada (sin IVA): {fmtMoneyCcy(Number(view.net_cap), ccy)}. El cobrado se lee del ledger de pagos.
          </p>

          {(view.billings || []).length > 0 ? (
            <ul className="mt-3 divide-y divide-slate-100">
              {(view.billings || []).map(b => (
                <li key={b.id} className={`flex items-center justify-between py-2 text-sm ${b.voided_at ? "opacity-50" : ""}`}>
                  <div className="min-w-0">
                    <span className="font-medium text-slate-700">Avance {b.seq}</span>
                    <span className="ml-2 text-xs text-slate-500">
                      {b.bill_mode === "lines" ? "por líneas" : "monto"}{b.concept ? ` · ${b.concept}` : ""}
                      {b.voided_at ? " · anulado" : ""}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="tabular-nums text-slate-700">{fmtMoneyCcy(Number(b.total), ccy)}</span>
                    {canBill && !b.voided_at && (
                      <button onClick={() => doVoid(b.id)} disabled={busyId === b.id}
                        className="text-[11px] text-rose-600 hover:text-rose-800 disabled:opacity-50">Anular</button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-xs text-slate-400">Aún no hay avances facturados.</p>
          )}
        </>
      ) : null}

      {err && <div className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</div>}

      <QuoteBillingModal open={modalOpen} quoteId={quoteId} view={view}
        onClose={() => setModalOpen(false)}
        onSaved={(v) => { setModalOpen(false); setView(v); onChanged?.(); }} />
    </div>
  );
}

function QuoteBillingModal({ open, quoteId, view, onClose, onSaved }) {
  const [mode, setMode] = useState("amount");
  const [amountKind, setAmountKind] = useState("money"); // money | pct
  const [concept, setConcept] = useState("");
  const [amount, setAmount] = useState("");
  const [pct, setPct] = useState("");
  const [lines, setLines] = useState([]);
  const [linesLoaded, setLinesLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const ccy = view?.currency || "USD";
  const netCap = new Decimal(view?.net_cap || 0);
  const remaining = new Decimal(view?.saldo_por_facturar || 0);

  useEffect(() => {
    if (!open) {
      setMode("amount"); setAmountKind("money"); setConcept(""); setAmount(""); setPct("");
      setLines([]); setLinesLoaded(false); setErr(null); setBusy(false);
    }
  }, [open]);

  // Cargar líneas de la cotización al entrar a modo 'lines' (una vez).
  useEffect(() => {
    if (open && mode === "lines" && !linesLoaded) {
      api(`/quotes/${quoteId}/lines`).then((ls: any) => {
        setLines((ls || []).map((l: any) => ({
          quote_line_id: l.id,
          description: l.description,
          sale_unit: l.saleUnit || l.sale_unit || "pieza",
          unit_price: String(l.unitPrice ?? l.unit_price ?? "0"),
          max_qty: String(l.quantity ?? "0"),
          qty: "",
        })));
        setLinesLoaded(true);
      }).catch(() => setLinesLoaded(true));
    }
  }, [open, mode, linesLoaded, quoteId]);

  const previewSubtotal = (() => {
    if (mode === "lines") return lines.reduce((a, l) => a.plus(D(l.qty).mul(D(l.unit_price))), new Decimal(0));
    if (amountKind === "pct") return round2(netCap.mul(D(pct)).div(100));
    return D(amount);
  })();

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      let body;
      if (mode === "lines") {
        const payload = lines.filter(l => D(l.qty).gt(0)).map(l => ({
          quoteLineId: l.quote_line_id, description: l.description, saleUnit: l.sale_unit,
          quantity: String(l.qty), unitPrice: l.unit_price,
          amount: round2(D(l.qty).mul(D(l.unit_price))).toString(),
        }));
        if (!payload.length) { setErr("Indica la cantidad a facturar en al menos una línea."); setBusy(false); return; }
        body = { mode: "lines", lines: payload };
      } else if (amountKind === "pct") {
        body = { mode: "amount", concept: concept || undefined, percentage: String(pct) };
      } else {
        body = { mode: "amount", concept: concept || undefined, subtotal: String(amount) };
      }
      const v = await api(`/quotes/${quoteId}/billings`, { method: "POST", body });
      onSaved?.(v);
    } catch (e) {
      setErr(e?.body?.message || e?.message || "No se pudo registrar el avance");
    } finally { setBusy(false); }
  };

  const overCap = previewSubtotal.gt(remaining.plus(0.001));
  const canSubmit = previewSubtotal.gt(0) && !overCap;

  return (
    <Modal open={open} onClose={onClose} title="Registrar avance" maxWidth="max-w-lg">
      <div className="space-y-4">
        <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Saldo por facturar: <strong className="tabular-nums">{fmtMoneyCcy(Number(view?.saldo_por_facturar || 0), ccy)}</strong>
          {" "}de {fmtMoneyCcy(Number(view?.net_cap || 0), ccy)} (sin IVA).
        </div>

        <div className="flex gap-2">
          <ModeTab active={mode === "amount"} onClick={() => setMode("amount")} label="Monto / %" />
          <ModeTab active={mode === "lines"} onClick={() => setMode("lines")} label="Por líneas" />
        </div>

        {mode === "amount" ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Concepto (opcional)</label>
              <input className="input mt-1" value={concept} onChange={e => setConcept(e.target.value)}
                placeholder="Ej: Anticipo 30%" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Importe del avance</label>
              <div className="mt-1 flex items-center gap-2">
                <div className="inline-flex overflow-hidden rounded-lg ring-1 ring-slate-200">
                  <button type="button" onClick={() => setAmountKind("money")}
                    className={`px-3 py-2 text-xs ${amountKind === "money" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>$</button>
                  <button type="button" onClick={() => setAmountKind("pct")}
                    className={`px-3 py-2 text-xs ${amountKind === "pct" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>%</button>
                </div>
                <div className="flex-1">
                  {amountKind === "money"
                    ? <MoneyInput value={amount} onChange={setAmount} placeholder="0.00" />
                    : <DecimalInput value={pct} onChange={setPct} placeholder="0" suffix="%" />}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {!linesLoaded ? (
              <div className="h-16 animate-pulse rounded-lg bg-slate-100" />
            ) : lines.length === 0 ? (
              <p className="text-xs text-slate-400">La cotización no tiene líneas.</p>
            ) : (
              lines.map((l, i) => (
                <div key={l.quote_line_id} className="rounded-lg border border-slate-100 p-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="truncate text-slate-700">{l.description}</span>
                    <span className="ml-2 shrink-0 text-xs text-slate-400">
                      {fmtMoneyCcy(Number(l.unit_price), ccy)}/{l.sale_unit} · máx {l.max_qty}
                    </span>
                  </div>
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="text-xs text-slate-500">Cantidad a facturar</span>
                    <div className="w-28">
                      <DecimalInput value={l.qty}
                        onChange={(v) => setLines(prev => prev.map((x, j) => j === i ? { ...x, qty: v } : x))}
                        placeholder="0" />
                    </div>
                    <span className="ml-auto text-xs tabular-nums text-slate-600">
                      = {fmtMoneyCcy(Number(round2(D(l.qty).mul(D(l.unit_price)))), ccy)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
          <span className="text-slate-600">Subtotal del avance</span>
          <span className="font-mono tabular-nums text-slate-900">{fmtMoneyCcy(Number(previewSubtotal), ccy)}</span>
        </div>
        {overCap && (
          <p className="text-xs text-rose-600">Excede el saldo por facturar ({fmtMoneyCcy(Number(remaining), ccy)}).</p>
        )}
        {err && <div className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="rounded-lg bg-white px-4 py-2 text-sm text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">Cancelar</button>
          <button onClick={submit} disabled={busy || !canSubmit}
            className="rounded-lg bg-[var(--brand-primary)] px-4 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
            {busy ? "Registrando…" : "Registrar avance"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// B3: unidades de venta (deben casar el enum del backend, V139).
const QUOTE_SALE_UNITS = [
  { v: "hora", l: "Hora" }, { v: "dia", l: "Día" }, { v: "mes", l: "Mes" },
  { v: "entregable", l: "Entregable" }, { v: "pieza", l: "Pieza" },
];

/* ===========================================================================
   EditQuoteModal — editar cotización (DRAFT libre; SENT archiva versión + DRAFT).
   Unidad de venta (B3) + descuentos línea/global (B5). Los totales DEFINITIVOS
   los calcula el backend (trigger V139); aquí solo hay un preview de edición.
   =========================================================================== */
function EditQuoteModal({ open, quote, onClose, onSaved }) {
  const [meta, setMeta] = useState({ title: "", taxRate: "0.16", globalDiscMode: "amount", globalDisc: "0", validUntil: "", notes: "", terms: "" });
  const [lines, setLines] = useState([]);       // {id?, description, quantity, unitPrice, lineDiscount, saleUnit, sortOrder}
  const [origIds, setOrigIds] = useState([]);   // ids que existían al cargar (para diff de borrados)
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const currency = (quote?.currency || "USD").trim();
  const wasSent = quote?.status === "SENT";

  useEffect(() => {
    if (!open || !quote) return;
    setErr(null); setLoading(true);
    (async () => {
      try {
        const ls: any = await api(`/quotes/${quote.id}/lines`);
        setLines((ls || []).map(l => ({
          id: l.id, description: l.description, quantity: String(l.quantity),
          unitPrice: String(l.unitPrice),
          discMode: "amount", disc: String(l.lineDiscount ?? "0"),   // el API guarda MONTO
          saleUnit: l.saleUnit || "pieza", sortOrder: l.sortOrder ?? 0,
        })));
        setOrigIds((ls || []).map(l => l.id));
        setMeta({
          title: quote.title || "", taxRate: String(quote.tax_rate ?? "0.16"),
          globalDiscMode: "amount", globalDisc: String(quote.global_discount ?? "0"),
          validUntil: quote.valid_until ? String(quote.valid_until).slice(0, 10) : "",
          notes: quote.notes || "", terms: quote.terms || "",
        });
      } catch (e) { setErr(e?.body?.message || e?.message || "No se pudieron cargar las líneas"); }
      finally { setLoading(false); }
    })();
  }, [open, quote]);

  const setLine = (i, patch) => setLines(ls => ls.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const addLine = () => setLines(ls => [...ls, { description: "", quantity: "1", unitPrice: "", discMode: "amount", disc: "0", saleUnit: "pieza", sortOrder: ls.length }]);
  const rmLine  = (i) => setLines(ls => ls.filter((_, idx) => idx !== i));

  // El descuento se capea a bruto en la UI (DiscountCell); solo validamos lo básico.
  const canSave = lines.length > 0 && lines.every(l =>
    l.description.trim() && Number(l.quantity) > 0 && Number(l.unitPrice) >= 0);

  const save = async () => {
    if (!canSave) return;
    setSaving(true); setErr(null);
    try {
      // 1) PATCH metadata SIEMPRE primero: en una cotización SENT esto dispara el
      //    snapshot de versión y la regresa a DRAFT (reviseIfSent). Así el resto
      //    de las operaciones de línea caen en DRAFT -> UNA sola versión archivada.
      await api(`/quotes/${quote.id}`, {
        method: "PATCH",
        body: {
          title: meta.title.trim(),
          taxRate: meta.taxRate,
          globalDiscount: resolvedGlobalDiscount(lines, meta.globalDiscMode, meta.globalDisc),
          ...(meta.validUntil ? { validUntil: meta.validUntil } : {}),
          notes: meta.notes,
          terms: meta.terms,
        },
      });
      // 2) Diff de líneas.
      const formIds = lines.filter(l => l.id).map(l => l.id);
      for (const oid of origIds) {
        if (!formIds.includes(oid)) await api(`/quotes/${quote.id}/lines/${oid}`, { method: "DELETE" });
      }
      for (let i = 0; i < lines.length; i++) {
        const l = lines[i];
        const payload = {
          description: l.description.trim(), quantity: String(l.quantity),
          unitPrice: String(l.unitPrice), lineDiscount: resolvedLineDiscount(l),   // % resuelto a monto
          saleUnit: l.saleUnit, sortOrder: i,
        };
        if (l.id) await api(`/quotes/${quote.id}/lines/${l.id}`, { method: "PATCH", body: payload });
        else      await api(`/quotes/${quote.id}/lines`, { method: "POST", body: payload });
      }
      onSaved();
    } catch (e) {
      setErr(e?.body?.message || e?.message || "No se pudo guardar la cotización");
    } finally { setSaving(false); }
  };

  if (!open || !quote) return null;
  return (
    <Modal open={open} onClose={onClose} title={`Editar ${quote.code}`}
           subtitle={quote.title} maxWidth="max-w-4xl">
      {wasSent && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
            <p>Esta cotización <strong>ya se envió</strong>. Al guardar se archivará la versión
               actual (v{quote.version}) y volverá a <strong>borrador (v{(quote.version ?? 1) + 1})</strong> para re-enviarla.</p>
          </div>
        </div>
      )}
      {loading ? (
        <div className="space-y-3">{[0,1,2].map(i => <div key={i} className="h-12 animate-pulse rounded-xl bg-slate-100" />)}</div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="md:col-span-2">
              <Field label="Título" required>
                <input className="input" value={meta.title} onChange={e => setMeta(m => ({ ...m, title: e.target.value }))} />
              </Field>
            </div>
            <Field label="IVA (decimal)">
              <input className="input tabular-nums" type="number" step="0.0001" min="0" max="0.9999"
                value={meta.taxRate} onChange={e => setMeta(m => ({ ...m, taxRate: e.target.value }))} />
            </Field>
            <Field label="Vigencia">
              <input className="input" type="date" value={meta.validUntil}
                onChange={e => setMeta(m => ({ ...m, validUntil: e.target.value }))} />
            </Field>
          </div>

          {/* Líneas — editor COMPARTIDO con la creación */}
          <QuoteLinesEditor lines={lines} setLine={setLine} addLine={addLine} rmLine={rmLine}
            currency={currency} taxRate={meta.taxRate}
            globalDiscMode={meta.globalDiscMode} globalDisc={meta.globalDisc}
            setGlobalDiscMode={(m) => setMeta(mm => ({ ...mm, globalDiscMode: m }))}
            setGlobalDisc={(v) => setMeta(mm => ({ ...mm, globalDisc: v }))} />

          <Field label="Notas"><textarea className="input" rows={2} value={meta.notes} onChange={e => setMeta(m => ({ ...m, notes: e.target.value }))} /></Field>
          <Field label="Términos"><textarea className="input" rows={2} value={meta.terms} onChange={e => setMeta(m => ({ ...m, terms: e.target.value }))} /></Field>

          {err && <div className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</div>}

          <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
            <button onClick={onClose} disabled={saving} className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50">Cancelar</button>
            <button onClick={save} disabled={!canSave || saving}
              className="rounded-xl bg-[var(--brand-primary)] px-5 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-40">
              {saving ? "Guardando…" : (wasSent ? "Guardar como nueva versión" : "Guardar cambios")}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ===========================================================================
   QuoteVersionsModal — consume GET /quotes/:id/versions (B7).
   =========================================================================== */
function QuoteVersionsModal({ open, quote, onClose }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const currency = (quote?.currency || "USD").trim();

  useEffect(() => {
    if (!open || !quote) return;
    setLoading(true); setErr(null);
    api(`/quotes/${quote.id}/versions`)
      .then((v: any) => setVersions(v || []))
      .catch(e => setErr(e?.body?.message || e?.message || "No se pudo cargar el historial"))
      .finally(() => setLoading(false));
  }, [open, quote]);

  if (!open || !quote) return null;
  return (
    <Modal open={open} onClose={onClose} title={`Historial de ${quote.code}`}
           subtitle={`Versión actual: v${quote.version}`} maxWidth="max-w-3xl">
      {loading ? (
        <div className="space-y-3">{[0,1].map(i => <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />)}</div>
      ) : err ? (
        <div className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</div>
      ) : versions.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-500">
          Sin versiones archivadas todavía. Se crea una cada vez que editas una cotización <strong>enviada</strong>.
        </p>
      ) : (
        <ul className="space-y-3">
          {versions.map(v => {
            const q = v.snapshot?.quote || {};
            const nLines = Array.isArray(v.snapshot?.lines) ? v.snapshot.lines.length : 0;
            return (
              <li key={v.id} className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-800">Versión v{v.version}</span>
                  <span className="text-xs text-slate-500">{fmtDate(v.created_at)}</span>
                </div>
                <div className="mt-1.5 grid grid-cols-2 gap-2 text-xs text-slate-600 md:grid-cols-4">
                  <div><span className="text-slate-400">Editó:</span> {v.edited_by_name?.trim() || "—"}</div>
                  <div><span className="text-slate-400">Líneas:</span> {nLines}</div>
                  <div><span className="text-slate-400">Total:</span> <span className="tabular-nums">{q.total_amount != null ? fmtMoneyCcy(Number(q.total_amount), currency) : "—"}</span></div>
                  <div><span className="text-slate-400">Título:</span> {q.title || "—"}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}

// V142: link público persistente de la cotización.
const quotePublicUrl = (token) => `${window.location.origin}/cotizacion/${token}`;

/* ===========================================================================
   SendQuoteModal — enviar por email (link, no PDF) o solo generar el link. V142.
   =========================================================================== */
function SendQuoteModal({ open, quote, onClose, onSent }) {
  const [contacts, setContacts] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    setErr(null); setEmail(""); setName("");
    api("/contacts").then((cs: any) => setContacts(Array.isArray(cs) ? cs : (cs?.items ?? []))).catch(() => setContacts([]));
  }, [open]);

  const pickContact = (id) => {
    const c = contacts.find((x: any) => String(x.id) === String(id));
    if (c) { setEmail(c.email || ""); setName([c.firstName, c.lastName].filter(Boolean).join(" ") || c.name || ""); }
  };

  const doSend = async (withEmail) => {
    setBusy(true); setErr(null);
    try {
      await api(`/quotes/${quote.id}/send`, {
        method: "POST",
        body: withEmail ? { toEmail: email.trim(), toName: name.trim() || undefined } : {},
      });
      onSent();
    } catch (e) { setErr(e?.body?.message || e?.message || "No se pudo enviar"); }
    finally { setBusy(false); }
  };

  if (!open || !quote) return null;
  return (
    <Modal open={open} onClose={onClose} title={`Enviar ${quote.code}`} subtitle={quote.title} maxWidth="max-w-lg">
      <div className="space-y-4">
        <p className="text-xs text-slate-500">Se envía un <strong>link</strong> al portal de la cotización (no un PDF): permite ver si lo abrieron y aceptar desde ahí, y no envejece al editar.</p>
        {contacts.length > 0 && (
          <Field label="Contacto del cliente">
            <select className="input" onChange={e => pickContact(e.target.value)}>
              <option value="">— elige un contacto o escribe el correo —</option>
              {contacts.map((c: any) => (
                <option key={c.id} value={c.id}>{[c.firstName, c.lastName].filter(Boolean).join(" ") || c.name} · {c.email}</option>
              ))}
            </select>
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Correo"><input className="input" value={email} onChange={e => setEmail(e.target.value)} placeholder="cliente@correo.com" /></Field>
          <Field label="Nombre (opcional)"><input className="input" value={name} onChange={e => setName(e.target.value)} /></Field>
        </div>
        {err && <div className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</div>}
        <div className="flex items-center justify-between border-t border-slate-100 pt-4">
          <button onClick={() => doSend(false)} disabled={busy}
            className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50">
            Solo generar link
          </button>
          <button onClick={() => doSend(true)} disabled={busy || !email.trim()}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--brand-primary)] px-5 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-40">
            <Send className="h-4 w-4" /> {busy ? "Enviando…" : "Enviar por email"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ===========================================================================
   QuoteEventsModal — bitácora (consume GET /quotes/:id/events). V142.
   =========================================================================== */
function QuoteEventsModal({ open, quote, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !quote) return;
    setLoading(true);
    api(`/quotes/${quote.id}/events`).then((e: any) => setEvents(e || [])).catch(() => setEvents([])).finally(() => setLoading(false));
  }, [open, quote]);
  const LABEL = { sent: "Enviada", opened: "Abrió el link", accepted: "Aceptada", rejected: "Rechazada", project_created: "Proyecto creado", link_revoked: "Link revocado", link_regenerated: "Link regenerado", versioned: "Versión archivada" };
  if (!open || !quote) return null;
  return (
    <Modal open={open} onClose={onClose} title={`Bitácora de ${quote.code}`} maxWidth="max-w-2xl">
      {loading ? <div className="h-24 animate-pulse rounded-xl bg-slate-100" />
        : events.length === 0 ? <p className="py-6 text-center text-sm text-slate-500">Sin eventos todavía.</p>
        : (
        <ul className="space-y-2">
          {events.map((e: any) => (
            <li key={e.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm">
              <div>
                <span className="font-medium text-slate-800">{LABEL[e.event_type] || e.event_type}</span>
                {e.to_contact && <span className="text-slate-500"> · {e.to_contact}</span>}
                {e.channel && <span className="text-[11px] text-slate-400"> ({e.channel})</span>}
                <span className="text-[11px] text-slate-400"> · {e.actor_kind === "public" ? "cliente" : (e.actor_name || "staff")}</span>
              </div>
              <span className="text-xs tabular-nums text-slate-400">{fmtDate(e.created_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

function isExpiredVirtual(q) {
  return q?.display_status === "EXPIRED" && q?.status === "SENT";
}

function Stat({ label, children }) {
  return (
    <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200/80">
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</p>
      <div className="mt-1 text-sm text-slate-800">{children}</div>
    </div>
  );
}

/* ===========================================================================
   AcceptQuoteModal — captura signer + checkbox de términos
   =========================================================================== */
function AcceptQuoteModal({ open, onClose, quoteId, quoteCode, onAccepted }) {
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) { setSignerName(""); setSignerEmail(""); setAcceptedTerms(false); setErr(null); }
  }, [open]);

  const canSubmit = signerName.trim().length > 1 && acceptedTerms;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true); setErr(null);
    try {
      await api(`/quotes/${quoteId}/accept`, {
        method: "POST",
        body: {
          signerName: signerName.trim(),
          ...(signerEmail.trim() ? { signerEmail: signerEmail.trim() } : {}),
          acceptedTerms: true,
        },
      });
      onAccepted();
    } catch (e2) {
      setErr(e2?.body?.message || e2?.message || "No se pudo registrar la aceptación");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}
           title="Confirmar aceptación"
           subtitle={`Cotización ${quoteCode}`}>
      <form onSubmit={submit} className="space-y-4">
        <p className="rounded-lg bg-slate-50 px-3 py-2.5 text-xs text-slate-600">
          Esta es una <strong>e-firma simple</strong>: registramos nombre, IP y timestamp.
          NO es criptográfica ni NOM-151. Tiene valor probatorio limitado — para contratos
          de alto valor usa un proveedor PKI.
        </p>
        <Field label="Nombre del firmante" required>
          <input className="input" value={signerName} onChange={e => setSignerName(e.target.value)}
            placeholder="Nombre completo (representante autorizado)" required />
        </Field>
        <Field label="Email (opcional)">
          <input className="input" type="email" value={signerEmail}
            onChange={e => setSignerEmail(e.target.value)}
            placeholder="firmante@cliente.com" />
        </Field>
        <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-slate-200 bg-white p-3 text-sm">
          <input type="checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900" />
          <span className="text-slate-700">
            Confirmo que estoy autorizado para aceptar esta cotización en nombre del cliente
            y acepto los términos y condiciones especificados.
          </span>
        </label>
        {err && (
          <div className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</div>
        )}
        <div className="flex justify-end gap-3 border-t border-slate-100 pt-5">
          <button type="button" onClick={onClose} disabled={submitting}
            className="rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50">
            Cancelar
          </button>
          <button type="submit" disabled={!canSubmit || submitting}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
            <CheckCircle2 className="h-4 w-4" />
            {submitting ? "Procesando…" : "Confirmar aceptación"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ===========================================================================
   TicketsView (V11 / Day 4 — Bloque B)
   - Tabs "Mis tickets" / "Todos" (segundo solo si tickets:read_all)
   - Filtros: status / category / priority
   - Modal de creación + detalle con escalate/resolve/reopen
   =========================================================================== */
const TICKET_STATUS_LABEL = {
  OPEN: "Abierto", IN_PROGRESS: "En proceso", ESCALATED: "Escalado",
  RESOLVED: "Resuelto", CLOSED: "Cerrado",
};
const TICKET_STATUS_STYLES = {
  OPEN:        "bg-sky-50 text-sky-700 ring-sky-700/15",
  IN_PROGRESS: "bg-amber-50 text-amber-700 ring-amber-700/15",
  ESCALATED:   "bg-rose-50 text-rose-800 ring-rose-700/15",
  RESOLVED:    "bg-emerald-50 text-emerald-700 ring-emerald-700/15",
  CLOSED:      "bg-slate-100 text-slate-600 ring-slate-500/10",
};
const TICKET_PRIORITY_LABEL = { LOW:"Baja", MEDIUM:"Media", HIGH:"Alta", URGENT:"Urgente" };
const TICKET_PRIORITY_STYLES = {
  LOW:    "bg-slate-100 text-slate-600",
  MEDIUM: "bg-sky-50 text-sky-700",
  HIGH:   "bg-amber-50 text-amber-700",
  URGENT: "bg-rose-50 text-rose-800",
};
const TICKET_CATEGORY_LABEL = {
  BUG:"Bug", FEATURE:"Feature", HOW_TO:"Cómo hago", BILLING:"Facturación", OTHER:"Otro",
};

function TicketStatusChip({ status }) {
  const cls = TICKET_STATUS_STYLES[status] ?? TICKET_STATUS_STYLES.OPEN;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wide ring-1 ring-inset ${cls}`}>
      {TICKET_STATUS_LABEL[status] ?? status}
    </span>
  );
}
function TicketPriorityChip({ priority }) {
  const cls = TICKET_PRIORITY_STYLES[priority] ?? TICKET_PRIORITY_STYLES.MEDIUM;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${cls}`}>
      {TICKET_PRIORITY_LABEL[priority] ?? priority}
    </span>
  );
}

function TicketsView({ user, isAdmin, onOpenDigest }) {
  const canReadAll = hasPermission(user, "tickets:read_all");
  const canCreate  = hasPermission(user, "tickets:create");
  const canDigest  = hasPermission(user, "tickets:digest");
  const canResolve = hasPermission(user, "tickets:resolve");

  const [tab, setTab] = useState("mine"); // 'mine' | 'all'
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter]     = useState("ALL");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [priorityFilter, setPriorityFilter] = useState("ALL");
  const [newOpen, setNewOpen]   = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const path = tab === "all" && canReadAll ? "/tickets/all" : "/tickets";
      const list = await api(path);
      setTickets(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.body?.message || e?.message || "No se pudieron cargar los tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const filtered = useMemo(() => {
    return tickets.filter(t =>
      (statusFilter === "ALL"   || t.status   === statusFilter) &&
      (categoryFilter === "ALL" || t.category === categoryFilter) &&
      (priorityFilter === "ALL" || t.priority === priorityFilter)
    );
  }, [tickets, statusFilter, categoryFilter, priorityFilter]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h3 className="font-serif text-xl tracking-tight text-slate-900">Tickets de soporte</h3>
          <p className="text-sm text-slate-500">
            Reporta bugs, pide features o consulta cómo hacer algo. Tu admin verá el digest diario.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canDigest && (
            <button onClick={onOpenDigest}
              className="inline-flex items-center gap-1.5 rounded-xl bg-white px-3 py-2 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
              <BarChart3 className="h-3.5 w-3.5" /> Digest diario
            </button>
          )}
          {canCreate && (
            <button onClick={() => setNewOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--brand-primary)] px-4 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
              <Plus className="h-3.5 w-3.5" /> Nuevo ticket
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        <button onClick={() => setTab("mine")}
          className={`-mb-px border-b-2 px-3 py-2 text-xs font-medium ${tab==="mine" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
          Mis tickets
        </button>
        {canReadAll && (
          <button onClick={() => setTab("all")}
            className={`-mb-px border-b-2 px-3 py-2 text-xs font-medium ${tab==="all" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            Todos
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <select className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs"
                value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="ALL">Todos los estados</option>
          {Object.entries(TICKET_STATUS_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs"
                value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
          <option value="ALL">Todas las categorías</option>
          {Object.entries(TICKET_CATEGORY_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs"
                value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}>
          <option value="ALL">Todas las prioridades</option>
          {Object.entries(TICKET_PRIORITY_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {error && <div className="alert-over rounded-xl px-3 py-2 text-xs">{error}</div>}

      <div className="card-soft overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50/60 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Título</th>
                <th className="px-4 py-3">Categoría</th>
                <th className="px-4 py-3">Prioridad</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Creado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td className="px-4 py-6 text-center text-slate-400" colSpan={6}>Cargando…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td className="px-4 py-12 text-center" colSpan={6}>
                  <LifeBuoy className="mx-auto h-8 w-8 text-slate-300" />
                  <p className="mt-2 text-sm text-slate-500">No hay tickets que coincidan con el filtro.</p>
                </td></tr>
              )}
              {!loading && filtered.map(t => (
                <tr key={t.id} className="cursor-pointer hover:bg-slate-50/60" onClick={() => setDetailId(t.id)}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{t.title}</div>
                    {tab === "all" && t.created_by_name && (
                      <div className="text-[11px] text-slate-400">Por {t.created_by_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{TICKET_CATEGORY_LABEL[t.category] ?? t.category}</td>
                  <td className="px-4 py-3"><TicketPriorityChip priority={t.priority} /></td>
                  <td className="px-4 py-3"><TicketStatusChip status={t.status} /></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(t.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <ChevronRight className="inline h-4 w-4 text-slate-300" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <NewTicketModal open={newOpen} onClose={() => setNewOpen(false)} onCreated={() => { setNewOpen(false); load(); }} />
      <TicketDetailModal
        ticketId={detailId} onClose={() => setDetailId(null)}
        canResolve={canResolve} isAdmin={isAdmin}
        onChanged={load}
      />
    </div>
  );
}

function NewTicketModal({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("OTHER");
  const [priority, setPriority] = useState("MEDIUM");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) { setTitle(""); setDescription(""); setCategory("OTHER"); setPriority("MEDIUM"); setErr(null); }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      await api("/tickets", { method: "POST", body: { title, description, category, priority } });
      onCreated?.();
    } catch (e2) {
      const msg = Array.isArray(e2?.body?.message) ? e2.body.message.join(" · ") : (e2?.body?.message || e2?.message);
      setErr(msg || "No se pudo crear el ticket");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <form onSubmit={submit} className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="mb-4">
          <h3 className="font-serif text-lg tracking-tight text-slate-900">Nuevo ticket</h3>
          <p className="text-xs text-slate-500">Cuéntanos qué pasa. Mientras más contexto, más rápido lo resolvemos.</p>
        </div>
        <div className="space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-xs text-slate-600">Título</span>
            <input className="input" value={title} onChange={e => setTitle(e.target.value)} required minLength={3} maxLength={200} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1.5 block text-xs text-slate-600">Categoría</span>
              <select className="input" value={category} onChange={e => setCategory(e.target.value)}>
                {Object.entries(TICKET_CATEGORY_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs text-slate-600">Prioridad</span>
              <select className="input" value={priority} onChange={e => setPriority(e.target.value)}>
                {Object.entries(TICKET_PRIORITY_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="mb-1.5 block text-xs text-slate-600">Descripción (mín. 20 chars)</span>
            <textarea className="input min-h-[120px]" value={description} onChange={e => setDescription(e.target.value)} required minLength={20} maxLength={5000} />
          </label>
        </div>
        {err && <div className="mt-3 alert-over rounded-xl px-3 py-2 text-xs">{err}</div>}
        <div className="mt-5 flex justify-end gap-3 border-t border-slate-100 pt-4">
          <button type="button" onClick={onClose} disabled={submitting}
            className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">Cancelar</button>
          <button type="submit" disabled={submitting}
            className="rounded-xl bg-[var(--brand-primary)] px-5 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
            {submitting ? "Creando…" : "Crear ticket"}
          </button>
        </div>
      </form>
    </div>
  );
}

function TicketDetailModal({ ticketId, onClose, canResolve, isAdmin, onChanged }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolution, setResolution] = useState("");
  const [working, setWorking] = useState(false);

  const load = async () => {
    if (!ticketId) return;
    setLoading(true); setErr(null);
    try {
      const t = await api(`/tickets/${ticketId}`);
      setTicket(t);
    } catch (e) {
      setErr(e?.body?.message || e?.message || "No se pudo cargar");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ticketId]);

  if (!ticketId) return null;

  const doAction = async (action) => {
    setWorking(true); setErr(null);
    try {
      if (action === "escalate") await api(`/tickets/${ticketId}/escalate`, { method: "POST" });
      if (action === "reopen")   await api(`/tickets/${ticketId}/reopen`,   { method: "POST" });
      if (action === "resolve") {
        await api(`/tickets/${ticketId}/resolve`, { method: "POST", body: { resolution } });
        setResolveOpen(false); setResolution("");
      }
      await load(); onChanged?.();
    } catch (e) {
      setErr(e?.body?.message || e?.message || "Acción fallida");
    } finally { setWorking(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-serif text-lg tracking-tight text-slate-900">{ticket?.title ?? "Ticket"}</h3>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {ticket && <TicketStatusChip status={ticket.status} />}
              {ticket && <TicketPriorityChip priority={ticket.priority} />}
              {ticket && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">
                  {TICKET_CATEGORY_LABEL[ticket.category] ?? ticket.category}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        {loading && <p className="text-sm text-slate-400">Cargando…</p>}
        {err && <div className="alert-over rounded-xl px-3 py-2 text-xs">{err}</div>}

        {ticket && (
          <>
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
              <p className="whitespace-pre-wrap text-sm text-slate-700">{ticket.description}</p>
            </div>
            <div className="mt-3 text-[11px] text-slate-500">
              Creado {fmtDate(ticket.created_at)}{ticket.created_by_name ? ` · por ${ticket.created_by_name}` : ""}
            </div>

            {ticket.status === "RESOLVED" && (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Resolución</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-emerald-900">{ticket.resolution}</p>
                {ticket.resolved_at && (
                  <p className="mt-2 text-[11px] text-emerald-700">Resuelto {fmtDate(ticket.resolved_at)}</p>
                )}
              </div>
            )}

            {/* Acciones */}
            <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 pt-4">
              {ticket.status === "RESOLVED" && canResolve && (
                <button onClick={() => doAction("reopen")} disabled={working}
                  className="rounded-xl bg-white px-4 py-2 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
                  Reabrir
                </button>
              )}
              {isAdmin && ["OPEN","IN_PROGRESS"].includes(ticket.status) && (
                <button onClick={() => doAction("escalate")} disabled={working}
                  className="rounded-xl bg-amber-100 px-4 py-2 text-xs font-medium text-amber-800 hover:bg-amber-200">
                  Escalar
                </button>
              )}
              {canResolve && ["OPEN","IN_PROGRESS","ESCALATED"].includes(ticket.status) && (
                <button onClick={() => setResolveOpen(true)} disabled={working}
                  className="rounded-xl bg-emerald-600 px-4 py-2 text-xs font-medium text-white hover:bg-emerald-700">
                  Marcar resuelto
                </button>
              )}
            </div>

            {resolveOpen && (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-white p-4">
                <p className="mb-2 text-xs font-medium text-slate-700">Resolución (mín. 5 chars)</p>
                <textarea className="input min-h-[80px]"
                  value={resolution}
                  onChange={e => setResolution(e.target.value)}
                  placeholder="Cómo se resolvió el ticket…" />
                <div className="mt-3 flex justify-end gap-2">
                  <button onClick={() => { setResolveOpen(false); setResolution(""); }}
                    className="rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200">Cancelar</button>
                  <button onClick={() => doAction("resolve")} disabled={working || resolution.length < 5}
                    className="rounded-xl bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
                    Confirmar resolución
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ===========================================================================
   TicketsDigestView (V11 / Day 4 — Bloque B.4)
   - admin only
   - 5 cards de summary, tabla de escalados, tabla de OPEN > 24h, BarChart por categoría
   - Exporta a Markdown descargable
   =========================================================================== */
function TicketsDigestView({ user, onBack }) {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const d = await api("/tickets/digest");
        if (!cancelled) setDigest(d);
      } catch (e) {
        if (!cancelled) setError(e?.body?.message || e?.message || "No se pudo cargar el digest");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const exportMarkdown = () => {
    if (!digest) return;
    const lines = [];
    lines.push(`# Daily Digest — ${digest.tenant.name} (${digest.tenant.slug})`);
    lines.push(`Generado: ${digest.generatedAt}`);
    lines.push("");
    lines.push(`## Resumen`);
    lines.push(`- Total: ${digest.summary.total}`);
    lines.push(`- Abiertos: ${digest.summary.open}`);
    lines.push(`- En proceso: ${digest.summary.inProgress}`);
    lines.push(`- Escalados: ${digest.summary.escalated}`);
    lines.push(`- Resueltos últimas 24h: ${digest.summary.resolvedLast24h}`);
    lines.push("");
    lines.push(`## Por categoría`);
    digest.byCategory.forEach(c => lines.push(`- ${TICKET_CATEGORY_LABEL[c.category] ?? c.category}: ${c.count}`));
    lines.push("");
    lines.push(`## Por prioridad (solo abiertos)`);
    digest.byPriority.forEach(p => lines.push(`- ${TICKET_PRIORITY_LABEL[p.priority] ?? p.priority}: ${p.count}`));
    lines.push("");
    lines.push(`## Escalados pendientes (${digest.escalatedTickets.length})`);
    digest.escalatedTickets.forEach(t => lines.push(`- [${t.priority}] ${t.title} — ${t.createdByName ?? "?"} (${t.created_at})`));
    lines.push("");
    lines.push(`## Abiertos > 24h (${digest.openOver24h.length})`);
    digest.openOver24h.forEach(t => lines.push(`- [${t.priority}] ${t.title} — ${t.createdByName ?? "?"} (${t.created_at})`));
    const md = lines.join("\n");
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `digest-${digest.tenant.slug}-${digest.generatedAt.slice(0,10)}.md`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <button onClick={onBack} className="mb-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900">
            <ArrowLeft className="h-3.5 w-3.5" /> Volver a tickets
          </button>
          <h3 className="font-serif text-xl tracking-tight text-slate-900">Daily Digest</h3>
          {digest && (
            <p className="text-xs text-slate-500">
              Generado {new Date(digest.generatedAt).toLocaleString("es-MX")}
            </p>
          )}
        </div>
        {digest && (
          <button onClick={exportMarkdown}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--brand-primary)] px-4 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
            <Download className="h-3.5 w-3.5" /> Exportar a Markdown
          </button>
        )}
      </div>

      {loading && <p className="text-sm text-slate-400">Cargando digest…</p>}
      {error && <div className="alert-over rounded-xl px-3 py-2 text-xs">{error}</div>}

      {digest && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              { label: "Total",             value: digest.summary.total,           tone: "bg-slate-50 text-slate-900" },
              { label: "Abiertos",          value: digest.summary.open,            tone: "bg-sky-50 text-sky-800" },
              { label: "En proceso",        value: digest.summary.inProgress,      tone: "bg-amber-50 text-amber-800" },
              { label: "Escalados",         value: digest.summary.escalated,       tone: "bg-rose-50 text-rose-800" },
              { label: "Resueltos 24h",     value: digest.summary.resolvedLast24h, tone: "bg-emerald-50 text-emerald-800" },
            ].map(card => (
              <div key={card.label} className={`card-soft rounded-2xl border border-slate-200/80 p-4 ${card.tone}`}>
                <p className="text-[10px] font-semibold uppercase tracking-wide opacity-60">{card.label}</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">{card.value}</p>
              </div>
            ))}
          </div>

          {/* Gráfico por categoría */}
          <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-5">
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-500">Tickets por categoría</p>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={digest.byCategory.map(c => ({
                  category: TICKET_CATEGORY_LABEL[c.category] ?? c.category,
                  count: c.count,
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="category" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0F172A" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Escalados */}
          <div className="card-soft overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
            <div className="border-b border-slate-100 px-5 py-3">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Escalados pendientes ({digest.escalatedTickets.length})
              </p>
            </div>
            {digest.escalatedTickets.length === 0 ? (
              <p className="px-5 py-6 text-sm text-slate-400">Sin escalados pendientes.</p>
            ) : (
              <table className="min-w-full text-sm">
                <tbody className="divide-y divide-slate-100">
                  {digest.escalatedTickets.map(t => (
                    <tr key={t.id}>
                      <td className="px-5 py-3">
                        <div className="font-medium text-slate-900">{t.title}</div>
                        <div className="text-[11px] text-slate-400">{t.createdByName ?? "?"} · {fmtDate(t.created_at)}</div>
                      </td>
                      <td className="px-5 py-3 text-right"><TicketPriorityChip priority={t.priority} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* OPEN > 24h */}
          <div className="card-soft overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
            <div className="border-b border-slate-100 px-5 py-3">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Abiertos sin tocar &gt; 24h ({digest.openOver24h.length})
              </p>
            </div>
            {digest.openOver24h.length === 0 ? (
              <p className="px-5 py-6 text-sm text-slate-400">Todos los tickets abiertos son recientes.</p>
            ) : (
              <table className="min-w-full text-sm">
                <tbody className="divide-y divide-slate-100">
                  {digest.openOver24h.map(t => (
                    <tr key={t.id}>
                      <td className="px-5 py-3">
                        <div className="font-medium text-slate-900">{t.title}</div>
                        <div className="text-[11px] text-slate-400">{t.createdByName ?? "?"} · {fmtDate(t.created_at)}</div>
                      </td>
                      <td className="px-5 py-3 text-right"><TicketPriorityChip priority={t.priority} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ===========================================================================
   Day 5 Block E — Sales stages customization (Settings section)
   =========================================================================== */
function SalesStagesSection() {
  const [stages, setStages] = useState([]);
  const [leadCounts, setLeadCounts] = useState({}); // {stageId: count}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // stage being edited inline
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#94a3b8");
  const [newProb, setNewProb] = useState("0");
  const [working, setWorking] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [s, leads] = await Promise.all([
        api("/sales-stages"),
        api("/leads").catch(() => []),
      ]);
      setStages(Array.isArray(s) ? s : []);
      const counts = {};
      (Array.isArray(leads) ? leads : []).forEach(l => {
        if (l.stageId) counts[l.stageId] = (counts[l.stageId] ?? 0) + 1;
      });
      setLeadCounts(counts);
    } catch (e) {
      setError(e?.body?.message || e?.message || "No se pudieron cargar las etapas");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveEdit = async () => {
    if (!editing) return;
    setWorking(true);
    try {
      await api(`/sales-stages/${editing.id}`, {
        method: "PATCH",
        body: {
          name: editing.name,
          color: editing.color,
          defaultProbability: Number(editing.defaultProbability) || 0,
        },
      });
      setEditing(null);
      await load();
    } catch (e) {
      alert(e?.body?.message || "No se pudo guardar");
    } finally { setWorking(false); }
  };

  const move = async (idx, delta) => {
    const newOrder = [...stages];
    const swapWith = idx + delta;
    if (swapWith < 0 || swapWith >= newOrder.length) return;
    [newOrder[idx], newOrder[swapWith]] = [newOrder[swapWith], newOrder[idx]];
    setStages(newOrder);
    try {
      await api("/sales-stages/reorder", {
        method: "POST",
        body: { stageIds: newOrder.map(s => s.id) },
      });
      await load();
    } catch (e) {
      alert(e?.body?.message || "No se pudo reordenar");
      await load(); // revert
    }
  };

  const remove = async (s) => {
    if (!confirm(`Eliminar etapa "${s.name}"?`)) return;
    try {
      await api(`/sales-stages/${s.id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      alert(e?.body?.message || "No se pudo eliminar");
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setWorking(true);
    try {
      await api("/sales-stages", {
        method: "POST",
        body: {
          name: newName,
          color: newColor,
          defaultProbability: Number(newProb) || 0,
        },
      });
      setNewOpen(false); setNewName(""); setNewColor("#94a3b8"); setNewProb("0");
      await load();
    } catch (e2) {
      alert(e2?.body?.message || "No se pudo crear");
    } finally { setWorking(false); }
  };

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
      <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-6 py-5 md:px-8">
        <div>
          <h3 className="font-serif text-lg tracking-tight text-slate-900">Etapas del Pipeline</h3>
          <p className="text-sm text-slate-500">Personaliza nombres, colores y orden. Las base no se eliminan pero sí se renombran.</p>
        </div>
        <button onClick={() => setNewOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
          <Plus className="h-3.5 w-3.5" /> Agregar etapa
        </button>
      </div>

      {error && <div className="m-6 alert-over rounded-xl px-3 py-2 text-xs">{error}</div>}

      <ul className="divide-y divide-slate-100">
        {loading && <li className="px-6 py-4 text-sm text-slate-400">Cargando…</li>}
        {!loading && stages.map((s, idx) => {
          const isEditing = editing?.id === s.id;
          const count = leadCounts[s.id] ?? 0;
          return (
            <li key={s.id} className="flex items-center gap-3 px-6 py-3">
              <span className="inline-flex items-center gap-2">
                <button onClick={() => move(idx, -1)} disabled={idx === 0}
                  className="rounded-md p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30">↑</button>
                <button onClick={() => move(idx, +1)} disabled={idx === stages.length - 1}
                  className="rounded-md p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30">↓</button>
              </span>
              {isEditing ? (
                <>
                  <input type="color" value={editing.color}
                    onChange={e => setEditing({ ...editing, color: e.target.value })}
                    className="h-7 w-9 cursor-pointer rounded border border-slate-200" />
                  <input value={editing.name}
                    onChange={e => setEditing({ ...editing, name: e.target.value })}
                    className="input flex-1" />
                  <input type="number" min="0" max="100"
                    value={editing.defaultProbability}
                    onChange={e => setEditing({ ...editing, defaultProbability: e.target.value })}
                    className="input w-20" />
                  <button onClick={saveEdit} disabled={working}
                    className="rounded-lg bg-emerald-600 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-700">
                    Guardar
                  </button>
                  <button onClick={() => setEditing(null)}
                    className="rounded-lg bg-white px-3 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
                    Cancelar
                  </button>
                </>
              ) : (
                <>
                  <span className="h-4 w-4 rounded-full ring-1 ring-slate-200" style={{ background: s.color }} />
                  <span className="flex-1 text-sm font-medium text-slate-900">
                    {s.name}
                    {s.isLocked && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">base</span>}
                    {(s.isWon || s.isLost) && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-700">terminal</span>}
                  </span>
                  <span className="text-xs text-slate-500 tabular-nums">{s.defaultProbability}%</span>
                  <span title={count > 0 ? `${count} lead(s) en esta etapa` : "Sin leads"}
                        className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${count > 0 ? "bg-slate-100 text-slate-600" : "bg-slate-50 text-slate-400"}`}>
                    {count} lead{count === 1 ? "" : "s"}
                  </span>
                  <button onClick={() => setEditing({ ...s })}
                    className="rounded-lg px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100">
                    Editar
                  </button>
                  <button onClick={() => remove(s)}
                    disabled={s.isLocked || count > 0}
                    title={s.isLocked ? "Etapa base — no se puede eliminar" : count > 0 ? `Tiene ${count} lead(s)` : "Eliminar"}
                    className={`rounded-lg px-2.5 py-1 text-xs font-medium ${s.isLocked || count > 0 ? "text-slate-300" : "text-rose-700 hover:bg-rose-50"}`}>
                    Eliminar
                  </button>
                </>
              )}
            </li>
          );
        })}
      </ul>

      {newOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
          <form onSubmit={create} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="font-serif text-lg tracking-tight text-slate-900">Nueva etapa</h3>
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="mb-1.5 block text-xs text-slate-600">Nombre</span>
                <input className="input" value={newName} onChange={e => setNewName(e.target.value)} required minLength={2} maxLength={80} />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1.5 block text-xs text-slate-600">Color</span>
                  <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)} className="h-10 w-full cursor-pointer rounded-lg border border-slate-200" />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-xs text-slate-600">Probabilidad %</span>
                  <input type="number" min="0" max="100" className="input" value={newProb} onChange={e => setNewProb(e.target.value)} />
                </label>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setNewOpen(false)} className="rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200">Cancelar</button>
              <button type="submit" disabled={working} className="rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
                {working ? "Creando…" : "Crear etapa"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

/* ===========================================================================
   Day 5 Block B — Platform admin views (cross-tenant, solo Arturo)
   =========================================================================== */
function PlatformTenantsView() {
  const [tenants, setTenants] = useState([]);
  const [digest, setDigest] = useState(null);
  // relay v5 §1 — la insignia de descuento del panel viejo LEE DEL MOTOR (charge-preview),
  // no de signup_discount, para que este panel y /platform digan lo mismo. La escritura
  // de descuentos vive SOLO en /platform (el botón "Descuento" de aquí se retiró).
  const [motorByTenant, setMotorByTenant] = useState({}); // { [tenantId]: {effectivePercent, baseType, referidoActive} }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("ALL"); // ALL | TRIAL | PAID | EXPIRED
  const [extendOpen, setExtendOpen] = useState(null); // { id, slug }
  const [extendDate, setExtendDate] = useState("");
  const [working, setWorking] = useState(false);
  // feat/modular-plans — selección MODULAR à la carte por el platform-admin.
  const [modOpen, setModOpen] = useState(null); // { id, slug }
  const [modSel, setModSel] = useState([]);     // string[] módulos seleccionados
  // V82 — pagos MANUALES (transferencia/efectivo): registro + historial + anular.
  const [payOpen, setPayOpen] = useState(null);   // { id, slug }
  const [payForm, setPayForm] = useState({ method: "transferencia", amount: "", paidAt: "", paidUntil: "" });
  const [payHistory, setPayHistory] = useState([]);
  const [payError, setPayError] = useState(null);
  // V83 — recompensas de referido del tenant como REFERENTE (créditos pendientes).
  const [payRewards, setPayRewards] = useState([]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [ts, d, cp] = await Promise.all([
        api("/platform/tenants"),
        api("/platform/digest").catch(() => null),
        api("/platform/pricing/charge-preview").catch(() => []), // insignia desde el motor
      ]);
      setTenants(Array.isArray(ts) ? ts : []);
      setDigest(d);
      const m = {};
      for (const r of (Array.isArray(cp) ? cp : [])) m[r.tenantId] = r;
      setMotorByTenant(m);
    } catch (e) {
      setError(e?.body?.message || e?.message || "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const now = Date.now();
    return tenants.filter(t => {
      if (filter === "PAID") return t.subscriptionActive;
      if (filter === "TRIAL") return !t.subscriptionActive && t.trialEndsAt && new Date(t.trialEndsAt).getTime() > now;
      if (filter === "EXPIRED") return !t.subscriptionActive && t.trialEndsAt && new Date(t.trialEndsAt).getTime() <= now;
      return true;
    });
  }, [tenants, filter]);

  const submitExtend = async () => {
    if (!extendOpen) return;
    setWorking(true);
    try {
      await api(`/platform/tenants/${extendOpen.id}`, {
        method: "PATCH",
        body: { trialEndsAt: new Date(extendDate).toISOString() },
      });
      setExtendOpen(null); setExtendDate("");
      await load();
    } catch (e) {
      alert(e?.body?.message || "No se pudo extender el trial");
    } finally { setWorking(false); }
  };

  // feat/modular-plans — el platform-admin fija los módulos (à la carte) de un tenant.
  const toggleMod = (m) =>
    setModSel((s) => (s.includes(m) ? s.filter((x) => x !== m) : [...s, m]));
  const submitModules = async () => {
    if (!modOpen || modSel.length === 0) return;
    setWorking(true);
    try {
      await api(`/platform/tenants/${modOpen.id}/modules`, { method: "PATCH", body: { modules: modSel } });
      setModOpen(null);
      await load();
    } catch (e) {
      alert(e?.body?.message || "No se pudieron cambiar los módulos");
    } finally { setWorking(false); }
  };

  // relay v5 §1 — la escritura de descuentos se movió a /platform (única puerta).
  // El botón "Descuento" y su modal se retiraron de este panel; la insignia lee del motor.

  // V82 — pagos MANUALES: el `pagado-hasta` del registro alimenta el candado de
  // sucursales Y el trial-guard (los 3 clientes reales pagan por transferencia).
  const fmtDate = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");
  const openPayments = async (t) => {
    setPayOpen({ id: t.tenantId, slug: t.slug });
    setPayForm({
      method: "transferencia", amount: "",
      paidAt: new Date().toISOString().slice(0, 10), paidUntil: "",
    });
    setPayError(null);
    try {
      setPayHistory(await api(`/platform/tenants/${t.tenantId}/manual-payments`));
    } catch { setPayHistory([]); }
    try {
      setPayRewards(await api(`/platform/tenants/${t.tenantId}/referral-rewards`));
    } catch { setPayRewards([]); }
  };
  const submitPayment = async () => {
    if (!payOpen) return;
    const amount = Number(payForm.amount);
    if (!amount || amount <= 0) { setPayError("Monto inválido"); return; }
    if (!payForm.paidAt || !payForm.paidUntil) { setPayError("Faltan fechas"); return; }
    if (payForm.paidUntil < payForm.paidAt) { setPayError("Pagado-hasta debe ser posterior a la fecha del pago"); return; }
    setWorking(true); setPayError(null);
    try {
      await api(`/platform/tenants/${payOpen.id}/manual-payments`, {
        method: "POST",
        body: { method: payForm.method, amount, paidAt: payForm.paidAt, paidUntil: payForm.paidUntil },
      });
      setPayHistory(await api(`/platform/tenants/${payOpen.id}/manual-payments`));
      // V83: el registro pudo drenar una recompensa de referido → refresca.
      try { setPayRewards(await api(`/platform/tenants/${payOpen.id}/referral-rewards`)); } catch { /* noop */ }
      setPayForm((f) => ({ ...f, amount: "", paidUntil: "" }));
      await load();
    } catch (e) {
      setPayError(e?.body?.message || "No se pudo registrar el pago");
    } finally { setWorking(false); }
  };
  const voidPayment = async (paymentId) => {
    if (!payOpen) return;
    if (!window.confirm("¿Anular este pago? La fila queda marcada (no se borra) y deja de contar para la cobertura.")) return;
    setWorking(true); setPayError(null);
    try {
      await api(`/platform/tenants/${payOpen.id}/manual-payments/${paymentId}/void`, { method: "POST" });
      setPayHistory(await api(`/platform/tenants/${payOpen.id}/manual-payments`));
      await load();
    } catch (e) {
      setPayError(e?.body?.message || "No se pudo anular el pago");
    } finally { setWorking(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h3 className="font-serif text-xl tracking-tight text-slate-900">Tenants registrados</h3>
          <p className="text-sm text-slate-500">Vista cross-tenant para el owner de FluidSol. RLS bypassed via SECURITY DEFINER.</p>
        </div>
      </div>

      {digest && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          {[
            { label: "Total tenants",  value: digest.totalTenants,           tone: "bg-slate-50 text-slate-900" },
            { label: "En trial",        value: digest.tenantsOnTrial,         tone: "bg-amber-50 text-amber-800" },
            { label: "Pagados",         value: digest.tenantsPaid,            tone: "bg-emerald-50 text-emerald-800" },
            { label: "Trial 7d",        value: digest.tenantsTrialExpiring7d, tone: "bg-rose-50 text-rose-800" },
            { label: "Signups hoy",     value: digest.signupsToday,           tone: "bg-sky-50 text-sky-800" },
          ].map(c => (
            <div key={c.label} className={`card-soft rounded-2xl border border-slate-200/80 p-4 ${c.tone}`}>
              <p className="text-[10px] font-semibold uppercase tracking-wide opacity-60">{c.label}</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">{c.value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        {["ALL","TRIAL","PAID","EXPIRED"].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset ${filter === f ? "bg-amber-600 text-white ring-amber-600" : "bg-white text-slate-600 ring-slate-200"}`}>
            {f === "ALL" ? "Todos" : f === "TRIAL" ? "En trial" : f === "PAID" ? "Pagados" : "Expirados"}
          </button>
        ))}
      </div>

      {error && <div className="alert-over rounded-xl px-3 py-2 text-xs">{error}</div>}

      <div className="card-soft overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50/60 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Tenant</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Users</th>
                <th className="px-4 py-3 text-right">Proyectos</th>
                <th className="px-4 py-3 text-right">Leads</th>
                <th className="px-4 py-3 text-right">Tickets (abrt/esc)</th>
                <th className="px-4 py-3">Módulos / estado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td className="px-4 py-6 text-center text-slate-400" colSpan={8}>Cargando…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td className="px-4 py-8 text-center text-slate-400" colSpan={8}>Sin tenants en este filtro.</td></tr>
              )}
              {!loading && filtered.map(t => {
                const trialMs = t.trialEndsAt ? new Date(t.trialEndsAt).getTime() : null;
                const now = Date.now();
                const expired = trialMs !== null && trialMs <= now && !t.subscriptionActive;
                const daysLeft = trialMs !== null ? Math.ceil((trialMs - now) / 86400000) : null;
                return (
                  <tr key={t.tenantId} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{t.name}</div>
                      <div className="font-mono text-[11px] text-slate-400">{t.slug}</div>
                    </td>
                    <td className="px-4 py-3">
                      {t.subscriptionActive ? (
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">Pagado</span>
                      ) : t.manuallyCovered ? (
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700"
                          title="Pago manual (transferencia/efectivo) vigente">
                          Pagado (manual) · hasta {fmtDate(t.manualPaidUntil)}
                        </span>
                      ) : expired ? (
                        <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-medium text-rose-700">Expirado</span>
                      ) : (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">Trial</span>
                      )}
                      {/* relay v5 §1 — insignia DESDE EL MOTOR (charge-preview), no signup_discount. */}
                      {(() => {
                        const mo = motorByTenant[t.tenantId];
                        if (!mo || !mo.effectivePercent) return null;
                        const label = mo.baseType === "fundador" ? "Fundador" : mo.baseType || (mo.referidoActive ? "Referido" : "");
                        return (
                          <span className="ml-1 rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-medium text-sky-700"
                            title={`Descuento del motor: ${label} −${mo.effectivePercent}%${mo.referidoActive ? " (incluye referido)" : ""}. Se gestiona en /platform.`}>
                            −{mo.effectivePercent}%{label ? ` ${label}` : ""}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{t.totalUsers}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{t.totalProjects}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{t.totalLeads}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <span className="text-slate-700">{t.openTickets}</span>
                      {t.escalatedTickets > 0 && (
                        <span className="ml-1 rounded bg-rose-100 px-1 text-rose-700 text-[10px]">{t.escalatedTickets} esc</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      <div className="flex flex-wrap items-center gap-1">
                        {(Array.isArray(t.enabledModules) ? t.enabledModules : []).map((m) => (
                          <span key={m} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-slate-600">{m}</span>
                        ))}
                        <span className="text-[10px] text-slate-400">
                          {t.subscriptionActive ? "pagado" : trialMs === null ? "legacy" : `${daysLeft >= 0 ? daysLeft : 0}d`}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <button onClick={() => { setModOpen({ id: t.tenantId, slug: t.slug }); setModSel(Array.isArray(t.enabledModules) && t.enabledModules.length ? [...t.enabledModules] : ["crm", "boutique", "gastro"]); }}
                        className="rounded-lg bg-slate-800 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-slate-900">
                        Módulos
                      </button>
                      {/* relay v5 §1 — el botón "Descuento" se retiró: /platform es la única
                          puerta de escritura de descuentos (evita la segunda puerta al motor). */}
                      <button onClick={() => openPayments(t)}
                        title="Pagos manuales (transferencia/efectivo): el pagado-hasta da acceso y habilita sucursales"
                        className={`rounded-lg px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset ${t.manuallyCovered ? "bg-emerald-600 text-white ring-emerald-600 hover:bg-emerald-700" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>
                        Registrar pago
                      </button>
                      {!t.subscriptionActive && (
                        <button onClick={() => {
                          setExtendOpen({ id: t.tenantId, slug: t.slug });
                          const base = trialMs && trialMs > now ? new Date(trialMs) : new Date();
                          base.setDate(base.getDate() + 14);
                          setExtendDate(base.toISOString().slice(0, 10));
                        }}
                          className="rounded-lg bg-amber-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-amber-700">
                          Extender trial
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {extendOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="font-serif text-lg tracking-tight text-slate-900">Extender trial de {extendOpen.slug}</h3>
            <label className="mt-4 block">
              <span className="mb-1.5 block text-xs text-slate-600">Nueva fecha de expiración</span>
              <input type="date" className="input" value={extendDate} onChange={e => setExtendDate(e.target.value)} />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => { setExtendOpen(null); setExtendDate(""); }}
                className="rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200">Cancelar</button>
              <button onClick={submitExtend} disabled={working || !extendDate}
                className="rounded-xl bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50">
                {working ? "Guardando…" : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* V82 — pagos manuales: registrar + historial + anular. */}
      {payOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="font-serif text-lg tracking-tight text-slate-900">Pagos manuales de {payOpen.slug}</h3>
            <p className="mt-1 text-xs text-slate-500">
              Transferencia o efectivo cobrados directo. El <strong>pagado-hasta</strong> más lejano (no anulado)
              da acceso a la app y habilita sucursales adicionales — inclusive todo ese día (CDMX).
            </p>

            {/* V83 — créditos de referido pendientes: al registrar ESTE pago se
                drena UNO (aplica el −10% sugerido sobre la base al monto). */}
            {payRewards.filter((r) => r.status === "pending").length > 0 && (
              <div className="mt-3 rounded-xl bg-sky-50 px-3 py-2 text-xs text-sky-800">
                <strong>Crédito de referido pendiente:</strong> este tenant refirió a{" "}
                {payRewards.filter((r) => r.status === "pending").map((r) => r.referredSlug).join(", ")}.
                Sugerido: <strong>−10% de su base</strong> en el monto de este pago — al registrarlo se
                consume UNA recompensa (una por pago/periodo).
              </div>
            )}

            <div className="mt-4 grid grid-cols-2 gap-3">
              <label className="block">
                <span className="mb-1 block text-xs text-slate-600">Método</span>
                <select className="input" value={payForm.method}
                  onChange={(e) => setPayForm((f) => ({ ...f, method: e.target.value }))}>
                  <option value="transferencia">Transferencia</option>
                  <option value="efectivo">Efectivo</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-slate-600">Monto (MXN)</span>
                <input type="number" min="0.01" step="0.01" className="input" value={payForm.amount}
                  onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-slate-600">Fecha del pago</span>
                <input type="date" className="input" value={payForm.paidAt}
                  onChange={(e) => setPayForm((f) => ({ ...f, paidAt: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-slate-600">Pagado hasta</span>
                <input type="date" className="input" value={payForm.paidUntil}
                  onChange={(e) => setPayForm((f) => ({ ...f, paidUntil: e.target.value }))} />
              </label>
            </div>
            {payError && <p className="mt-2 text-xs text-rose-600">{payError}</p>}
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setPayOpen(null)}
                className="rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200">Cerrar</button>
              <button onClick={submitPayment} disabled={working}
                className="rounded-xl bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-800 disabled:opacity-50">
                {working ? "Guardando…" : "Registrar pago"}
              </button>
            </div>

            <div className="mt-5 border-t border-slate-100 pt-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Historial</p>
              {payHistory.length === 0 && <p className="mt-2 text-xs text-slate-400">Sin pagos registrados.</p>}
              <div className="mt-2 max-h-52 space-y-1.5 overflow-y-auto">
                {payHistory.map((p) => (
                  <div key={p.id}
                    className={`flex items-center justify-between rounded-lg border border-slate-100 px-3 py-1.5 text-xs ${p.voidedAt ? "opacity-50" : ""}`}>
                    <span className={p.voidedAt ? "line-through" : ""}>
                      <span className="font-medium text-slate-700 capitalize">{p.method}</span>
                      <span className="text-slate-500"> · ${Number(p.amount).toLocaleString("es-MX")} · pagó {fmtDate(p.paidAt)} · hasta {fmtDate(p.paidUntil)}</span>
                      {p.recordedByEmail && <span className="text-slate-400"> · {p.recordedByEmail}</span>}
                    </span>
                    {p.voidedAt ? (
                      <span className="text-[10px] text-rose-500">Anulado</span>
                    ) : (
                      <button onClick={() => voidPayment(p.id)} disabled={working}
                        className="rounded bg-white px-2 py-0.5 text-[10px] font-medium text-rose-600 ring-1 ring-rose-200 hover:bg-rose-50">
                        Anular
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* relay v5 §1 — modal "Descuento" retirado: la escritura de descuentos vive SOLO
          en /platform (Otorgar/Revocar Fundador con auditoría). Aquí la insignia lee del motor. */}

      {/* feat/modular-plans — selección MODULAR à la carte (sin auto-bundling). */}
      {modOpen && (() => {
        // Suma de REFERENCIA de los módulos elegidos (NO es el cobro). El combo
        // completo se mantiene en $2,700 (beneficio temporal). Solo display.
        const allSelected = MODULE_ORDER.every((m) => modSel.includes(m));
        const refSum = modSel.reduce((acc, m) => acc + (MODULE_META[m]?.priceMxn ?? 0), 0);
        return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="font-serif text-lg tracking-tight text-slate-900">Módulos de {modOpen.slug}</h3>
            <p className="mt-1 text-xs text-slate-500">Marca los módulos que usa este tenant. El acceso (sidebar/gating) se deriva de esta selección — sin bundles.</p>

            {/* Atajos: presets que pre-seleccionan módulos (no fuerzan bundle). */}
            <div className="mt-3 flex flex-wrap gap-1.5">
              {PLAN_ORDER.map((id) => (
                <button key={id} type="button" onClick={() => setModSel([...PLAN_META[id].modules])}
                  className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-200">
                  {PLAN_META[id].label}
                </button>
              ))}
            </div>

            <div className="mt-4 space-y-2">
              {MODULE_ORDER.map((m) => {
                const meta = MODULE_META[m];
                const on = modSel.includes(m);
                return (
                  <button key={m} type="button" onClick={() => toggleMod(m)}
                    className={`flex w-full items-center justify-between rounded-xl border p-3 text-left transition ${on ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-slate-200 hover:bg-slate-50"}`}>
                    <span className="flex items-center gap-2.5">
                      <span className={`grid h-4 w-4 place-items-center rounded border ${on ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300"}`}>
                        {on && <CheckCircle2 className="h-3 w-3" />}
                      </span>
                      <span className="text-sm font-medium text-slate-800">{meta.label}</span>
                    </span>
                    <span className="text-[11px] text-slate-500">{fmtMxn(meta.priceMxn)} / mes</span>
                  </button>
                );
              })}
            </div>

            {/* Precio de REFERENCIA (display, no aditivo de cobro). */}
            <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
              {allSelected
                ? <>Combo completo: <strong>{fmtMxn(COMBO_PRICE_MXN)} / mes</strong> (beneficio temporal).</>
                : <>Referencia: <strong>{fmtMxn(refSum)} / mes</strong> ({modSel.length} módulo{modSel.length === 1 ? "" : "s"}).</>}
              <span className="block text-slate-400">Precios de referencia — el cobro à la carte llega después; el combo completo se mantiene en {fmtMxn(COMBO_PRICE_MXN)}.</span>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setModOpen(null)}
                className="rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200">Cancelar</button>
              <button onClick={submitModules} disabled={working || modSel.length === 0}
                className="rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
                {working ? "Guardando…" : "Guardar módulos"}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

function PlatformTicketsView() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const list = await api("/platform/tickets/escalated");
        if (!cancelled) setTickets(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) setError(e?.body?.message || e?.message || "No se pudo cargar");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-serif text-xl tracking-tight text-slate-900">Tickets escalados — cross-tenant</h3>
        <p className="text-sm text-slate-500">Tickets que necesitan atención del owner. Filtrados solo a status=ESCALATED.</p>
      </div>

      {error && <div className="alert-over rounded-xl px-3 py-2 text-xs">{error}</div>}

      <div className="card-soft overflow-x-auto rounded-2xl border border-slate-200/80 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50/60 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Ticket</th>
              <th className="px-4 py-3">Tenant</th>
              <th className="px-4 py-3">Reporta</th>
              <th className="px-4 py-3">Prioridad</th>
              <th className="px-4 py-3">Categoría</th>
              <th className="px-4 py-3">Escalado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td className="px-4 py-6 text-center text-slate-400" colSpan={6}>Cargando…</td></tr>}
            {!loading && tickets.length === 0 && (
              <tr><td className="px-4 py-8 text-center text-slate-400" colSpan={6}>Sin tickets escalados ahora mismo.</td></tr>
            )}
            {!loading && tickets.map(t => (
              <tr key={t.ticketId}>
                <td className="px-4 py-3 font-medium text-slate-900">{t.title}</td>
                <td className="px-4 py-3">
                  <div className="text-slate-700">{t.tenantName}</div>
                  <div className="font-mono text-[11px] text-slate-400">{t.tenantSlug}</div>
                </td>
                <td className="px-4 py-3 text-slate-600 text-xs">{t.createdByEmail}</td>
                <td className="px-4 py-3"><TicketPriorityChip priority={t.priority} /></td>
                <td className="px-4 py-3 text-slate-600 text-xs">{t.category}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{t.escalatedAt ? fmtDate(t.escalatedAt) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* Capa 2 (V96) — operación de CFDI de VENTAS (tenant → su cliente):
   emisores + candado issuer_enabled (el "GO" tras el gate legal), consumo de
   timbres por tenant/capa (pool compartido absorbido por FluidSol) y cola de
   CFDIs con reintento manual. El backend re-valida todo con PlatformAdminGuard
   + fns 42501. */
/**
 * Capa 1 — "Facturas de suscripción": los CFDIs que FLUIDSOL emite a los
 * tenants (suscripción + adicionales vía Stripe). Aquí FluidSol ES el emisor,
 * así que ver al receptor es legítimo (SUS ingresos) — distinto de la
 * privacidad de Capa 2. Solo lectura + retry manual (la cola ya tiene cron) +
 * descargas. Todo vía fn_platform_cfdi_list (DEFINER V64; el JOIN a tenants
 * vive dentro — muro V56) con gate platform-admin doble.
 */
function PlatformCfdiSubsView() {
  const [cfdis, setCfdis] = useState([]);
  const [status, setStatus] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(null);
  const PAGE = 20;

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (tenantId) params.set("tenantId", tenantId);
      if (from) params.set("from", new Date(`${from}T00:00:00`).toISOString());
      if (to) params.set("to", new Date(new Date(`${to}T00:00:00`).getTime() + 24 * 3_600_000).toISOString());
      const r = (await api(`/platform/cfdi${params.toString() ? `?${params}` : ""}`)) as any;
      setCfdis(r?.cfdis ?? []);
      setPage(0);
    } catch (e) {
      setError(e?.body?.message || e?.message || "No se pudo cargar");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status, tenantId, from, to]);

  const retry = async (id) => {
    setWorking(id);
    try { await api(`/platform/cfdi/${id}/retry`, { method: "POST" }); await load(); }
    catch (e) { alert(e?.body?.message || "Reintento falló"); }
    finally { setWorking(null); }
  };

  // Opciones del filtro de tenant: derivadas de lo cargado (sin filtro tenant).
  const tenantOptions = Array.from(
    new Map(cfdis.map((c) => [c.tenant_id, c.tenant_name])).entries(),
  );

  const STATUS_CLS = {
    stamped: "bg-emerald-50 text-emerald-700",
    failed: "bg-rose-50 text-rose-700",
    needs_data: "bg-amber-50 text-amber-700",
    pending: "bg-slate-100 text-slate-600",
    opted_out: "bg-slate-100 text-slate-400",
  };

  const pages = Math.max(1, Math.ceil(cfdis.length / PAGE));
  const pageItems = cfdis.slice(page * PAGE, (page + 1) * PAGE);
  const inputCls = "rounded-xl border border-slate-200 px-3 py-1.5 text-sm text-slate-700";

  return (
    <div>
      <h3 className="font-serif text-xl tracking-tight text-slate-900">Facturas de suscripción (Capa 1)</h3>
      <p className="text-sm text-slate-500">
        CFDIs que FluidSol emite a los tenants por su suscripción y adicionales — tus ingresos como emisor.
      </p>
      {error && <p className="mt-2 text-sm text-rose-600">{error}</p>}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-[11px]">
          {["", "stamped", "failed", "pending", "needs_data", "opted_out"].map((s) => (
            <button key={s || "all"} onClick={() => setStatus(s)}
              className={`rounded px-2 py-0.5 ${status === s ? "bg-slate-900 text-white" : "text-slate-500"}`}>
              {s === "" ? "Todos" : s}
            </button>
          ))}
        </div>
        <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} className={inputCls}>
          <option value="">Todos los tenants</option>
          {tenantOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} />
        <span className="text-xs text-slate-400">→</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} />
        {(from || to) && (
          <button onClick={() => { setFrom(""); setTo(""); }} className="text-xs text-slate-500 underline">limpiar</button>
        )}
      </div>

      <div className="mt-3 max-h-[32rem] overflow-x-auto overflow-y-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-4 py-2">Fecha</th><th className="px-4 py-2">Tenant</th>
              <th className="px-4 py-2">Concepto</th><th className="px-4 py-2">Total</th>
              <th className="px-4 py-2">Estado</th><th className="px-4 py-2">Folio fiscal</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pageItems.map((c) => (
              <tr key={c.id}>
                <td className="px-4 py-2 text-xs text-slate-500">
                  {new Date(c.created_at).toLocaleDateString("es-MX")}
                </td>
                <td className="px-4 py-2">
                  <span className="font-medium text-slate-800">{c.tenant_name}</span>
                  {c.receptor_rfc && <span className="block font-mono text-[10px] text-slate-400">{c.receptor_rfc}</span>}
                </td>
                <td className="px-4 py-2 text-xs text-slate-500" title={c.stripe_invoice_id}>
                  Suscripción · <span className="font-mono">{(c.stripe_invoice_id ?? "").slice(0, 14)}…</span>
                </td>
                <td className="px-4 py-2 tabular-nums">
                  ${Number(c.total).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                </td>
                <td className="px-4 py-2" title={c.error ?? ""}>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_CLS[c.status] ?? "bg-slate-100 text-slate-600"}`}>
                    {c.status}
                  </span>
                </td>
                <td className="max-w-[180px] truncate px-4 py-2 font-mono text-[11px]" title={c.folio_uuid ?? ""}>
                  {c.folio_uuid ?? "—"}
                </td>
                <td className="px-4 py-2">
                  <div className="flex justify-end gap-1.5">
                    {c.has_xml && (
                      <button onClick={() => apiDownload(`/platform/cfdi/${c.id}/download/xml`, `CFDI-${c.folio_uuid ?? c.id}.xml`)}
                        className="rounded-lg border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50">XML</button>
                    )}
                    {c.has_pdf && (
                      <button onClick={() => apiDownload(`/platform/cfdi/${c.id}/download/pdf`, `CFDI-${c.folio_uuid ?? c.id}.pdf`)}
                        className="rounded-lg border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50">PDF</button>
                    )}
                    {(c.status === "failed" || c.status === "needs_data" || c.status === "pending") && (
                      <button onClick={() => retry(c.id)} disabled={working === c.id}
                        className="rounded-lg bg-slate-900 px-2 py-1 text-[11px] font-medium text-white hover:bg-slate-700 disabled:opacity-50">
                        {working === c.id ? "…" : "Reintentar"}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && cfdis.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-slate-400">Sin CFDIs con estos filtros.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="mt-3 flex items-center justify-end gap-2 text-xs text-slate-500">
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
            className="rounded-lg border border-slate-200 px-2 py-1 hover:bg-slate-50 disabled:opacity-40">←</button>
          <span>Página {page + 1} de {pages}</span>
          <button onClick={() => setPage(Math.min(pages - 1, page + 1))} disabled={page >= pages - 1}
            className="rounded-lg border border-slate-200 px-2 py-1 hover:bg-slate-50 disabled:opacity-40">→</button>
        </div>
      )}
    </div>
  );
}

/**
 * V98 — PRIVACIDAD: el contenido de las facturas (folio/total/receptor) es DEL
 * TENANT y aquí NO aparece. FluidSol solo ve estatus del emisor, el gate legal
 * POR ESTATUS (contrato/aviso), conteos (timbres + failed) y el candado
 * auditado. El detalle vive en la vista tenant "Mis facturas".
 */
function PlatformCfdiSalesView() {
  const [issuers, setIssuers] = useState([]);
  const [consumption, setConsumption] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(null);

  const MODULES = ["gym", "boutique", "gastro"];

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [i, c] = (await Promise.all([
        api("/platform/cfdi-sales/issuers"),
        api("/platform/cfdi-sales/consumption"),
      ])) as any[];
      setIssuers(i?.issuers ?? []);
      setConsumption(c?.consumption ?? []);
    } catch (e) {
      setError(e?.body?.message || e?.message || "No se pudo cargar (¿CFDI_ISSUER_ENABLED apagado?)");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // V199 — version-aware: el candado solo abre con la aceptación de la versión
  // VIGENTE del contrato (una aceptación de versión anterior / placeholder no cuenta).
  const gateReady = (it) => Boolean(it.contract_current && it.privacy_attested_at);

  const toggleIssuer = async (it, enabled, modules) => {
    // Sin window.alert (regla): el botón ya está deshabilitado cuando el gate no está
    // listo (gateReady), y el servidor enforca el gate — fn_platform_issuer_set_enabled
    // lanza gate_legal_incompleto → 409 "Gate legal incompleto: ...". Ese 409 (y cualquier
    // error de la mutación) lo muestra el interceptor global (ToastHost). No se duplica aquí.
    setWorking(it.tenant_id);
    try {
      const r = (await api("/platform/cfdi-sales/issuers/enabled", {
        method: "PUT", body: { tenantId: it.tenant_id, enabled, modules },
      })) as any;
      setIssuers(r?.issuers ?? []);
    } catch {
      /* el interceptor (ToastHost) ya muestra el error; sin window.alert */
    } finally { setWorking(null); }
  };

  return (
    <div className="space-y-8">
      {/* T2b (C-04): sonda de la llave del PAC, la misma llamada que hace el timbrado. */}
      <PacProbeCard />
      <div>
        <h3 className="font-serif text-xl tracking-tight text-slate-900">CFDI Ventas — emisores</h3>
        <p className="text-sm text-slate-500">
          Solo estatus y conteos: el contenido de las facturas es del tenant. El candado exige contrato
          firmado + aviso confirmado (lo valida también la base de datos) y queda auditado.
        </p>
        {error && <p className="mt-2 text-sm text-rose-600">{error}</p>}
        <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-4 py-2">Tenant</th><th className="px-4 py-2">RFC</th>
                <th className="px-4 py-2">CSD</th><th className="px-4 py-2">Manifiesto</th>
                <th className="px-4 py-2">Contrato</th><th className="px-4 py-2">Aviso</th>
                <th className="px-4 py-2">Failed</th>
                <th className="px-4 py-2">Módulos</th><th className="px-4 py-2">Candado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {issuers.map((it) => (
                <tr key={it.tenant_id}>
                  <td className="px-4 py-2 font-medium text-slate-800">{it.tenant_name}</td>
                  <td className="px-4 py-2 font-mono text-xs">{it.rfc}</td>
                  <td className="px-4 py-2 text-xs">
                    {/* V222 (C-17): un CSD vencido ya no sale como "✓"; ≤ 30 días, en ámbar. */}
                    {it.csd_status !== "uploaded" ? "—" : (() => {
                      const to = it.csd_valid_to ? new Date(it.csd_valid_to) : null;
                      if (!to) return "✓";
                      const left = to.getTime() - Date.now();
                      const day = to.toLocaleDateString("es-MX");
                      if (left <= 0) return <span className="rounded-full bg-rose-50 px-2 py-0.5 font-medium text-rose-700">✕ venció {day}</span>;
                      if (left <= 30 * 86_400_000) return <span className="rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-700">vence {day}</span>;
                      return `✓ vence ${day}`;
                    })()}
                  </td>
                  <td className="px-4 py-2 text-xs">{it.manifest_status === "signed" ? "✓ firmado" : "—"}</td>
                  <td className="px-4 py-2 text-xs" title={it.contract_version ?? ""}>
                    {it.contract_current
                      ? <span className="text-emerald-700">✓ {it.contract_signed_at ? new Date(it.contract_signed_at).toLocaleDateString("es-MX") : ""} (vigente)</span>
                      : it.contract_signed_at
                        ? <span className="text-amber-600" title={`Aceptó ${it.contract_version ?? "una versión anterior"}`}>versión anterior — re-aceptar</span>
                        : <span className="text-amber-600">pendiente</span>}
                  </td>
                  <td className="px-4 py-2 text-xs" title={it.privacy_notice_version ?? ""}>
                    {it.privacy_attested_at ? (
                      <span className="text-emerald-700">
                        ✓ {new Date(it.privacy_attested_at).toLocaleDateString("es-MX")}
                        {it.privacy_notice_url && (
                          <> · <a href={it.privacy_notice_url} target="_blank" rel="noreferrer" className="underline">ver</a></>
                        )}
                      </span>
                    ) : <span className="text-amber-600">pendiente</span>}
                  </td>
                  <td className="px-4 py-2 tabular-nums text-xs">
                    {Number(it.failed_count) > 0
                      ? <span className="rounded-full bg-rose-50 px-2 py-0.5 font-medium text-rose-700">{it.failed_count}</span>
                      : "0"}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      {MODULES.map((m) => {
                        const on = (it.cfdi_modules ?? []).includes(m);
                        return (
                          <button key={m}
                            onClick={() => toggleIssuer(it, it.issuer_enabled,
                              on ? (it.cfdi_modules ?? []).filter((x) => x !== m) : [...(it.cfdi_modules ?? []), m])}
                            disabled={working === it.tenant_id}
                            className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${on ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"}`}>
                            {m}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <button onClick={() => toggleIssuer(it, !it.issuer_enabled, it.cfdi_modules)}
                      disabled={working === it.tenant_id || (!it.issuer_enabled && !gateReady(it))}
                      title={!it.issuer_enabled && !gateReady(it)
                        ? "Gate legal incompleto: falta contrato firmado y/o aviso confirmado"
                        : it.enabled_changed_at
                          ? `Último cambio: ${new Date(it.enabled_changed_at).toLocaleString("es-MX")} por ${it.enabled_changed_by_email ?? "?"}`
                          : ""}
                      className={`rounded-full px-2.5 py-1 text-[11px] font-semibold disabled:opacity-40 ${it.issuer_enabled ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-600"}`}>
                      {it.issuer_enabled ? "ACTIVO" : "OFF"}
                    </button>
                    {it.enabled_changed_at && (
                      <p className="mt-1 text-[10px] text-slate-400">
                        {new Date(it.enabled_changed_at).toLocaleDateString("es-MX")} · {it.enabled_changed_by_email ?? "?"}
                      </p>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && issuers.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-6 text-center text-sm text-slate-400">Sin emisores configurados.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="font-serif text-xl tracking-tight text-slate-900">Consumo de timbres (pool compartido)</h3>
        <p className="text-sm text-slate-500">
          El pool lo absorbe FluidSol: vigila el consumo por tenant para escalar el paquete a tiempo
          (alerta automática al 80% vía CFDI_TIMBRES_POOL_SIZE/SINCE).
        </p>
        <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400">
              <tr><th className="px-4 py-2">Tenant</th><th className="px-4 py-2">Capa</th>
                <th className="px-4 py-2">Timbres</th><th className="px-4 py-2">Último</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {consumption.map((c, i) => (
                <tr key={i}>
                  <td className="px-4 py-2 text-slate-800">{c.tenant_name}</td>
                  <td className="px-4 py-2 text-xs">{c.capa === "capa2_ventas" ? "Ventas (Capa 2)" : "Suscripción (Capa 1)"}</td>
                  <td className="px-4 py-2 tabular-nums">{c.stamped_count}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{c.last_stamped ? new Date(c.last_stamped).toLocaleString("es-MX") : "—"}</td>
                </tr>
              ))}
              {!loading && consumption.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-slate-400">Sin timbres consumidos aún.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* V98 — aquí vivía la lista cross-tenant CON CONTENIDO (folio/total/
          receptor). Eliminada: FluidSol es encargado, no ve facturas del
          tenant. El detalle + reintento viven en la vista tenant "Mis
          facturas"; el soporte platform se hace con el conteo de failed. */}
    </div>
  );
}

/**
 * Comentarios / Sugerencias — vista cross-tenant solo platform-admin (junto a
 * Tenants y Tickets). Lista de POST /feedback de TODOS los tenants; "marcar
 * revisado" (PATCH). Mismo patrón que PlatformTicketsView.
 */
function PlatformFeedbackView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(null);
  const [filter, setFilter] = useState("ALL"); // ALL | NEW | REVIEWED

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const list = await api("/platform/feedback");
      setItems(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.body?.message || e?.message || "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setWorking(id);
    try {
      await api(`/platform/feedback/${id}`, { method: "PATCH", body: { status } });
      await load();
    } catch (e) {
      alert(e?.body?.message || "No se pudo actualizar");
    } finally { setWorking(null); }
  };

  const CAT_LABEL = { SUGERENCIA: "Sugerencia", PROBLEMA: "Problema", OTRO: "Otro" };
  const CAT_CLS = {
    SUGERENCIA: "bg-emerald-50 text-emerald-700",
    PROBLEMA: "bg-rose-50 text-rose-700",
    OTRO: "bg-slate-100 text-slate-600",
  };
  const filtered = items.filter((f) => filter === "ALL" || f.status === filter);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-serif text-xl tracking-tight text-slate-900">Comentarios / Sugerencias — cross-tenant</h3>
          <p className="text-sm text-slate-500">Lo que reportan los usuarios de todos los tenants.</p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-[11px]">
          {["ALL", "NEW", "REVIEWED"].map((s) => (
            <button key={s} onClick={() => setFilter(s)}
              className={`rounded px-2 py-0.5 ${filter === s ? "bg-slate-900 text-white" : "text-slate-500"}`}>
              {s === "ALL" ? "Todos" : s === "NEW" ? "Nuevos" : "Revisados"}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="alert-over rounded-xl px-3 py-2 text-xs">{error}</div>}

      <div className="card-soft overflow-x-auto rounded-2xl border border-slate-200/80 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50/60 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Categoría</th>
              <th className="px-4 py-3">Mensaje</th>
              <th className="px-4 py-3">Tenant</th>
              <th className="px-4 py-3">Usuario</th>
              <th className="px-4 py-3">Fecha</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td className="px-4 py-6 text-center text-slate-400" colSpan={7}>Cargando…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td className="px-4 py-8 text-center text-slate-400" colSpan={7}>Sin comentarios.</td></tr>
            )}
            {!loading && filtered.map((f) => (
              <tr key={f.id}>
                <td className="px-4 py-3"><span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${CAT_CLS[f.category] ?? "bg-slate-100 text-slate-600"}`}>{CAT_LABEL[f.category] ?? f.category}</span></td>
                <td className="px-4 py-3 max-w-md text-slate-700">{f.message}</td>
                <td className="px-4 py-3">
                  <div className="text-slate-700">{f.tenantName}</div>
                  <div className="font-mono text-[11px] text-slate-400">{f.tenantSlug}</div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">{f.userName || f.userEmail}<div className="text-[11px] text-slate-400">{f.userEmail}</div></td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(f.createdAt)}</td>
                <td className="px-4 py-3">
                  {f.status === "REVIEWED"
                    ? <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase bg-slate-100 text-slate-500">Revisado</span>
                    : <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase bg-amber-50 text-amber-700">Nuevo</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  {f.status === "NEW" ? (
                    <button disabled={working === f.id} onClick={() => setStatus(f.id, "REVIEWED")}
                      className="rounded-lg bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50">
                      {working === f.id ? "…" : "Marcar revisado"}
                    </button>
                  ) : (
                    <button disabled={working === f.id} onClick={() => setStatus(f.id, "NEW")}
                      className="rounded-lg px-2.5 py-1 text-[11px] font-medium text-slate-400 hover:text-slate-700 disabled:opacity-50">
                      Reabrir
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ===========================================================================
   App (raíz)
   =========================================================================== */
/**
 * Mapea la respuesta de /me al shape que usa el Sidebar/Topbar.
 * Day 4 hotfix BUG#2: antes había un mock hardcoded acá, ahora viene del JWT.
 */
function meToUser(me) {
  if (!me) return { name: "—", email: "", role: "COMMON_USER", permissions: [] };
  const name = [me.firstName, me.lastName].filter(Boolean).join(" ").trim() || me.email;
  return {
    name,
    email: me.email ?? "",
    role: me.role ?? "COMMON_USER",
    permissions: Array.isArray(me.permissions) ? me.permissions : [],
  };
}

/**
 * 2.2 — Mapea una Task del backend al shape del Kanban. `assignee` es el NOMBRE
 * del usuario (la UI filtra y dibuja por nombre); `assignedUserId` se conserva
 * para los PATCH. estimatedHours/actualHours (numeric → string) se coaccionan.
 */
function mapTaskFromApi(t, usrMap) {
  return {
    id: t.id,
    projectId: t.projectId,
    title: t.title,
    status: t.status,
    assignedUserId: t.assignedUserId ?? null,
    assignee: (t.assignedUserId && usrMap.get(t.assignedUserId)) || "Sin asignar",
    hoursEst: Number(t.estimatedHours ?? 0),
    hoursLogged: Number(t.actualHours ?? 0),
  };
}

// F0: mapea la entidad Project del API → shape del Kanban/cartera. Fuente ÚNICA:
// la usan tanto la hidratación inicial como handleCreateProject (antes el create
// fabricaba un objeto en memoria con id/folio falsos que nunca se persistía).
// `date` de Postgres vuelve como string "YYYY-MM-DD" → se pasa tal cual (sin
// new Date(), que correría el día en UTC-6; ver docs/DIAG_DESFASE_FECHAS.md).
function mapProjectFromApi(p, clientName = "—", ownerName = "—") {
  const budgetedHours = Number(p.budgetedHours ?? 0);
  const hoursLogged = Number(p.hoursLogged ?? 0);
  return {
    id: p.id,
    code: p.code ?? "",
    clientId: p.endClientId ?? null,
    name: p.name,
    client: clientName,
    owner: ownerName,
    status: p.status,
    progress: budgetedHours > 0 ? Math.min(100, Math.round((hoursLogged / budgetedHours) * 100)) : 0,
    dueDate: p.endDate ?? null,
    startDate: p.startDate ?? null,
    budget: Number(p.budget ?? 0),
    budgetedHours,
    hoursLogged,
    hourlyRate: Number(p.hourlyRate ?? 0),
    budgetCurrency: p.budgetCurrency ?? "USD",
    approvalStatus: p.approvalStatus ?? "APPROVED",
  };
}

// v1.4.6 — Recordatorios tiene URL propia. Si la app carga en /recordatorios,
// abre esa vista directo (no el default). El resto de secciones siguen siendo
// tabs sin ruta. Patrón pathname (igual que /signup), sin react-router.
const RECORDATORIOS_PATH = "/recordatorios";
const isRecordatoriosPath = () =>
  window.location.pathname.replace(/\/+$/, "") === RECORDATORIOS_PATH;

// §17.3 — landing por default según el plan/módulos: un tenant SIN `crm` no debe
// caer en "Proyectos" (oculta sin el módulo). Aterriza en la primera vista del plan.
function defaultActiveForModules(me: any): string {
  const mods = me?.tenant?.enabledModules?.length ? me.tenant.enabledModules : ["crm", "boutique", "gastro"];
  if (mods.includes("crm")) return "projects";        // crm/mixto → como hoy
  if (mods.includes("gym")) return "gym-members";     // wellness (sin crm) → sección gym
  if (mods.includes("boutique")) return "boutique-services";
  if (mods.includes("gastro")) return "gastro-pos";   // Mesas
  return "contacts";                                   // transversal, siempre disponible
}

// Tarea 3 — recordar la última vista para que un refresh no regrese siempre a
// Mesas. Solo persistimos vistas "top-level"; las de drill-down dependen de un id
// que NO persistimos (openProjectId/profileContactId), así que restaurarlas
// dejaría la pantalla en blanco.
const ACTIVE_VIEW_KEY = "stratos.activeView";
const NON_PERSISTED_VIEWS = new Set(["project-hub", "customer-profile", "tickets-digest"]);
function readSavedActiveView(): string | null {
  try {
    const v = window.localStorage.getItem(ACTIVE_VIEW_KEY);
    return v && !NON_PERSISTED_VIEWS.has(v) ? v : null;
  } catch { return null; }
}

export default function Dashboard({ onLogout = () => {}, me = null }: { onLogout?: () => void; me?: any } = {}) {
  // Deep-link de recordatorios manda; si no, restauramos la última vista guardada
  // (Tarea 3) y si no hay, el default por módulos. La sucursal activa ya sobrevive
  // al refresh: vive en el JWT (activeBranchId), no hace falta persistirla aparte.
  const [active, setActive] = useState(() =>
    isRecordatoriosPath() ? "boutique-reminders" : (readSavedActiveView() ?? defaultActiveForModules(me))); // crm | projects | project-hub | clients | settings | boutique-reminders
  // Persistimos la vista en cada cambio (saltando las de drill-down).
  useEffect(() => {
    if (NON_PERSISTED_VIEWS.has(active)) return;
    try { window.localStorage.setItem(ACTIVE_VIEW_KEY, active); } catch { /* quota */ }
  }, [active]);
  // V222 — banners fuera del Dashboard (p. ej. el de facturación) piden abrir una vista.
  useEffect(() => {
    const onNavigate = (e: Event) => {
      const view = (e as CustomEvent<string>).detail;
      if (typeof view === "string" && view) setActive(view);
    };
    window.addEventListener("fluidsol:navigate", onNavigate);
    return () => window.removeEventListener("fluidsol:navigate", onNavigate);
  }, []);
  // V92 — entrada "Enviar comunicado" desde Contactos: abre la sección con el
  // composer ya abierto (one-shot; ComunicadosView lo consume al montar).
  const [commAutoCompose, setCommAutoCompose] = useState(false);
  const [openProjectId, setOpenProjectId] = useState(null);
  const [open, setOpen] = useState(false);
  // fix/mobile-drawer: scroll-lock del fondo mientras el drawer está abierto. Sin
  // esto el body queda en overflow:visible y los gestos de scroll mueven la página
  // de atrás en vez del menú (sensación de "atorado"). El botón que abre el drawer
  // es md:hidden, así que `open` solo es true en móvil; el cleanup restaura siempre
  // (al cerrar, navegar o desmontar) para que el scroll del body no quede pegado.
  useEffect(() => {
    if (!open) return;
    document.body.classList.add("overflow-hidden");
    return () => { document.body.classList.remove("overflow-hidden"); };
  }, [open]);
  const [logOpen, setLogOpen] = useState(false);
  const [logDefaultPid, setLogDefaultPid] = useState(null);

  // Carril A PR2 — Ayuda in-app, modelo PROGRESIVO (elegido por Arturo):
  //  · El tour COMPLETO (driver.js, vertical-first) es re-lanzable on-demand desde
  //    "¿Cómo funciona?" / el Centro de ayuda — NO auto-inicia en el primer login.
  //  · En su lugar, un coach-mark aparece la 1a vez que el usuario entra a cada
  //    sección (help/coach.ts, "visto" por-usuario en localStorage). No bloquea.
  const startTour = useCallback(() => {
    startGuidedTour(me, () => { api("/me/tour-completed", { method: "PATCH" }).catch(() => {}); });
  }, [me]);
  useEffect(() => {
    if (!me) return;
    // Deja montar el sidebar/vista antes del spotlight; el coach se auto-omite si
    // ya se vio, si no aplica al tenant, o si el ancla aún no existe.
    const t = setTimeout(() => { showSectionCoach(me, active, me?.userId); }, 500);
    return () => clearTimeout(t);
  }, [me, active]);

  // Mantiene la URL en sync con la sección Recordatorios (deep-link compartible),
  // sin ensuciar el historial. Otras secciones limpian la URL a "/".
  useEffect(() => {
    const onReminders = active === "boutique-reminders";
    const path = onReminders ? RECORDATORIOS_PATH : "/";
    if (window.location.pathname.replace(/\/+$/, "") !== path.replace(/\/+$/, "")) {
      try { window.history.replaceState(null, "", path); } catch { /* noop */ }
    }
  }, [active]);

  // Day 4 hotfix BUG#1+#2: user viene del prop `me` (que sale de /me), NO de
  // un mock hardcoded. Si por alguna razón no hay me (loading raro), caemos a
  // un placeholder vacío — el Sidebar se renderiza pero sin nombre, evitando
  // mostrar "Arturo Garduño / SUPER_USER" a un user nuevo.
  const [user, setUser] = useState(() => meToUser(me));

  // Day 4 hotfix BUG#1: TODOS los mocks se reemplazan por arrays vacíos.
  // Los useEffect debajo poblan desde el backend (vía RLS por tenant del JWT).
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks]       = useState([]);   // sin endpoint /tasks aún
  const [clients, setClients]   = useState([]);
  // Fuente ÚNICA de personas: hook compartido (provider en App.tsx). Reemplaza el
  // viejo contactsState local que se hidrataba con params inválidos
  // (pageSize=500 > max 100, sortBy=firstName inválido → 400 → lista vacía: ese
  // era el bug del dropdown). El hook usa params válidos + paginación y expone
  // create/update que dejan el contacto disponible en TODOS los selectores.
  const { contacts: contactsState, createContact, updateContact } = useContacts();
  const [tagsState, setTagsState]         = useState([]);
  const [usersState, setUsersState]       = useState([]);

  // v1.1.2 Fase B: moneda de visualización + tabla FX editable.
  // Day 5 Block D: inicial desde me.tenant.displayCurrency. Cambios persisten
  // vía PATCH /me/tenant/display-currency con fallback silencioso si falla
  // (ej. user sin settings:currency).
  const [displayCurrency, setDisplayCurrencyRaw] = useState(() => me?.tenant?.displayCurrency ?? "USD");
  const setDisplayCurrency = (next) => {
    setDisplayCurrencyRaw(next);
    if (next === (me?.tenant?.displayCurrency ?? "USD")) return;
    api("/me/tenant/display-currency", {
      method: "PATCH",
      body: { displayCurrency: next },
    }).catch(() => { /* swallow: el state local ya cambió, el banner FX informa si falta tasa */ });
  };
  const [fxRates, setFxRates] = useState([]);

  // Day 4 hotfix BUG#1: hidratar desde backend al montar. Errores son silenciados
  // a nivel listado (RLS o permisos faltantes devuelven 403 → array vacío).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const safeGet = async (path) => {
        try { const r = await api(path); return Array.isArray(r) ? r : []; }
        catch { return []; }
      };
      // NOTA: los contactos ya NO se hidratan aquí — son fuente única en el hook
      // useContacts (provider en App.tsx). Antes este Promise.all los traía con
      // params inválidos (pageSize=500/sortBy=firstName) → 400 → dropdown vacío.
      const [prj, ecs, usrs, tags, fx, tsk] = await Promise.all([
        // Day 6 Block D: admin ve también proyectos PENDING (?includeStatus=ALL).
        safeGet(me?.role === "SUPER_USER" ? "/projects?includeStatus=ALL" : "/projects"),
        safeGet("/end-clients"),
        safeGet("/users"),
        safeGet("/activity-tags"),
        safeGet("/currencies/rates"),
        safeGet("/tasks"),   // 2.2: tareas reales (antes useState([]) efímero)
      ]);
      if (cancelled) return;
      // Day 5 hotfix BUG#4: hidratar fxRates para que fmtUsdInDisplay()
      // pueda convertir USD→display ccy en todas las vistas.
      setFxRates(fx);
      // Mapeo Project entity → shape esperado por la UI.
      // 2.4/2.5: budgetedHours, hoursLogged y hourlyRate ahora vienen reales en
      // GET /projects (hoursLogged lo agrega view_project_profitability DB-side;
      // budgetedHours/hourlyRate son columnas del proyecto). progress se deriva
      // del consumo real de horas. Antes estaban hardcodeados a 0 → "Avance por
      // cliente final", la pantalla Clientes y los costos salían vacíos.
      const ecMap = new Map(ecs.map(c => [c.id, c.name]));
      const usrMap = new Map(usrs.map(u => [u.id, [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.email]));
      setProjects(prj.map(p => mapProjectFromApi(
        p,
        ecMap.get(p.endClientId) ?? "—",
        usrMap.get(p.managerUserId) ?? "—",
      )));
      setClients(ecs.map(c => ({
        id: c.id, name: c.name, isActive: c.isActive !== false,
        approvalStatus: c.approvalStatus ?? "APPROVED",
      })));
      setUsersState(usrs.map(u => ({
        id: u.id,
        name: [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.email,
        email: u.email,
        role: u.role,
        hourlyRate: Number(u.hourlyRate ?? 0),
        isActive: u.isActive !== false,
      })));
      // 2.2: mapeo Task entity → shape del Kanban. assignee es el NOMBRE (la UI
      // filtra/dibuja por nombre); el id real del usuario viaja como assignedUserId.
      setTasks(tsk.map(t => mapTaskFromApi(t, usrMap)));
      setTagsState(tags.map(t => ({
        id: t.id, name: t.name, color: t.color ?? "#475569",
        isActive: t.isActive !== false,
        isBillableDefault: t.isBillableDefault ?? false,
      })));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleUpsertFx = (rate) => {
    setFxRates(rs => {
      const idx = rs.findIndex(r => r.currencyCode === rate.currencyCode && r.effectiveMonth === rate.effectiveMonth);
      if (idx >= 0) { const copy = [...rs]; copy[idx] = { ...rs[idx], ...rate }; return copy; }
      return [...rs, { id:`fx-${Date.now()}`, source:"MANUAL", ...rate }];
    });
  };
  const handleFetchBmv = (code) => {
    // Stub: simula traer el rate desde BMV. En producción esto llama al backend.
    const stub = { MXN: 17.42, BRL: 5.08 };
    if (stub[code]) {
      handleUpsertFx({ currencyCode: code, effectiveMonth: new Date().toISOString().slice(0,7), rateToUsd: stub[code], source: "BMV" });
    }
  };

  // Estados de modales admin
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [clientModal, setClientModal]       = useState({ open: false, initial: null });
  const [newTaskOpen, setNewTaskOpen]       = useState(false);
  const [contactModal, setContactModal]     = useState({ open: false, initial: null });
  const [tagModal, setTagModal]             = useState({ open: false, initial: null });

  const isAdmin = user.role === "SUPER_USER";

  const toggleRole = () =>
    setUser(u => ({ ...u, role: u.role === "SUPER_USER" ? "COMMON_USER" : "SUPER_USER" }));

  const openProject = (pid) => { setOpenProjectId(pid); setActive("project-hub"); };
  const backToProjects = () => { setOpenProjectId(null); setActive("projects"); };
  // 1.4.4 — Customer Profile 360: abrir el perfil de un cliente desde Contactos.
  const [profileContactId, setProfileContactId] = useState(null);
  const openCustomerProfile = (cid) => { setProfileContactId(cid); setActive("customer-profile"); };
  const openLogHours = (pid = null) => { setLogDefaultPid(pid); setLogOpen(true); };

  // Handlers admin
  // Bloque B (Mi Cuenta): cuando el user actualiza su nombre vía /me/profile,
  // refrescamos el state local para que el sidebar y header se actualicen
  // inmediatamente sin esperar al próximo /me.
  const handleProfileUpdated = (me) => {
    const fullName = [me?.firstName, me?.lastName].filter(Boolean).join(" ").trim();
    if (!fullName) return;
    setUser(u => ({ ...u, name: fullName }));
  };

  // F0: persistir el proyecto (antes era setState puro → los 5 proyectos de la
  // demo desaparecían al recargar; prod tenía 0 filas). Update optimista con
  // rollback, igual que handleCreateTask. Si viene un cliente inline nuevo, se
  // persiste PRIMERO en /end-clients para obtener su UUID real (el `cl-<ts>` que
  // fabrica el modal no es una FK válida). Ver docs/DIAG_FLOW_PROYECTOS_PERSISTENCIA.md.
  const isRealUuid = (v) => typeof v === "string" && /^[0-9a-f-]{36}$/i.test(v);
  const handleCreateProject = async (project, newClient) => {
    const tempId = `tmp-${Date.now()}`;
    setProjects(ps => [{ ...project, id: tempId }, ...ps]);
    if (newClient) setClients(cs => [...cs, newClient]);
    try {
      let endClientId = project.clientId;
      let clientName = project.client;
      if (newClient) {
        const createdEc: any = await api("/end-clients", {
          method: "POST",
          body: {
            name: newClient.name,
            industry: newClient.industry || undefined,
            contactEmail: newClient.contactEmail || undefined,
          },
        });
        endClientId = createdEc.id;
        clientName = createdEc.name;
        setClients(cs => cs.map(c => c.id === newClient.id
          ? { id: createdEc.id, name: createdEc.name, isActive: createdEc.isActive !== false, approvalStatus: createdEc.approvalStatus ?? "APPROVED" }
          : c));
      }
      const created = await api("/projects", {
        method: "POST",
        body: {
          name: project.name,
          code: project.code || undefined,
          endClientId: isRealUuid(endClientId) ? endClientId : undefined,
          status: project.status || "PLANNING",
          // strings "YYYY-MM-DD" tal cual — el backend los pasa a la columna DATE
          // sin new Date() (el create no corrompe; el UPDATE sí, ver DIAG_DESFASE_FECHAS).
          startDate: project.startDate || undefined,
          endDate: project.dueDate || undefined,
          budget: project.budget || undefined,
          budgetCurrency: project.budgetCurrency,
          budgetedHours: project.budgetedHours || undefined,
          monthlyBudgetedHours: project.monthlyBudgetedHours || undefined,
        },
      });
      setProjects(ps => ps.map(p => p.id === tempId
        ? mapProjectFromApi(created, clientName, "Tú")
        : p));
    } catch {
      setProjects(ps => ps.filter(p => p.id !== tempId));
      if (newClient) setClients(cs => cs.filter(c => c.id !== newClient.id));
      alert("No se pudo crear el proyecto. Verifica tus permisos e intenta de nuevo.");
    }
  };
  const handleSaveClient = (client) => {
    setClients(cs => {
      const exists = cs.some(c => c.id === client.id);
      if (exists) return cs.map(c => c.id === client.id ? { ...c, ...client } : c);
      return [...cs, { ...client, id: client.id ?? `cl-${Date.now()}` }];
    });
  };
  const handleToggleActiveClient = (client) => {
    setClients(cs => cs.map(c => c.id === client.id ? { ...c, isActive: !c.isActive } : c));
  };

  // v1.1.2 + F0: workflow de aprobación de PROYECTOS, ahora persistido vía el
  // módulo /approvals (SUPER_USER). Antes era setState puro → el aprobado/rechazado
  // se perdía al recargar. Optimista con rollback. Los endpoints exigen UUID real,
  // así que solo operan sobre proyectos ya persistidos (los `tmp-`/`p-` no aplican).
  const handleApproveProject = async (id) => {
    if (!isRealUuid(id)) return;
    const prev = projects;
    setProjects(ps => ps.map(p => p.id === id ? { ...p, approvalStatus: "APPROVED" } : p));
    try {
      await api(`/approvals/projects/${id}/approve`, { method: "POST" });
    } catch {
      setProjects(prev);
      alert("No se pudo aprobar el proyecto. Verifica tus permisos e intenta de nuevo.");
    }
  };
  const handleRejectProject = async (id) => {
    if (!isRealUuid(id)) return;
    const prev = projects;
    setProjects(ps => ps.filter(p => p.id !== id));
    try {
      await api(`/approvals/projects/${id}/reject`, {
        method: "POST",
        body: { reason: "Rechazado desde Aprobaciones" },  // RejectDto exige reason (1-500)
      });
    } catch {
      setProjects(prev);
      alert("No se pudo rechazar el proyecto. Verifica tus permisos e intenta de nuevo.");
    }
  };
  const handleApproveClient  = (id) => setClients(cs => cs.map(c => c.id === id ? { ...c, approvalStatus: "APPROVED" } : c));
  const handleRejectClient   = (id) => setClients(cs => cs.filter(c => c.id !== id));

  const pendingApprovalsCount =
    projects.filter(p => p.approvalStatus === "PENDING").length +
    clients.filter(c => c.approvalStatus === "PENDING").length;

  // Handlers Fase 2
  // 2.2: persistir la creación de la tarea (antes era setState puro → se perdía
  // al refrescar). Update optimista con rollback si el POST falla.
  const handleCreateTask = async (task) => {
    const assignedUserId = usersState.find(u => u.name === task.assignee)?.id ?? null;
    const tempId = `tmp-${Date.now()}`;
    setTasks(ts => [...ts, { ...task, id: tempId, assignedUserId }]);
    try {
      const created = await api("/tasks", {
        method: "POST",
        body: {
          projectId: task.projectId,
          title: task.title,
          status: task.status,
          assignedUserId,
          estimatedHours: Number(task.hoursEst) || 0,
        },
      });
      const usrMap = new Map(usersState.map(u => [u.id, u.name]));
      setTasks(ts => ts.map(t => t.id === tempId ? mapTaskFromApi(created, usrMap) : t));
    } catch {
      setTasks(ts => ts.filter(t => t.id !== tempId));
      alert("No se pudo crear la tarea. Verifica tus permisos e intenta de nuevo.");
    }
  };
  // Persiste el contacto en /contacts (antes era state local efímero con id
  // falso `c-<ts>` → no aparecía en otros módulos ni sobrevivía un refresh).
  // El hook actualiza la fuente única → disponible en TODOS los selectores.
  // `form` viene del ContactFormModal: { id?, name, role, company, email, phone }.
  const handleSaveContact = async (form) => {
    const fullName = String(form.name ?? "").trim().replace(/\s+/g, " ");
    const sp = fullName.indexOf(" ");
    const body = {
      firstName: sp === -1 ? fullName : fullName.slice(0, sp),
      lastName: sp === -1 ? undefined : fullName.slice(sp + 1),
      email: form.email?.trim() || undefined,
      phone: form.phone?.trim() || undefined,
      company: form.company?.trim() || undefined,
      jobTitle: form.role?.trim() || undefined,
    };
    // id real (UUID) → edición; sin id o id local viejo `c-...` → creación.
    const isRealId = typeof form.id === "string" && /^[0-9a-f-]{36}$/i.test(form.id);
    if (isRealId) await updateContact(form.id, body);
    else await createContact(body);
  };
  const handleSaveTag = (tag) => {
    setTagsState(ts => {
      const exists = ts.some(t => t.id === tag.id);
      if (exists) return ts.map(t => t.id === tag.id ? { ...t, ...tag } : t);
      return [...ts, { ...tag, isActive: true }];
    });
  };
  const handleToggleTag = (tag) => {
    setTagsState(ts => ts.map(t => t.id === tag.id ? { ...t, isActive: t.isActive === false ? true : false } : t));
  };
  const handleChangeUserRate = (userId, rate) => {
    setUsersState(us => us.map(u => u.id === userId ? { ...u, hourlyRate: rate } : u));
  };

  // F0: persistir el registro de horas en /timelogs (antes era setState puro →
  // las horas desaparecían al recargar; prod tenía 0 filas en time_logs). El
  // incremento optimista coincide con lo que la vista de rentabilidad recalcula
  // server-side; si el POST falla, se revierte. Ver docs/DIAG_FLOW_PROYECTOS_PERSISTENCIA.md.
  const submitLog = async (entry) => {
    const hrs = Number(entry.hoursSpent);
    const prevProjects = projects;
    const prevTasks = tasks;
    setProjects(ps => ps.map(p => p.id === entry.projectId
      ? { ...p, hoursLogged: p.hoursLogged + hrs }
      : p));
    if (entry.taskId) {
      setTasks(ts => ts.map(t => t.id === entry.taskId
        ? { ...t, hoursLogged: (t.hoursLogged ?? 0) + hrs }
        : t));
    }
    try {
      await api("/timelogs", {
        method: "POST",
        body: {
          projectId: entry.projectId,
          taskId: isRealUuid(entry.taskId) ? entry.taskId : undefined,
          hoursSpent: hrs,
          logDate: entry.logDate || undefined,
          description: entry.description || undefined,
          billable: entry.billable ?? true,
          activityTagId: isRealUuid(entry.activityTagId) ? entry.activityTagId : undefined,
        },
      });
    } catch {
      setProjects(prevProjects);
      setTasks(prevTasks);
      alert("No se pudieron registrar las horas. Verifica tus permisos e intenta de nuevo.");
    }
  };

  const titles = {
    crm:        { title: "Actividades de clientes", crumb: "Flow / Actividad" },
    contacts:   { title: "Contactos",               crumb: "Flow / Contactos" },
    comunicados: { title: "Comunicados",            crumb: "Contactos / Correo masivo" },
    "customer-profile": { title: "Perfil del cliente", crumb: "Flow / Contactos / Perfil" },
    pipeline:   { title: "Pipeline de ventas",      crumb: "Ventas / Embudo" },
    quotes:     { title: "Cotizaciones",            crumb: "Ventas / Cotizaciones" },
    tickets:    { title: "Soporte",                  crumb: "Soporte / Tickets" },
    "tickets-digest": { title: "Digest diario",      crumb: "Soporte / Digest" },
    projects:   { title: "Cartera de proyectos",    crumb: "Proyectos / Vista general" },
    reports:    { title: "Reportes",                 crumb: "Analytics / KPIs" },
    "project-hub": (() => {
      const p = projects.find(x => x.id === openProjectId);
      return { title: p ? p.name : "Proyecto", crumb: p ? `Proyectos / ${p.client}` : "Proyectos" };
    })(),
    clients:    { title: "Clientes", crumb: "Cartera / Por cliente" },
    approvals:  { title: "Aprobaciones", crumb: "Workflow / Pendientes" },
    settings:   { title: "Configuración", crumb: "Espacio de trabajo" },
    "import":   { title: "Importar datos", crumb: "Carga masiva" },
    "platform-tenants": { title: "Tenants",          crumb: "Plataforma / Cross-tenant" },
    "platform-tickets": { title: "Tickets globales", crumb: "Plataforma / Soporte" },
    "platform-feedback": { title: "Comentarios / Sugerencias", crumb: "Plataforma / Feedback" },
    "platform-cfdi-sales": { title: "CFDI Ventas (Capa 2)", crumb: "Plataforma / Facturación" },
    "platform-cfdi-subs": { title: "Facturas de suscripción (Capa 1)", crumb: "Plataforma / Facturación" },
    "boutique-pos":      { title: "Venta rápida", crumb: "Boutique / POS" },
    "boutique-pedidos":  { title: "Pedidos en línea", crumb: "Boutique / Pedidos en línea" },
    "boutique-services": { title: "Servicios", crumb: "Boutique / Catálogo" },
    "boutique-products": { title: "Productos", crumb: "Boutique / Catálogo" },
    "boutique-agenda":   { title: "Agenda",    crumb: "Boutique / Citas" },
    "boutique-reminders": { title: "Recordatorios", crumb: "Boutique / Seguimientos" },
    "boutique-history":  { title: "Historial", crumb: "Boutique / Servicios" },
    "boutique-sales":    { title: "Insights",  crumb: "Boutique / Ventas" },
    "boutique-margen":   { title: "Margen",    crumb: "Boutique / Analítica" },
    "boutique-ticket":   { title: "Ticket y volumen", crumb: "Boutique / Analítica" },
    "boutique-corte":    { title: "Cierre de día", crumb: "Boutique / Corte de caja" },
    "gastro-pos":        { title: "Mesas",     crumb: "GastroGest / POS" },
    "gastro-cocina":     { title: "Cocina",    crumb: "GastroGest / Cocina" },
    "gastro-pendientes": { title: "Pendientes de entrega", crumb: "GastroGest / Pendientes de entrega" },
    "gastro-products":   { title: "Productos", crumb: "GastroGest / Catálogo" },
    "gastro-modificadores": { title: "Modificadores", crumb: "GastroGest / Arma tu platillo" },
    "gastro-paquetes": { title: "Paquetes", crumb: "GastroGest / Menú por tiempos" },
    "gastro-sales":      { title: "Insights",  crumb: "GastroGest / Ventas" },
    "gastro-margen":     { title: "Margen",    crumb: "GastroGest / Analítica" },
    "gastro-ticket":     { title: "Ticket y volumen", crumb: "GastroGest / Analítica" },
    "gastro-cocina-tiempos": { title: "Tiempos de cocina", crumb: "GastroGest / Analítica" },
    "gastro-corte":      { title: "Cierre de día", crumb: "GastroGest / Corte de caja" },
    "gastro-insumos":    { title: "Insumos", crumb: "GastroGest / Inventario" },
    "finance-pnl":       { title: "Ingresos vs gastos", crumb: "Finanzas / Ingresos vs gastos" },
    "finance-expenses":  { title: "Gastos",        crumb: "Finanzas / Egresos" },
    "finance-cfdi":      { title: "Mis facturas (CFDI)", crumb: "Finanzas / Mis facturas" },
    "gym-pos":           { title: "Venta rápida", crumb: "Wellness / POS" },
    "gym-pedidos":       { title: "Pedidos en línea", crumb: "Wellness / Pedidos en línea" },
    "gym-products":      { title: "Productos", crumb: "Wellness / Catálogo" },
    "gym-members":       { title: "Miembros",  crumb: "Wellness / Membresías" },
    "gym-checkin":       { title: "Check-in",  crumb: "Wellness / Acceso" },
    "gym-classes":       { title: "Clases",    crumb: "Wellness / Reservas" },
    "gym-plans":         { title: "Planes",    crumb: "Wellness / Catálogo" },
    "gym-visits":        { title: "Visitas",   crumb: "Wellness / Asistencia" },
    "gym-sales":         { title: "Insights",  crumb: "Wellness / Ventas" },
    "gym-margen":        { title: "Margen",    crumb: "Wellness / Analítica" },
    "gym-ticket":        { title: "Ticket y volumen", crumb: "Wellness / Analítica" },
    "gym-corte":         { title: "Cierre de día", crumb: "Wellness / Corte de caja" },
    help:                { title: "Centro de ayuda", crumb: "Ayuda / Preguntas frecuentes" },
  };
  // Defensa: una sección sin entrada en `titles` no debe tumbar el Topbar (pantalla
  // en blanco). Fallback neutro si `active` no está mapeado.
  const pageMeta = titles[active] ?? { title: "FluidSol", crumb: "" };

  return (
    // TREN 1.2 (PINK #1 P1.5) — sesión de piso: TODO el workspace vive detrás de
    // la pantalla de bloqueo (nada se monta sin PIN). Sesiones normales: no-op.
    <FloorLockProvider me={me}>
    <div className="relative min-h-screen bg-[#FAFAF7] font-sans text-slate-800 antialiased">
      {/* §17.2.c — fondo del workspace (z-0, fijo); el contenido va en z-10 encima. */}
      <WorkspaceBackground theme={me?.tenant?.theme} />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Fraunces:opsz,wght@9..144,400;9..144,500&display=swap');
        :root { font-family: 'Inter', ui-sans-serif, system-ui, sans-serif; }
        .font-serif { font-family: 'Fraunces', ui-serif, Georgia, serif; font-optical-sizing: auto; }
        .input {
          width: 100%; border-radius: .75rem; border: 1px solid rgb(226 232 240); background: white;
          padding: .625rem .875rem; font-size: .875rem; color: rgb(15 23 42); outline: none;
          transition: border-color .15s, box-shadow .15s;
        }
        .input:focus { border-color: rgb(15 23 42); box-shadow: 0 0 0 3px rgb(15 23 42 / .05); }
        .input::placeholder { color: rgb(148 163 184); }
        .card-soft { box-shadow: 0 1px 2px rgba(15,23,42,.04), 0 1px 3px rgba(15,23,42,.04); }
        .card-soft-hover:hover { box-shadow: 0 4px 12px rgba(15,23,42,.06), 0 2px 4px rgba(15,23,42,.04); }
        .kanban-col[data-drop-active="true"] { background: rgb(241 245 249); }
        .kanban-card.dragging { opacity: .4; transform: rotate(-1deg); }
        /* Paleta sobria de alertas — sin rojos estridentes */
        .alert-over     { background: #FBF1EE; color: #7B2D26; }
        .alert-over-bar { background: linear-gradient(90deg, #B23A3A, #8E2A2A); }
        .alert-warn     { background: #FBF6E8; color: #7B5A12; }
        .alert-warn-bar { background: linear-gradient(90deg, #D4A446, #B5852E); }
        .alert-ok       { background: #ECF4EE; color: #2C5C3F; }
        .alert-ok-bar   { background: linear-gradient(90deg, #4A8A66, #2C5C3F); }
      `}</style>

      <div className="relative z-10 flex min-h-screen">
        <Sidebar active={active === "project-hub" ? "projects" : active}
                 setActive={setActive} open={open} setOpen={setOpen}
                 user={user} me={me} onToggleRole={toggleRole}
                 onLogout={onLogout}
                 pendingApprovalsCount={pendingApprovalsCount}
                 isPlatformAdmin={Boolean(me?.isPlatformAdmin)} />
        {/* min-w-0: sin esto, <main> (hijo flex) usa min-width:auto y NO encoge por
            debajo del min-content de su contenido; una fila flex ancha (Topbar,
            Header) forzaba todo el layout a ~640px y lo desbordaba en 375-412px. */}
        <main className="min-w-0 flex-1">
          <Topbar pageTitle={pageMeta.title} breadcrumb={pageMeta.crumb}
                  onMenu={() => setOpen(true)} user={user} onHelp={startTour} />
          <div className="mx-auto max-w-7xl px-6 py-8 md:px-10 md:py-12">
            {active === "projects"    && <ProjectsView projects={projects.filter(p => p.approvalStatus !== "PENDING" && p.approvalStatus !== "REJECTED")}
                                                       onOpenProject={openProject}
                                                       onNewProject={() => setNewProjectOpen(true)}
                                                       displayCurrency={displayCurrency} fxRates={fxRates}
                                                       isAdmin={isAdmin} />}
            {active === "project-hub" && <ProjectHub projectId={openProjectId} projects={projects}
                                                     tasks={tasks} setTasks={setTasks}
                                                     onBack={backToProjects}
                                                     onLogHours={() => openLogHours(openProjectId)}
                                                     onNewTask={() => setNewTaskOpen(true)}
                                                     displayCurrency={displayCurrency} fxRates={fxRates}
                                                     users={usersState} isAdmin={isAdmin} user={user} />}
            {active === "clients"     && <ClientsView projects={projects.filter(p => p.approvalStatus !== "PENDING" && p.approvalStatus !== "REJECTED")}
                                                      clients={clients.filter(c => c.approvalStatus !== "PENDING" && c.approvalStatus !== "REJECTED")}
                                                      isAdmin={isAdmin}
                                                      displayCurrency={displayCurrency} fxRates={fxRates}
                                                      onNewClient={() => setClientModal({ open:true, initial:null })}
                                                      onEditClient={(c) => setClientModal({ open:true, initial:c })}
                                                      onToggleActiveClient={handleToggleActiveClient} />}
            {active === "approvals"   && <ApprovalsView projects={projects} clients={clients}
                                                        onApproveProject={handleApproveProject}
                                                        onRejectProject={handleRejectProject}
                                                        onApproveClient={handleApproveClient}
                                                        onRejectClient={handleRejectClient} />}
            {active === "reports"     && <ReportsView projects={projects} tasks={tasks} users={usersState}
                                                      contacts={contactsState}
                                                      displayCurrency={displayCurrency} fxRates={fxRates} />}
            {active === "crm"         && <CRMView user={user} contacts={contactsState} isAdmin={isAdmin}
                                                  onNewContact={() => setContactModal({ open:true, initial:null })}
                                                  onEditContact={(c) => setContactModal({ open:true, initial:c })} />}
            
            {active === "contacts" && <ContactsView canImport={hasPermission(user, "contacts:create")} onOpenProfile={openCustomerProfile}
                                                    onSendComunicado={hasPermission(user, "communications:manage") ? () => { setCommAutoCompose(true); setActive("comunicados"); } : undefined} />}
            {/* V92 — Comunicados (defensa en profundidad: el API igual 403 sin permiso/módulo). */}
            {active === "comunicados" && hasPermission(user, "communications:manage") &&
              <ComunicadosView autoCompose={commAutoCompose} onAutoComposeConsumed={() => setCommAutoCompose(false)} />}
            {active === "customer-profile" && profileContactId &&
                <CustomerProfileView contactId={profileContactId} onBack={() => setActive("contacts")}
                                     canEdit={hasPermission(user, "contacts:edit")} />}
            
            {active === "pipeline"    && <PipelineView isAdmin={isAdmin} contacts={contactsState}
                                                       displayCurrency={displayCurrency} fxRates={fxRates} />}
            {active === "quotes"      && <QuotesView user={user} isAdmin={isAdmin}
                                                     displayCurrency={displayCurrency} fxRates={fxRates} onOpenProject={openProject} />}
            {active === "tickets"     && <TicketsView user={user} isAdmin={isAdmin} onOpenDigest={() => setActive("tickets-digest")} />}
            {active === "tickets-digest" && <TicketsDigestView user={user} onBack={() => setActive("tickets")} />}
            {active === "platform-tenants" && <PlatformTenantsView />}
            {active === "platform-tickets" && <PlatformTicketsView />}
            {active === "platform-feedback" && <PlatformFeedbackView />}
            {active === "platform-cfdi-sales" && <PlatformCfdiSalesView />}
            {active === "platform-cfdi-subs" && <PlatformCfdiSubsView />}
            {active === "boutique-services" && <ServicesView />}
            {active === "boutique-products" && <ProductsView />}
            {active === "boutique-agenda"   && <AgendaView />}
            {active === "boutique-reminders" && <RemindersView />}
            {active === "boutique-history"  && <HistoryView />}
            {/* POS parametrizado por modo: Mesas (gastro) y Venta rápida (boutique/Wellness). */}
            {active === "gastro-pos"        && <GastroGestPOS me={me} />}
            {active === "gastro-cocina"     && <KitchenView />}
            {active === "gastro-pendientes" && <PendingDeliveryScreen me={me} />}
            {active === "boutique-pedidos"  && <PendingDeliveryScreen me={me} sourceModule="boutique" />}
            {active === "gym-pedidos"       && <PendingDeliveryScreen me={me} sourceModule="gym" />}
            {/* feat/inventory-management — Productos para GastroGest (reusa ProductsView). */}
            {active === "gastro-products"   && <ProductsView />}
            {/* V54 — Insumos (ingredientes) + recetas: control de inventario a nivel insumo. */}
            {active === "gastro-insumos"    && <InsumosView />}
            {/* V86 — "Arma tu platillo": defensa en profundidad (el API igual 403 sin write). */}
            {active === "gastro-modificadores" && hasPermission(user, "product_catalog:write") && <ModifiersView />}
            {active === "gastro-paquetes" && hasPermission(user, "product_catalog:write") && <PaquetesView />}
            {active === "boutique-pos"      && <GastroGestPOS me={me} mode="quick" sourceModule="boutique" />}
            {active === "gym-pos"           && <GastroGestPOS me={me} mode="quick" sourceModule="gym" />}
            {/* feat/gym-catalog-bridge — gestión de productos del gym (reusa ProductsView de Boutique). */}
            {active === "gym-products"      && <ProductsView />}
            {/* §18/fix-financials-gating — defensa en profundidad: aunque la tab ya
                se oculta sin financials:view, no renderizamos la vista de Insights si
                `active` se forzara a "gastro-sales" sin la capacidad (el API igual 403). */}
            {/* sourceModule="gastro": estas vistas son del módulo Mesas; acotan a
                ventas gastro para no contaminarse con la venta rápida de Boutique/
                Wellness (V46). Los dashboards por módulo de Boutique/Wellness reusarán
                estos mismos componentes con su sourceModule (PR B). */}
            {active === "gastro-sales"      && hasPermission(user, "financials:view") && <GastroGestInsights sourceModule="gastro" />}
            {/* §21.3 — Margen: misma defensa en profundidad que Insights (API igual 403). */}
            {active === "gastro-margen"     && hasPermission(user, "financials:view") && <MarginView sourceModule="gastro" />}
            {/* §21.3 — Ticket/volumen: operativo (pos:view) o dueño (financials:view). */}
            {active === "gastro-ticket"     && (hasPermission(user, "pos:view") || hasPermission(user, "financials:view")) && <TicketVolumeView sourceModule="gastro" />}
            {active === "gastro-cocina-tiempos" && (hasPermission(user, "pos:view") || hasPermission(user, "financials:view")) && <KitchenTimesView sourceModule="gastro" />}
            {active === "gastro-corte"      && <GastroGestCashClose sourceModule="gastro" me={me} />}
            {/* feat/analytics-por-modulo — analítica de Boutique (mismo componente,
                sourceModule="boutique"). Mismo gating que gastro (defensa en profundidad). */}
            {active === "boutique-sales"    && hasPermission(user, "financials:view") && <GastroGestInsights sourceModule="boutique" />}
            {active === "boutique-margen"   && hasPermission(user, "financials:view") && <MarginView sourceModule="boutique" />}
            {active === "boutique-ticket"   && (hasPermission(user, "pos:view") || hasPermission(user, "financials:view")) && <TicketVolumeView sourceModule="boutique" />}
            {active === "boutique-corte"    && <GastroGestCashClose sourceModule="boutique" me={me} />}
            {/* §21.1 Finanzas — defensa en profundidad: la sección ya se oculta sin
                financials:view, pero tampoco renderizamos sin la capacidad (API igual 403). */}
            {active === "finance-pnl"       && hasPermission(user, "financials:view") && <PnlView />}
            {active === "finance-expenses"  && hasPermission(user, "financials:view") && <ExpensesView />}
            {active === "finance-cfdi"      && hasPermission(user, "cfdi_sales:view") && (
              <div>
                {/* Fase 2 (A) — facturar/reintentar desde la LISTA de órdenes del día. */}
                {hasPermission(user, "cfdi_sales:emit") && <FacturableOrdersCard />}
                <SalesCfdiListCard standalone canRetry={hasPermission(user, "cfdi_sales:emit")} />
              </div>
            )}
            {/* §20 Gym/Estudio — gateado por módulo en backend (@RequiresModule('gym')); la tab solo aparece con hasModule('gym'). */}
            {active === "gym-members"       && <GymMembersView />}
            {active === "gym-checkin"       && <GymCheckInView />}
            {/* V69 — motor de booking (clases con cupo). Doble enforcement: la API
                exige bookings:read/write/manage; aquí solo modulamos los botones. */}
            {active === "gym-classes"       && <BookingManagerView kind="CLASS"
                                                 canWrite={hasPermission(user, "bookings:write")}
                                                 canManage={hasPermission(user, "bookings:manage")} />}
            {active === "gym-plans"         && <GymPlansView />}
            {active === "gym-visits"        && <GymVisitsView />}
            {/* feat/analytics-por-modulo — analítica de Wellness (sourceModule="gym",
                el token de Wellness en V46/enabled_modules). Mismo gating que gastro. */}
            {active === "gym-sales"         && hasPermission(user, "financials:view") && <GastroGestInsights sourceModule="gym" />}
            {active === "gym-margen"        && hasPermission(user, "financials:view") && <MarginView sourceModule="gym" />}
            {active === "gym-ticket"        && (hasPermission(user, "pos:view") || hasPermission(user, "financials:view")) && <TicketVolumeView sourceModule="gym" />}
            {active === "gym-corte"         && <GastroGestCashClose sourceModule="gym" me={me} />}
            {/* feat/frontend-import-ui — carga masiva self-service (envuelve /imports/*).
                El componente reaplica el gate módulo-comercio + product_catalog:write. */}
            {active === "import"      && <ImportDataView />}
            {active === "settings"    && <SettingsView user={user} me={me} onToggleRole={toggleRole} isAdmin={isAdmin}
                                                       tags={tagsState}
                                                       onNewTag={() => setTagModal({ open:true, initial:null })}
                                                       onEditTag={(t) => setTagModal({ open:true, initial:t })}
                                                       onToggleTag={handleToggleTag}
                                                       users={usersState}
                                                       onChangeUserRate={handleChangeUserRate}
                                                       displayCurrency={displayCurrency}
                                                       setDisplayCurrency={setDisplayCurrency}
                                                       fxRates={fxRates}
                                                       onUpsertFx={handleUpsertFx}
                                                       onFetchBmv={handleFetchBmv}
                                                       onProfileUpdated={handleProfileUpdated} />}
            {active === "help"        && <HelpCenter me={me} userId={me?.userId}
                                                     onNavigate={(k) => setActive(k)} onStartTour={startTour} />}
          </div>
        </main>
      </div>

      {/* FAB Log Hours en mobile: quitado — flotaba sobre las tarjetas (p.ej. Cotizaciones)
          e interfería. Registrar horas sigue en Proyectos/ProjectHub. */}

      <LogHoursModal open={logOpen} onClose={() => setLogOpen(false)}
                     projects={projects} tasks={tasks} tags={tagsState}
                     onSubmit={submitLog}
                     defaultProjectId={logDefaultPid} />

      {/* Modales — Fase 1 (ya con awareness de aprobación v1.1.2) */}
      <NewProjectModal open={newProjectOpen} onClose={() => setNewProjectOpen(false)}
                       clients={clients.filter(c => c.isActive !== false && c.approvalStatus !== "PENDING")}
                       onCreate={handleCreateProject}
                       isAdmin={isAdmin} />
      <ClientFormModal open={clientModal.open}
                       onClose={() => setClientModal({ open:false, initial:null })}
                       initial={clientModal.initial}
                       onSave={handleSaveClient}
                       isAdmin={isAdmin} />

      {/* Modales admin — Fase 2 */}
      <NewTaskModal open={newTaskOpen}
                    onClose={() => setNewTaskOpen(false)}
                    projectId={openProjectId}
                    users={usersState}
                    onCreate={handleCreateTask} />
      <ContactFormModal open={contactModal.open}
                        onClose={() => setContactModal({ open:false, initial:null })}
                        initial={contactModal.initial}
                        onSave={handleSaveContact} />
      <TagFormModal open={tagModal.open}
                    onClose={() => setTagModal({ open:false, initial:null })}
                    initial={tagModal.initial}
                    onSave={handleSaveTag} />
    </div>
    </FloorLockProvider>
  );
}
